// Sheet del flow de cualquier preset: cubre toda la pantalla (mobile)
// o el área a la derecha del sidebar (desktop) y maneja todos los
// `step` del state machine: preview → choose-product → pick-product →
// upload-product → configure (con la rama paralela select-vistas →
// configure cuando es 'catalogo-ia').
//
// Ojo: este componente es el "núcleo" del browse de los modos
// `template`/`design`/`design-modelo`/`design-auto`/`catalogo-ia`. El
// modo `asistente` usa su propio sheet (AsistenteMarketingSheet) en
// vez de éste — el padre conmuta entre los dos en la rama del JSX.

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Minus,
  Play,
  Plus,
  Sparkles,
  Type as TypeIcon,
} from 'lucide-react';
import type { MockProductV2, PresetV2 } from '../mockData';
import { getFormatLabel } from '../mockData';
import type {
  ModelPickerView,
  ModelSelection,
} from '../ModelPicker';
import type {
  FondoPickerView,
  FondoSelection,
} from '../FondoPicker';
import { fondoSelectionLabel } from '../FondoPicker';
// Cuánto ocupa la barra inferior de mobile: el primer paso del modo le deja el hueco
// (ADR-2026-08-04-el-modo-se-lleva-la-barra).
import { ALTO_NAV_INFERIOR } from './NavInferiorMobile';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import type { ModelV2 } from '../modelsApi';
import type {
  VistaCatalogo,
  VistaProductoConjuntoOption,
} from '../vistasCatalogo';
import { ChooseProductPanel } from './ChooseProductPanel';
// La card de productos del modo Modelo. Vive en su fork porque nació ahí, pero es la
// misma card en sus cinco puntos de montaje (ver su comentario) — el Catálogo la usa en
// su paso 1 de mobile (ADR-2026-09-08-el-paso-1-de-catalogo-en-mobile-copia-al-de-modelo).
import { CardProductos } from '../modelo/PanelCompacto';
// Y el pie de esa misma pantalla: los ajustes de la tanda y el Generar. Lo usa la única
// pantalla de Automática en mobile, que sin él no tenía CTA en todo el modo
// (ADR-2026-09-23-automatico-en-mobile-tiene-su-generar).
import { BarraInferiorMobile } from '../modelo/BarraInferiorMobile';
import { PromptRecientesButton } from './PromptRecientesButton';
import { PickProductPanel } from './PickProductPanel';
import { SubirProductoDrawer } from './SubirProductoDrawer';
import {
  AjustesRapidosProducto,
  type CalidadImagen,
} from './AjustesRapidosProducto';
// El costo del upscale que encadena la Calidad — el mismo número que cobra
// `aplicarCalidadALaTanda` en InterfazV2 (ADR-2026-09-02-la-calidad-se-resuelve-con-el-upscale).
// El rediseño de Catálogo IA: una columna con todo + panel lateral
// (ADR-2026-08-24-catalogo-toma-el-diseno-de-modelo).
import { PanelCatalogoCompacto } from './PanelCatalogoCompacto';
import {
  COLORES_FONDO_LISO,
  esFondoProducto,
  FondoProductoPicker,
} from './FondoProductoPicker';
import { VistaProductoPicker } from './VistaProductoPicker';
// Los dos pickers embebidos del panel lateral viven en `modelo/` porque los estrenó el
// rediseño del modo Modelo. Se importan desde acá tal cual —duplicarlos para el Catálogo
// serían dos grillas que mantener— (ADR-2026-08-27-fondo-y-modelo-en-el-panel-lateral).
import { ModelPickerInline } from '../modelo/ModelPickerInline';
import { FondoGrilla } from '../modelo/FondoGrilla';
import { usePalabraDeModelo } from '../../../lib/nombreDeModelo';
import { PanelLateralModo } from './PanelLateralModo';
// La grilla de formatos del panel lateral — la misma que abre Modelo.
import { FormatPickerGrid } from '../FormatPicker';
import { ContadorImagenes } from './ContadorImagenes';
import { UploadProductPanel } from './UploadProductPanel';
import { VistaPickerFullscreen } from './VistaPickerFullscreen';
import {
  CatalogoCardsFullscreen,
  construirCardsDeCatalogo,
  contarImagenesCatalogo,
  type CatalogoCard,
} from './CatalogoCardsFullscreen';
import { ConfigurePanel } from '../panels/ConfigurePanel';
import {
  creditosPorResolucion,
  type ResolucionImagen,
} from '../../../lib/resolucionImagen';
import { plantillaEligeCalidad } from '../calidadDePlantilla';
import {
  isVoiceGuionIncomplete,
  getGuionDurationSec,
  INFLUENCER_GUION_BASE_COST,
  type VoiceGuionValue,
} from './VoiceGuionSection';
import { scrollToAndShake } from '../scrollShake';
import {
  MAX_MODELOS_SERIE,
  resolveDesignModeloCount,
  resumenTandaAutomatica,
  resumenTandaDesignModelo,
} from '../designModeloCount';
import { isSinGancho } from '../ganchosCatalog';
import { SwitchSeleccionMultiple } from './SwitchSeleccionMultiple';
import { MultiPresetCarousel, MultiSelectedStrip } from './MultiPresetUI';
import { ProductHeroCollage } from './ProductHeroCollage';
import { type Miga } from '../../../components/Migas';
import {
  MODE_KIND_TO_PATH,
  esCatalogoIA,
  usaBibliotecaDeFondos,
  nombreCortoModo,
} from '../modesCatalog';
import { RUBRO_HOGAR_DECO } from '../../../lib/rubro';
// El back del chrome de mobile, para que en la segunda pantalla de Automática vuelva al
// paso 1 en vez de cerrar el sheet (el mismo mecanismo que la de Modelo).
import { useSidebar } from '../../../context/SidebarContext';
// El nombre corto de un modo vive en `COPY_TIRA`, dentro del shell, y se expone por
// esta función — mismo camino que usa `AsistenteMode` para su breadcrumb. No hay
// ciclo: `EstiloMinimal` no importa esta pantalla ni `InterfazV2`.
import {
  EncabezadoPantalla,
  MARCO_PANTALLA,
  BORDE_PANTALLA,
} from '../../../components/PantallaChrome';
import {
  ANCHO_PANEL_CONFIG,
  ANCHO_PANEL_CONFIG_CATALOGO,
  COLUMNA_RESULTADOS_AIRE_INTERNO,
  COLUMNA_RESULTADOS_AL_BORDE,
  COLUMNA_SECCION,
  FILA_MODO_REDISENO,
} from './layoutModo';

/**
 * Heroes tipo "1 producto → sus resultados" para las pantallas de selección de
 * producto de los modos catálogo/infografía (mismo espíritu que la del polo del
 * modo automático, pero como componente). Cada entrada: la foto original + los
 * resultados apilados. Si un kind no está acá, cae al `<img>` de siempre. Los
 * assets los dropea Imanol en /public/images/<carpeta>/. Ver ADR-817.
 */
const HERO_COLLAGE: Record<
  string,
  { original: string; results: string[]; layout?: 'fan3-wide' | 'fan2-tall' }
> = {
  // Catálogo (ropero, producto ALTO → fan2-tall): la principal es el packshot
  // sobre fondo blanco; la flecha apunta a la foto en contexto + la infografía.
  'catalogo-muebles': {
    original: '/images/modo_catalogo_muebles/hero_packshot.png',
    results: [
      '/images/modo_catalogo_muebles/hero_infografia.png',
      '/images/modo_catalogo_muebles/hero_contexto.jpg',
    ],
    layout: 'fan2-tall',
  },
  // Infografía (mesa ratona, producto ANCHO → fan3-wide): original + los 3
  // estilos (boceto / línea / fotográfica).
  infografia: {
    original: '/images/modo_infografia/hero_original.png',
    results: [
      '/images/modo_infografia/hero_boceto.png',
      '/images/modo_infografia/hero_linea.png',
      '/images/modo_infografia/hero_foto.jpg',
    ],
    layout: 'fan3-wide',
  },
};

/**
 * El hero también sigue al RUBRO —
 * ADR-2026-08-05-el-hero-de-catalogo-sigue-al-rubro.
 *
 * En Hogar y Decoración, "Catálogo" se presenta con el collage de muebles (packshot →
 * infografía + foto en contexto) en vez del suyo, que es una remera sobre tres vistas de
 * un modelo: adentro del modo, esa imagen es lo que explica QUÉ va a pasar con el
 * producto, y con un ropero no explica nada.
 *
 * Remapea el `kind` a otro que YA tiene collage en vez de repetir los archivos: si el de
 * muebles cambia, cambian los dos juntos.
 */
const HERO_COLLAGE_POR_RUBRO: Record<string, Record<string, string>> = {
  [RUBRO_HOGAR_DECO]: {
    'catalogo-ia': 'catalogo-muebles',
  },
};

// ─── Preset detail sheet ────────────────────────────────────────────────
// Cubre toda la pantalla. Arriba: la imagen del preset en grande (hero).
// Abajo: un container que cambia entre dos vistas según `step`:
//   • choose-product → "Subí la imagen del producto" + CTA "Ver mis productos"
//   • configure      → "¿Querés ajustar la generación?" + chips Pose/Formato
//                      + textarea libre + botón Generar
//
// Backdrop fade-in + sheet con slide-up de 300ms ease-out. Cerrar
// disponible vía X arriba a la derecha y tap en el backdrop.
/** Paso del flujo que dibuja el sheet. */
export type PanelStep =
  | 'preview'
  | 'choose-product'
  | 'pick-product'
  | 'upload-product'
  | 'select-vistas'
  | 'configure'
  | 'asistente'
  // Como 'asistente': el paso existe en `InterfazV2` pero este sheet nunca lo monta
  // (ADR-2026-09-07-modo-prompt-libre, ADR-2026-09-07-modo-creador-video).
  | 'prompt-libre'
  | 'creador-video';

export interface PresetDetailSheetProps {
  preset: PresetV2;
  animated: boolean;
  /**
   * Breadcrumb del header de desktop — ADR-2026-08-03-breadcrumbs-pantallas-de-modo.
   *
   * Lo arma `InterfazV2`, que es quien sabe si este sheet es una **pantalla de
   * modo** (una de las cards sintéticas de "Diseña tu imagen") o el detalle de una
   * plantilla, y en qué paso del flujo está. Acá sólo se dibuja.
   *
   * Cuando viene, REEMPLAZA al botón "Volver": el primer nivel hace lo mismo que
   * hacía él. Cuando no viene —plantillas, poses—, el header sigue como estaba.
   */
  migas?: Miga[];
  step: PanelStep;
  /**
   * El modo se está reabriendo con una configuración ya hecha (el "Atrás" desde los
   * resultados). Este shell no lo usa: lo declara porque `InterfazV2` monta los dos
   * shells con los MISMOS props y elige cuál según el modo
   * (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar). Lo mira el paso 1
   * del shell de Modelo.
   */
  configuracionRestaurada?: boolean;
  /** Lista de presets para swipear en el step 'preview'. */
  previewList: PresetV2[];
  /**
   * Las plantillas del catálogo POR CATEGORÍA, para el panel lateral del paso 3 —
   * ADR-2026-09-08-la-plantilla-se-elige-desde-el-panel.
   *
   * No alcanza con `previewList`: ésa es la colección de la que salió la plantilla
   * abierta, y lo que se pidió es poder cambiar a cualquiera. Agrupadas porque el panel
   * filtra con los mismos chips que la pantalla de Plantillas. Sin esta prop el panel cae
   * a `previewList`, que es mejor que una grilla vacía.
   */
  categoriasDePlantillas?: { id: string; titulo: string; presets: PresetV2[] }[];
  /** Cambiar la plantilla abierta desde ese panel, sin volver a la grilla. */
  onElegirPlantilla?: (p: PresetV2) => void;
  /** Lista de productos elegidos (multi-selección). Vacío = ninguno aún. */
  selectedProducts: MockProductV2[];
  /** El anuncio salió de una creación y no de un producto —
   *  ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo. Sólo se muestra; el id
   *  que viaja al backend lo pone `InterfazV2` al generar. */
  imagenBase?: { url: string } | null;
  modelSelection: ModelSelection | null;
  fondoVal: string | null;
  formatoVal: string | null;
  /** Resolución de salida elegida (1K/2K/4K). Multiplica el costo del botón Generar.
   *  Ver ADR-2026-08-25-resolucion-de-generacion-2k-4k. */
  resolucion: ResolucionImagen;
  otraConfig: string;
  sidebarWidth: number;
  onClose: () => void;
  /** El user tocó "Utilizar" en preview — avanza a choose-product con ese preset. */
  onUtilizarPreset: (p: PresetV2) => void;
  /** El user activó multi-select en preview y eligió N plantillas. Solo
   *  aplica al kind 'template'. La primera se usa como anchor visual. */
  onUtilizarMultiple: (presets: PresetV2[]) => void;
  /** IDs de plantillas seleccionadas en multi-select. Cuando hay más de
   *  una, la imagen del preset detail sheet se reemplaza por un carrusel
   *  de las plantillas seleccionadas. */
  multiPresetIds: string[];
  onShowProducts: () => void;
  /** "Añadir producto al outfit" desde configure — abre el picker de
   *  productos con la selección actual premarcada para sumar más. */
  onAddProducts: () => void;
  /** El user confirmó su selección (uno o varios productos). */
  onConfirmProducts: (products: MockProductV2[]) => void;
  /**
   * La selección de productos EN VIVO, sin confirmar y sin avanzar de paso.
   *
   * La usa "Automático", que no tiene paso 2 (ADR-2026-08-21-automatico-con-el-diseno-nuevo):
   * ahí el CTA del panel es Generar, no Confirmar, así que lo elegido tiene que estar en
   * el estado de arriba en el momento del tap — con el confirm de siempre, el tap
   * generaba con la selección vacía.
   */
  onProductosEnVivo?: (products: MockProductV2[]) => void;
  onBackToChoose: () => void;
  /** Vacía la selección actual y vuelve al step 'choose-product'. */
  onClearProducts: () => void;
  /** Quita UN producto de la selección (fotos-producto multi-producto,
   *  ADR-2026-08-30). Quitar el último se comporta como `onClearProducts`. */
  onRemoveProduct?: (id: string) => void;
  // ── Upload de producto nuevo (step 'upload-product') ────────────
  /** Disparado desde ChooseProductPanel al seleccionar archivos. */
  onStartUpload: (files: FileList | File[]) => void;
  /** "Subir nuevo producto" → entra al paso `upload-product` SIN archivos
   *  (pantalla explicativa) en vez de abrir el file picker directo. */
  onStartNewProduct: () => void;
  /** "Generar sin producto" (ADR-673) — saltea la elección y va directo a
   *  configurar. Solo lo renderizan los paneles cuando el preset es
   *  `video-influencer`. */
  onSkipProduct?: () => void;
  /** "Sin producto" activo (ADR-673) — cambia el chip de Producto del
   *  configurador y saca el toggle de modos de guion. */
  sinProducto?: boolean;
  /** Autogeneración del diálogo en curso / fallida (ADR-674). */
  dialogoLoading?: boolean;
  dialogoError?: string | null;
  /** Alta en bloque: abre el popup "Subir varios productos" de Mi Marca (lo
   *  monta InterfazV2). Va SOLO en el estado vacío de `upload-product` —la
   *  pantalla "Nuevo producto"—, no en las pantallas de elegir producto
   *  (ADR-688 ajusta ADR-684). Antes era un file picker directo exclusivo de
   *  Catálogo; ahora abre el popup y está en todos los modos. */
  onBulkUpload?: () => void;
  /** Plan del usuario. Este sheet no lo usa directo: lo pasa a
   *  CatalogoCardsFullscreen → MultiSlotProductPickerSheet, donde define el
   *  tope del popup de alta en bloque (ADR-684). */
  userPlan?: string | null;
  onAddMoreFiles: (files: FileList | File[]) => void;
  onRemovePendingFile: (idx: number) => void;
  onCancelUpload: () => void;
  onConfirmUpload: () => void;
  pendingProductFiles: File[];
  pendingProductName: string;
  onPendingNameChange: (v: string) => void;
  uploadingProduct: boolean;
  uploadError: string | null;
  /** Subida en bloque (catálogo, 2+ archivos) en curso → mostramos un
   *  spinner DENTRO de la card (donde viven el grid + "Subir nuevo
   *  producto"), no un overlay que tape toda la pantalla. */
  bulkUploadingProducts?: boolean;
  /** Setter del modelo (preset/auto/custom/upload). */
  onModelChange: (sel: ModelSelection) => void;
  /**
   * Reemplaza el contenido de la columna de CONFIGURACIÓN del `configure` a dos
   * columnas. Lo usa el panel de la tanda: al generar, Modelo + Pose dejan lugar
   * a los recuadros de las imágenes en curso
   * (ADR-2026-08-20-tanda-en-el-panel). Sin esto, el sheet no sabe nada de
   * generaciones — el nodo lo arma el padre.
   */
  columnaConfiguracion?: React.ReactNode;
  /**
   * Vuelve de las tandas a la configuración (Modelo + Pose) desde los ajustes rápidos
   * del panel izquierdo — ADR-2026-08-20-varias-tandas-en-el-panel.
   *
   * Sin esto la celda no se dibuja: con la configuración ya a la vista, un control para
   * ir hacia ella no dice nada.
   */
  onCambiarModeloYPose?: () => void;
  /** Baja el z del panel desktop para que el visor de creaciones (lightbox
   *  `fixed z-50` portalizado al body) pueda quedar por encima. */
  cederZIndex?: boolean;
  /** Serie de modelos (grilla multi-select de design-modelo): los que siguen
   *  al principal. Cada uno genera su propia imagen — multiplica el costo. */
  modelSelectionsSerie?: ModelSelection[];
  /** Tap en una card de la grilla: acumula o quita de la serie. */
  onToggleModelSerie?: (sel: ModelSelection) => void;
  /**
   * "Utilizar varios modelos" activo.
   *
   * Apagado —el default— la grilla de modelos del panel lateral es de selección única.
   * Encendido, acumula y la tanda se genera una vez POR MODELO
   * (ADR-2026-08-28-catalogo-varios-modelos). El mismo par de props lo usa el fork de
   * Modelo v2, donde "varios" significa otra cosa (todos en la misma foto): el shell que
   * lo monta es el que sabe qué significa, y `InterfazV2` le pasa el estado que
   * corresponda al modo abierto.
   */
  variosModelos?: boolean;
  onVariosModelosChange?: (v: boolean) => void;
  // ── Modo "varios modelos" (UI-only) ─────────────────────────────
  modelSelectionsExtra: (ModelSelection | null)[];
  onModelChangeExtras: (next: (ModelSelection | null)[]) => void;
  multiModelEnabled: boolean;
  onMultiModelEnabledChange: (v: boolean) => void;
  /** Abre el sheet de modelo en mobile. */
  onOpenModelPicker: () => void;
  /** Abre el sheet directo a una vista. Lo usa design-modelo desktop
   *  cuando el user toca "Creá tu modelo" o "Cargá modelo". */
  onOpenModelSheet: (initialView: ModelPickerView) => void;
  /** Variante del anterior para el sub-flow de multi: abre el sheet
   *  pre-configurado para editar un slot específico con la sub-vista
   *  pedida ('create' o 'upload'). */
  onOpenModelSheetForMultiSlot: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  /** Productos asignados por slot (modo varios modelos). */
  multiProductsPerSlot: Record<number, MockProductV2[]>;
  /** Tap en "Utilizando"/miniaturas de un slot del MultiView. */
  onTapSlotProducts: (slotIdx: number) => void;
  /** Abre el FondoPickerSheet directo a una vista — para "Describí el
   *  fondo" o "Cargá tu fondo" en desktop. */
  onOpenFondoSheet: (initialView: FondoPickerView) => void;
  onFondoChange: (v: string | null) => void;
  onFormatoChange: (v: string | null) => void;
  onResolucionChange: (r: ResolucionImagen) => void;
  /** Abre el picker de formato (sheet/side panel). */
  onOpenFormatPicker: () => void;
  onOtraChange: (v: string) => void;
  /** Gancho elegido (modo video-influencer) — controlado desde InterfazV2 para
   *  poder mandarlo como `gancho_id` en la generación. */
  ganchoId?: string | null;
  onGanchoIdChange?: (id: string | null) => void;
  /** Voz + velocidad + guion del modo video-influencer — pass-through
   *  InterfazV2 → ConfigurePanel (sección "Voz y guion"). ADR-525. */
  voiceGuion?: VoiceGuionValue;
  onVoiceGuionChange?: (next: VoiceGuionValue) => void;
  /** Setter de `videoDuration` — barra de duración inline del modo
   *  video-influencer (dentro de "Voz y guion"). ADR-528. */
  onVideoDurationChange?: (v: number) => void;
  /** `cost` = créditos que consumirá la generación (lo que muestra el pill).
   *  InterfazV2 lo usa para chequear saldo suficiente. Ver ADR-518. */
  /**
   * Calidad de la tanda (1080p / 2K / 4K).
   *
   * Llega de `InterfazV2` —donde viven fondo y formato— y no de un `useState` de acá:
   * desde `ADR-2026-09-02-la-calidad-se-resuelve-con-el-upscale` tiene efecto real (el
   * código de generación la lee para encadenar el upscale) y además cambia lo que cuesta
   * la tanda, así que el pill de créditos de este panel la suma.
   */
  calidad: CalidadImagen;
  onCalidadChange: (v: CalidadImagen) => void;
  onGenerate: (cost: number) => void;
  /** Solo plantillas: generación normal + carrusel automático. Se pasa
   *  tal cual al ConfigurePanel. `carruselCount` = cuántas imágenes pedirle
   *  al carrusel (= imágenes elegidas en el stepper − 1). `cost` = créditos
   *  totales de la producción. */
  onGenerateProduction?: (carruselCount: number, cost: number) => void;
  // ── Modo 'design-auto' (cantidad de imágenes) ────────────────────
  /** Cantidad de imágenes a generar — solo se usa cuando el preset es
   *  'design-auto'. Controla el +/- arriba del Generar. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  /** Tope superior del autoImageCount (mínimo siempre es 1). */
  autoImageCountMax: number;
  /** Modelos reales (para el ModelPickerInline desktop). */
  userModels: ModelV2[];
  userAuthId: string | null;
  userCompanyId: string | null;
  onModelCreated: () => void;
  // ── Modo 'design-modelo' ─────────────────────────────────────────────
  allPoses: PoseV2[];
  // ── Catálogo: tab "Mis poses" del vista picker (poses custom) ─────────
  /** Poses custom (muñeco) de la empresa, filtradas a 1 persona. */
  customPoses: PoseV2[];
  /** Crear una pose custom desde el vista picker (sube la foto → muñeco). */
  onUploadCustomPose: (file: File) => void;
  /** true mientras se procesa la subida de una pose custom. */
  uploadingCustomPose: boolean;
  /** Borrar una pose custom propia. */
  onDeleteCustomPose: (pose: PoseV2) => void;
  poseIds: string[];
  onPoseIdsChange: (ids: string[]) => void;
  // ── Modo 'fotos-producto' ──────────────────────────────────────────
  /** Vistas solo-producto compatibles con el producto elegido (ya
   *  filtradas + thumb resuelto). Pass-through a ConfigurePanel. */
  vistasProducto?: VistaProductoConjuntoOption[];
  /** Productos desactivados por vista (checklist del badge). Keyed por slug. */
  vistaProductosExcluidos?: Record<string, string[]>;
  /** Toggle de un producto para una vista (checklist del badge). */
  onToggleVistaProducto?: (vistaId: string, productId: string) => void;
  /** Total de imágenes de la tanda = Σ por vista de sus productos activos.
   *  Alimenta el pill del Generar y el contador del panel
   *  (ADR-2026-08-30-fotos-producto-multi-producto-2). */
  fotosProductoTotalImagenes?: number;
  /** Slugs de las vistas elegidas — una imagen generada por cada una. */
  vistaIds?: string[];
  onVistaIdsChange?: (ids: string[]) => void;
  /** Toggle "Seleccionar múltiples" del menú de poses — pass-through
   *  InterfazV2 → ConfigurePanel (el switch vive en el PosePickerSheet). */
  multiPoseEnabled: boolean;
  /**
   * Commit del toggle "Utilizar varias poses". Acá NO se usa: este fork no dibuja el
   * interruptor —lo dibujan el sheet de poses y, desde
   * ADR-2026-09-04-el-interruptor-de-varias-poses-tambien-en-desktop, el panel del modo
   * Modelo—. Está declarado porque los dos forks se montan por el mismo `SheetDelModo`
   * y comparten el juego de props: sin esto, pasarlo sería un error de tipos.
   */
  onMultiPoseEnabledChange?: (enabled: boolean) => void;
  /**
   * La generación arrancó (la pantalla de carga está arriba). Acá NO se usa: es de la
   * segunda pantalla de mobile del modo Modelo, que decide con esto cuándo cerrarse
   * (ADR-2026-09-04-el-generar-de-mobile-espera-a-que-la-carga-este-arriba). Se declara
   * porque los dos forks se montan por el mismo `SheetDelModo` y comparten props.
   */
  generacionEnCurso?: boolean;
  onOpenPosePicker: () => void;
  /** Abre el sheet de poses directo en "Subí tu propia pose" — la tarjeta que cierra
   *  la grilla de poses (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). */
  onSubirPose?: () => void;
  bibliotecaFondos: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  fondoSelection: FondoSelection | null;
  onFondoSelectionChange: (sel: FondoSelection) => void;
  onOpenFondoPicker: () => void;
  fondoView: FondoPickerView;
  onFondoViewChange: (v: FondoPickerView) => void;
  onFondoSaved: () => void;
  /** Rubro de la empresa — para que el product picker active la sección
   *  "Sugeridos" solo en rubros de moda. */
  rubro: string | null;
  /**
   * Rubro de la MARCA ACTIVA (con el de la empresa como respaldo) — decide con qué
   * imagen se presenta el modo (ADR-2026-08-05-el-hero-de-catalogo-sigue-al-rubro).
   *
   * Va aparte de `rubro` y no lo reemplaza: ese de arriba es el de la EMPRESA y decide
   * otra cosa (los "Sugeridos" del picker). Unificarlos es probablemente lo correcto,
   * pero es un cambio de comportamiento en una pantalla que no es la de este pedido.
   */
  rubroMarca?: string | null;
  /**
   * La generación sale de UN solo producto: el picker va en `singleSelect` y la bajada
   * deja de invitar a elegir varios.
   *
   * Lo decide el host y no este sheet porque depende del rubro de la MARCA activa, que
   * acá sólo llega como `rubroMarca` —y ese valor resuelve la marca por otro camino
   * (ver ADR-2026-08-21-plantillas-rebotaban-por-dos-marcas-activas)—. Hoy lo prende
   * Plantillas en Belleza (ADR-2026-09-14-la-plantilla-de-belleza-se-genera-con-un-producto);
   * el video con influencer no lo necesita: ya va por su propio `kind`.
   */
  unSoloProducto?: boolean;
  /** Si true, el ConfigurePanel bloquea el add/remove de slots del
   *  multi-modelo. Usado por E-commerce (kind: 'design') con poses
   *  de cantidad fija. */
  lockSlotCount?: boolean;
  // ── Modo 'catalogo-ia' ───────────────────────────────────────────────
  /** Vistas elegidas en el step `select-vistas`. */
  catalogoVistas: VistaCatalogo[];
  /** Setter del array de vistas elegidas. */
  onCatalogoVistasChange: (next: VistaCatalogo[]) => void;
  /** Confirma la selección de vistas y pasa al step `configure`. */
  onConfirmCatalogoVistas: () => void;
  /** Vuelve del step `configure` (cards review) al `select-vistas`. */
  onBackFromCatalogoConfigure: () => void;
  /** Dispara la generación del catálogo con las combinaciones que
   *  quedaron activas después del review (post-X de descartes). */
  onGenerateCatalog: (cards: CatalogoCard[]) => void;
  /** Marca activa — para el sheet de productos secundarios del catálogo
   *  (asociar producto nuevo subido). */
  brandId: string | null;
  /** Refresca el contexto global de userProducts tras subir uno nuevo
   *  desde el sheet de productos secundarios. */
  onProductsRefreshed: () => void;
  // ── Modo video (toggle "Imagen / Video" en el header) ──────────────
  /** Tab seleccionado en el header. Cuando es 'video' y el preset es
   *  'template', el ConfigurePanel agrega el botón "Configurar video"
   *  y la generación encadena imagen → prompt → video. */
  previewMediaTab: 'imagen' | 'video';
  /** Cambia el tab — usado por el toggle desktop dentro del preview
   *  (el mobile lo controla via mobileHeaderCenter). */
  onPreviewMediaTabChange: (tab: 'imagen' | 'video') => void;
  /** Segundos por escena para el video. Se usa al calcular el costo y
   *  al armar el payload de generación. */
  videoDuration: number;
  /** Si true, se genera multi-shot (Kling vía atlas /v1/videos/from-image,
   *  ADR-582), que cuesta el doble de créditos. */
  videoMultiScene: boolean;
  /** Costo en créditos del VIDEO por cada imagen generada (duración *
   *  multiplicador). El total final = creditCost base × cantidad imágenes
   *  + videoCreditCostPerImage × cantidad imágenes. */
  videoCreditCostPerImage: number;
  /** Abre el sheet de configuración de video. */
  onOpenVideoConfig: () => void;
  // ── Modo 'anuncio' ──────────────────────────────────────────────────
  /** State + handlers de los 3 campos textuales del modo Anuncio. Cada
   *  uno es `{ mode: 'auto'|'custom'|'hidden', value: string }`. */
  anuncioTitulo?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioTituloChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioDescripcion?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioDescripcionChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioCta?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioCtaChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioColors?: string[];
  onAnuncioColorsChange?: (next: string[]) => void;
  /** Paleta de la marca activa (`brand_profiles.colors`) — default del
   *  color picker. */
  anuncioBrandColors?: string[];
}
/**
 * La columna de la plantilla: lo que va ARRIBA de la tarjeta (hoy el interruptor
 * Imagen/Video) y la tarjeta debajo —
 * ADR-2026-09-08-el-switch-de-imagen-o-video-va-arriba-de-la-plantilla.
 *
 * Sin `encabezado` no se dibuja nada: la tarjeta vuelve a ser el ítem de la fila, tal
 * cual estaba, y ningún otro modo se entera.
 *
 * Con él, la columna toma el ancho que le queda a la derecha del panel (`flex-1`) y
 * centra su contenido: la tarjeta se lleva el alto que sobra y su ancho sale de la
 * proporción, que es lo que la mantiene centrada en el área libre sin el `mx-auto` que
 * usaba cuando era ítem de la fila.
 */
const ColumnaDeLaPlantilla: React.FC<{
  encabezado: React.ReactNode;
  children: React.ReactNode;
}> = ({ encabezado, children }) =>
  encabezado ? (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col items-center gap-3">
      <div className="shrink-0">{encabezado}</div>
      {children}
    </div>
  ) : (
    <>{children}</>
  );

/**
 * Cuántos productos por fila en la grilla de MOBILE.
 *
 * Dos, no las tres del panel de escritorio: ese número está calculado para una columna de
 * 615px, y en un teléfono deja tarjetas de ~110px donde la foto —una prenda sobre una cama
 * o un colgador— no se distingue de la de al lado. Es el mismo criterio (y el mismo
 * número) que el modo Modelo, ADR-2026-09-04-los-productos-de-mobile-van-de-a-dos.
 */
const COLUMNAS_PRODUCTOS_MOBILE = 2;

export const PresetDetailSheet: React.FC<PresetDetailSheetProps> = ({
  preset,
  animated,
  migas,
  step,
  previewList,
  categoriasDePlantillas,
  onElegirPlantilla,
  rubro,
  rubroMarca,
  unSoloProducto = false,
  configuracionRestaurada = false,
  generacionEnCurso = false,
  selectedProducts,
  imagenBase,
  modelSelection,
  fondoVal,
  formatoVal,
  resolucion,
  otraConfig,
  sidebarWidth,
  onClose,
  onUtilizarPreset,
  onUtilizarMultiple,
  multiPresetIds,
  onShowProducts,
  onAddProducts,
  onConfirmProducts,
  onProductosEnVivo,
  onBackToChoose,
  onClearProducts,
  onRemoveProduct,
  onStartUpload,
  onStartNewProduct,
  onSkipProduct,
  sinProducto = false,
  dialogoLoading = false,
  dialogoError = null,
  onBulkUpload,
  userPlan = null,
  onAddMoreFiles,
  onRemovePendingFile,
  onCancelUpload,
  onConfirmUpload,
  pendingProductFiles,
  pendingProductName,
  onPendingNameChange,
  uploadingProduct,
  uploadError,
  bulkUploadingProducts = false,
  onModelChange,
  columnaConfiguracion,
  onCambiarModeloYPose,
  cederZIndex = false,
  modelSelectionsSerie = [],
  onToggleModelSerie,
  variosModelos = false,
  onVariosModelosChange,
  modelSelectionsExtra,
  onModelChangeExtras,
  multiModelEnabled,
  onMultiModelEnabledChange,
  onOpenModelPicker,
  onOpenModelSheet,
  onOpenModelSheetForMultiSlot,
  multiProductsPerSlot,
  onTapSlotProducts,
  onOpenFondoSheet,
  onFondoChange,
  onFormatoChange,
  onResolucionChange,
  onOpenFormatPicker,
  onOtraChange,
  ganchoId,
  onGanchoIdChange,
  voiceGuion,
  onVoiceGuionChange,
  onVideoDurationChange,
  calidad,
  onCalidadChange: setCalidad,
  onGenerate,
  onGenerateProduction,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  userModels,
  userAuthId,
  userCompanyId,
  onModelCreated,
  allPoses,
  customPoses,
  onUploadCustomPose,
  uploadingCustomPose,
  onDeleteCustomPose,
  poseIds,
  onPoseIdsChange,
  vistasProducto = [],
  vistaIds = [],
  onVistaIdsChange,
  vistaProductosExcluidos = {},
  onToggleVistaProducto,
  fotosProductoTotalImagenes = 0,
  multiPoseEnabled,
  onOpenPosePicker,
  onSubirPose,
  bibliotecaFondos,
  fondosGuardados,
  fondoSelection,
  onFondoSelectionChange,
  onOpenFondoPicker,
  fondoView,
  onFondoViewChange,
  onFondoSaved,
  lockSlotCount = false,
  catalogoVistas,
  onCatalogoVistasChange,
  onConfirmCatalogoVistas,
  onBackFromCatalogoConfigure,
  onGenerateCatalog,
  brandId,
  onProductsRefreshed,
  previewMediaTab,
  onPreviewMediaTabChange,
  videoDuration,
  videoMultiScene,
  videoCreditCostPerImage,
  onOpenVideoConfig,
  anuncioTitulo,
  onAnuncioTituloChange,
  anuncioDescripcion,
  onAnuncioDescripcionChange,
  anuncioCta,
  onAnuncioCtaChange,
  anuncioColors,
  onAnuncioColorsChange,
  anuncioBrandColors,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  // Helpers de kind:
  //  • isDesignFlow agrupa los kinds que comparten layout del sheet
  //    (sin preview, sin imagen full-bleed, image scaled object-contain
  //    en choose-product/upload-product, configure mobile full-screen y
  //    sticky Generate). Incluye design-modelo, design-auto y anuncio.
  //  • isFullScreenConfigure identifica los kinds cuyo step `configure`
  //    ocupa TODO el panel en DESKTOP (aside full-width transparente, sin
  //    imagen a la izquierda, scroll de ventana y Generar flotante en el
  //    header). Aplica a 'design-modelo' (muchas opciones: Modelo + Pose +
  //    Fondo + Formato), a 'anuncio' (productos + 3 textos + colores +
  //    formato) y a 'video-influencer' (Gancho + Voz y guion + opciones,
  //    ADR-533). 'design-auto' NO — tiene pocas opciones (solo Formato +
  //    Instrucción) y se mantiene en el aside lateral derecho. Esto solo
  //    afecta el layout desktop; mobile usa `fullScreenConfigureMobile`.
  const isDesignFlow =
    preset.kind === 'design-modelo' ||
    preset.kind === 'design-auto' ||
    preset.kind === 'fotos-producto' ||
    preset.kind === 'anuncio';
  const isFullScreenConfigure =
    preset.kind === 'design-modelo' ||
    // "Fotos de producto" tiene 4 secciones (producto + vistas + fondo +
    // opciones): en el aside lateral la grilla de vistas queda impracticable.
    preset.kind === 'fotos-producto' ||
    preset.kind === 'anuncio' ||
    preset.kind === 'video-influencer';

  /**
   * Modos con el rediseño de la pantalla "Elegí el producto", en DESKTOP.
   *
   * Mantiene las dos columnas de siempre (muestra a la izquierda, productos a la
   * derecha) y cambia lo de adentro: la muestra va más grande con una bajada corta, el
   * panel de productos ocupa el 35% del ancho, pierde el bloque de "Subir nuevo
   * producto" de arriba —el alta pasó a ser la primera tarjeta de la grilla, y abre en un
   * drawer— y las tarjetas van a dos columnas, sin marco y con el nombre encima de la
   * foto.
   *
   * Empezó en `catalogo-ia` y se replicó a `design-modelo`, a `design-auto`
   * (ADR-2026-08-21-automatico-con-el-diseno-nuevo) y a `video-influencer`
   * (ADR-2026-08-21-video-con-el-diseno-nuevo). A propósito NO alcanza a muebles /
   * infografía / anuncio, que comparten este render pero no el pedido.
   */
  /**
   * Catálogo IA con la columna única: qué productos, qué vistas, los ajustes y el
   * Generar, todo junto, y lo que se elige se abre en un panel lateral sobre el área de
   * la derecha (ADR-2026-08-24-catalogo-toma-el-diseno-de-modelo).
   *
   * Es el diseño que estrenó el modo Modelo. Reemplaza al flujo de dos pasos —elegir
   * producto → elegir vistas—, así que `step` deja de mandar en lo que se dibuja: la
   * columna es la misma siempre.
   *
   * SÓLO DESKTOP por ahora: en mobile el modo sigue con su bottom sheet de dos pasos.
   */
  const catalogoUnaColumna = preset.kind === 'catalogo-ia';

  /**
   * Automático también abre en el panel lateral lo que se elige — por ahora sólo el
   * Formato, que es lo único que elige (ADR-2026-08-28-automatico-formato-en-el-panel).
   *
   * No es "una columna" como Catálogo: su columna ya era única. Lo que comparte es la
   * CAPA: la grilla de formatos se abre al lado de la columna, que queda a la vista,
   * en vez de en el bottom sheet centrado que tapaba la pantalla entera.
   */
  const autoConPanelLateral = preset.kind === 'design-auto';

  /**
   * Qué se está eligiendo en el panel lateral, o `null` si está cerrado.
   *
   * Lo comparten Catálogo (productos / fondo / modelo) y Automático (formato): es el
   * mismo panel, en el mismo lugar, y lo único que cambia es la grilla de adentro.
   */
  const [panelCatalogo, setPanelCatalogo] = React.useState<
    null | 'productos' | 'vistas' | 'fondo' | 'modelo' | 'formato' | 'plantilla'
  >(null);

  /**
   * Categoría marcada en el panel lateral de plantillas. `null` = todas.
   *
   * Es el mismo single-select de los chips de la pantalla de Plantillas, incluido tocar
   * la activa para apagarla (ADR-2026-09-08-el-panel-de-plantillas-filtra-por-categoria).
   */
  const [filtroPlantilla, setFiltroPlantilla] = React.useState<string | null>(null);
  /**
   * Abrir el panel deja la plantilla ACTUAL a la vista, no el principio de la grilla —
   * ADR-2026-09-08-el-panel-de-plantillas-abre-en-la-elegida.
   *
   * Con 224 tiles, abrir arriba de todo obliga a buscar dónde está parado uno antes de
   * poder seguir explorando. Se centra en su contenedor y NO con `scrollIntoView`: ése
   * scrollea todos los ancestros scrollables y podía llevarse también la página de atrás.
   *
   * Va en un `useLayoutEffect` atado a la APERTURA y no en un callback ref: React asigna
   * los refs de abajo hacia arriba, así que cuando el del tile corre el del contenedor
   * todavía es `null` y el centrado no pasaba (medido: `scrollTop` en 0). El efecto corre
   * con los dos ya puestos. Y al depender sólo de la apertura, pasa UNA vez: si corriera
   * en cada render de la grilla, filtrar devolvería el scroll a la activa y el usuario no
   * podría moverse.
   */
  const scrollerPlantillasRef = React.useRef<HTMLDivElement | null>(null);
  const tilePlantillaActivaRef = React.useRef<HTMLButtonElement | null>(null);
  React.useLayoutEffect(() => {
    if (panelCatalogo !== 'plantilla') return;
    const cont = scrollerPlantillasRef.current;
    const el = tilePlantillaActivaRef.current;
    if (!cont || !el) return;
    // Con `getBoundingClientRect` y no con `offsetTop`: éste se mide contra el ancestro
    // POSICIONADO más cercano, que acá no es el scroller sino el panel, así que sobraba
    // el alto de los chips y la elegida terminaba arriba de todo en vez de centrada.
    const c = cont.getBoundingClientRect();
    const e = el.getBoundingClientRect();
    cont.scrollTop = Math.max(
      0,
      cont.scrollTop + (e.top - c.top) - c.height / 2 + e.height / 2,
    );
  }, [panelCatalogo]);

  /** Pestaña del picker de modelos dentro del panel lateral. */
  const [modelViewLateral, setModelViewLateral] =
    React.useState<ModelPickerView>('list');

  /**
   * El área de las dos columnas. La usa el panel lateral para saber qué clicks son "por
   * fuera": los de acá adentro lo cierran, los de los sheets que se abren desde él —que
   * viven en portales colgados del `body`— no.
   */
  const areaDosColumnasCatalogoRef = React.useRef<HTMLDivElement>(null);

  /**
   * Dónde arranca el panel lateral flotante: pegado al borde derecho de la columna de
   * configuración.
   *
   * Se MIDE en vez de escribirse. El offset era `calc(35% + 2rem)`, que valía cuando el
   * panel medía siempre 35%; desde que el paso 2 baja a 320px
   * (ADR-2026-08-27-el-panel-del-paso-2-baja-a-320) el flotante arrancaba donde habría
   * terminado un panel que ya no existe, dejando una franja muerta entre los dos. Con la
   * medición sigue al panel mida lo que mida, sin repetir acá la tabla de anchos —que
   * son cuatro y viven en el `className` del `<aside>`—.
   */
  const panelConfigRef = React.useRef<HTMLElement | null>(null);
  const [offsetPanelLateral, setOffsetPanelLateral] = React.useState<
    number | null
  >(null);
  React.useLayoutEffect(() => {
    const contenedor = areaDosColumnasCatalogoRef.current;
    const panel = panelConfigRef.current;
    if (!contenedor || !panel) return;
    const medir = () => {
      const c = contenedor.getBoundingClientRect();
      const p = panel.getBoundingClientRect();
      // + el `gap-6` de la fila: el flotante arranca donde arrancaría la columna de al
      // lado, no pegado al borde del panel.
      setOffsetPanelLateral(Math.round(p.right - c.left + 24));
    };
    medir();
    // El ancho del panel cambia con el PASO, no sólo con el viewport, y el paso no pasa
    // por un resize: se observan los dos elementos para cubrir las dos causas.
    const ro = new ResizeObserver(medir);
    ro.observe(contenedor);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [catalogoUnaColumna, step, panelCatalogo]);

  const modoConDisenoNuevo =
    preset.kind === 'catalogo-ia' ||
    preset.kind === 'design-modelo' ||
    preset.kind === 'design-auto' ||
    // Fotos de producto entra al rediseño — ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo.
    // Es el modo hermano del Catálogo (la tanda sale de producto × vista), así que toma
    // su misma anatomía: la muestra del modo de un lado y el panel de configuración del
    // otro, con lo que se elige —Productos, Vistas, Fondo, Formato— abriéndose en el
    // panel lateral (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral).
    preset.kind === 'fotos-producto';

  /**
   * PLANTILLAS eligiendo el producto: el panel a la izquierda y la plantilla a la
   * derecha — ADR-2026-09-07-el-paso-de-producto-de-plantillas-toma-el-diseno-de-los-modos.
   *
   * Es el reparto de los modos del rediseño, pero SÓLO acá: no entra a
   * `modoConDisenoNuevo` porque esa bandera cambia otras veinte cosas —la cabecera con
   * breadcrumb, el bloque de subida, las tarjetas, el `configure` a dos columnas— que en
   * plantillas o no aplican o cambiarían pasos que no se pidió tocar. Lo que se comparte
   * es la ANATOMÍA de este paso: dónde va cada columna y cuánto mide el panel.
   */
  /**
   * Los TRES tipos de card que muestra la pantalla de Plantillas: las plantillas de
   * verdad, las poses de la colección virtual "Fondo blanco" (`design`) y las vistas
   * solo-producto (`design-producto`). Se ven iguales en la grilla y llevan al mismo
   * flujo, así que también tienen que verse iguales acá — con `template` sola, la primera
   * card de la grilla (que es una pose) se quedaba con el reparto viejo.
   */
  const esPlantilla =
    preset.kind === 'template' ||
    preset.kind === 'design' ||
    preset.kind === 'design-producto';

  const plantillaEligeProducto =
    esPlantilla &&
    (step === 'choose-product' ||
      step === 'pick-product' ||
      step === 'upload-product');

  /**
   * El paso 3 de una plantilla: modelo, formato e instrucción —
   * ADR-2026-09-08-la-configuracion-de-plantillas-va-al-panel-de-la-izquierda.
   *
   * Mismo reparto que el paso anterior: lo que se opera a la izquierda y la plantilla a
   * la derecha. Antes era el espejo, y encima el panel cambiaba de lado entre un paso y
   * el siguiente.
   */
  const plantillaConfigura = esPlantilla && step === 'configure';

  /** Los dos pasos de plantilla que van con la anatomía de los modos. */
  const plantillaEnElPanel = plantillaEligeProducto || plantillaConfigura;

  /**
   * "Automático" con el diseño nuevo: UNA sola pantalla
   * (ADR-2026-08-21-automatico-con-el-diseno-nuevo).
   *
   * En este modo la escena entera la decide la IA, así que no hay Modelo ni Pose que
   * configurar: todo lo que se elige —Calidad, Fondo, Formato, Cantidad e Instrucción—
   * ya vive en la bandeja del panel de productos. Sin nada que poner en un paso 2, el
   * "Siguiente" desaparece: el CTA es Generar desde el principio y la columna de al lado
   * pasa de la muestra del modo a las tandas.
   */
  /**
   * El modo Automática, a secas. El selector de productos y la segunda pantalla de mobile
   * se gatean con ESTO y no con `autoUnSoloPaso`: mientras el selector está abierto el
   * paso es `pick-product`, así que una condición que pide `choose-product` ahí es falsa
   * y el selector se quedaba con su "Confirmar" propio —que devuelve al paso 1 en vez de
   * abrir las opciones— (ADR-2026-09-23-automatico-en-mobile-tiene-su-generar-3).
   */
  const esAutomatica = preset.kind === 'design-auto';
  const autoUnSoloPaso = esAutomatica && step === 'choose-product';

  /**
   * Mobile: ¿está abierta la SEGUNDA pantalla de Automática, la de las opciones?
   *
   * En el teléfono el modo se recorre en dos pantallas, como Modelo: primero el producto,
   * después la instrucción, Calidad, Formato, la cantidad y el Generar
   * (ADR-2026-09-23-automatico-en-mobile-tiene-su-generar-2). Es la pantalla que el modo
   * había perdido: su `configure` de mobile quedó inalcanzable cuando el paso pasó a
   * quedarse en `choose-product` (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
   *
   * Estado LOCAL y no `panelStep`, por lo mismo que `mobilePaso2` en Modelo: mover el step
   * real arrastraría el layout de desktop —donde el modo es una sola columna— y el resto
   * de lo que ese paso decide.
   *
   * Arranca ABIERTA cuando el modo se reabre con la configuración puesta —el "Atrás" desde
   * los resultados, "Seguir generando"—: es la pantalla de la que se venía, la del Generar.
   * Sólo en mobile: en desktop no se dibuja (`md:hidden`).
   */
  const [autoPaso2Mobile, setAutoPaso2Mobile] = useState(
    () =>
      configuracionRestaurada &&
      preset.kind === 'design-auto' &&
      typeof window !== 'undefined' &&
      !window.matchMedia('(min-width: 768px)').matches,
  );

  /**
   * La segunda pantalla se cierra cuando la generación ARRANCÓ, no al tocar Generar —
   * ADR-2026-09-04-el-generar-de-mobile-espera-a-que-la-carga-este-arriba. Y 350ms
   * después de que se prende, no en el mismo instante: la pantalla de carga entra
   * deslizándose (300ms) y cerrar ésta apenas empieza ese viaje deja ver el paso 1 por
   * detrás. Mismo timer que en Modelo, por el mismo motivo.
   */
  useEffect(() => {
    if (!generacionEnCurso) return;
    const t = window.setTimeout(() => setAutoPaso2Mobile(false), 350);
    return () => window.clearTimeout(t);
  }, [generacionEnCurso]);

  /**
   * Salir del selector de productos QUEDA PENDIENTE hasta que la segunda pantalla terminó
   * de entrar — ADR-2026-09-04-el-confirmar-de-mobile-no-muestra-el-paso-1-al-pasar. Si el
   * "Confirmar" cerrara el selector en el mismo tap, durante los 280ms del deslizamiento
   * se vería el paso 1 por detrás. Montado hasta el `animationend`, lo que hay atrás es
   * la pantalla de la que se viene, y la nueva la tapa entera (z-36 contra z-35).
   */
  const salirDelPickerAutoRef = useRef<(() => void) | null>(null);
  const cerrarPickerAutoPendiente = () => {
    const salir = salirDelPickerAutoRef.current;
    salirDelPickerAutoRef.current = null;
    salir?.();
  };

  /**
   * Mientras la segunda pantalla está abierta, el back del chrome vuelve al PASO 1 del
   * modo en vez de cerrar el sheet. Es el mismo par de effects que en Modelo
   * (ADR-2026-09-04-modelo-mobile-un-solo-back-en-el-paso-2): se re-aplica cada vez que
   * el override cambia y no es el nuestro —`InterfazV2` tiene su propio effect sobre el
   * mismo override, con `panelStep` entre las dependencias, y el "Confirmar" del
   * selector mueve `panelStep` justo antes de abrir esta pantalla—, y al cerrar RESTAURA
   * el override de la pantalla de abajo en vez de dejarlo en `null`.
   */
  const { setMobileBackOverride, mobileBackOverride } = useSidebar();
  const prevBackAutoRef = useRef<(() => void) | null>(null);
  const miBackAutoRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!autoPaso2Mobile) return;
    if (mobileBackOverride === miBackAutoRef.current) return;
    prevBackAutoRef.current = mobileBackOverride;
    const mio = () => setAutoPaso2Mobile(false);
    miBackAutoRef.current = mio;
    // Doble flecha: el setter de `useState` trata una función como updater.
    setMobileBackOverride(() => mio);
  }, [autoPaso2Mobile, mobileBackOverride, setMobileBackOverride]);
  useEffect(() => {
    if (!autoPaso2Mobile) return;
    return () => {
      setMobileBackOverride(() => prevBackAutoRef.current);
      miBackAutoRef.current = null;
    };
  }, [autoPaso2Mobile, setMobileBackOverride]);

  /**
   * ...y además estamos en un paso que dibuja las dos columnas.
   *
   * El gate por step importa para `design-modelo`, que tiene el `configure` a pantalla
   * completa: sin él, el 35% de ancho del panel se le aplicaría también a esa pantalla,
   * que no es de elegir producto y usa todo el ancho a propósito.
   *
   * Sólo desktop: esta rama del render ya es la de desktop (mobile arma su propio
   * bottom sheet más arriba), así que no hace falta preguntar por el breakpoint.
   */
  const disenoElegirProducto =
    modoConDisenoNuevo &&
    (step === 'choose-product' ||
      step === 'pick-product' ||
      step === 'upload-product');

  /**
   * `configure` a DOS COLUMNAS: la configuración a la izquierda —donde hasta el paso
   * anterior estaba la imagen de muestra— y el panel de productos intacto a la derecha.
   *
   * Antes, tocar "Siguiente" en estos modos cambiaba la pantalla ENTERA: el panel de
   * productos desaparecía y la configuración se abría a todo el ancho. Con esto el paso
   * se lee como un avance sobre la misma pantalla —la mitad que no cambió sigue ahí, y
   * los productos elegidos quedan a la vista mientras se configura— en vez de como una
   * pantalla nueva.
   *
   * Es lo único que separa a estos modos del `configure` full-screen del resto; el
   * `configureFullScreen` de abajo es la condición vieja con esta excepción restada, y
   * es la que gobierna el layout de una sola columna.
   */
  const configureDosColumnas =
    // Sólo `design-modelo`, y no todo `modoConDisenoNuevo`: el `configure` del Catálogo
    // no es este panel sino el review de cards a pantalla completa
    // (`CatalogoCardsFullscreen`), que se monta aparte y tapa esta rama entera; y
    // `design-auto` directamente no tiene paso 2.
    preset.kind === 'design-modelo' && step === 'configure';

  /**
   * El mismo layout de dos columnas, en el paso de VISTAS del Catálogo.
   *
   * El pedido fue que el Catálogo se comporte como "Imagen con modelo": que al avanzar,
   * el panel de productos siga ahí y lo que cambie sea la columna de al lado, donde se
   * eligen las poses. En el Catálogo ese paso no es `configure` —el `configure` es el
   * review de cards— sino `select-vistas`, que hasta ahora se abría como un overlay a
   * pantalla completa y se llevaba puesta la pantalla entera, productos incluidos.
   *
   * Sólo `catalogo-ia`: la V2 es el banco de pruebas de la pantalla vieja y tiene que
   * seguir mostrándola tal cual, y muebles usa el mismo step con otro catálogo de
   * vistas (3 fijas) que no pidió esto.
   */
  const catalogoVistasDosColumnas =
    preset.kind === 'catalogo-ia' && step === 'select-vistas';

  /**
   * Paso 3 del Catálogo —el review— dibujado como una columna más, no como pantalla
   * completa: el panel de la izquierda se queda, igual que al pasar del 1 al 2
   * (ADR-2026-08-27-catalogo-el-review-convive-con-el-panel).
   */
  const catalogoReviewDosColumnas =
    preset.kind === 'catalogo-ia' && step === 'configure';

  /**
   * Las cards que quedaron vivas en el review, después de los descartes.
   *
   * El "Generar" del paso 3 está en el PANEL, no en el review, así que la lista tiene
   * que subir hasta acá: generando con las combinaciones crudas, las X del review no
   * descontarían nada y se cobraría lo descartado.
   */
  const [cardsActivasReview, setCardsActivasReview] = React.useState<
    CatalogoCard[]
  >([]);

  /**
   * De dónde sale el número de imágenes de la tanda, en una línea.
   *
   * "Tocá la X para descartarla" salió: el gesto ya está a la vista —cada card tiene su
   * X— y lo que el número necesitaba era una CUENTA, no una instrucción. Un "8 imágenes"
   * a secas no deja verificar nada; "4 productos × 2 poses" sí, y es lo que se revisa en
   * esta pantalla (ADR-2026-08-27-la-explicacion-dice-la-cuenta).
   *
   * Se cuenta sobre las cards ACTIVAS y no sobre lo elegido: con algo descartado, el
   * producto de las dos listas ya no da el total. Cuando no cuadra —porque se descartó, o
   * porque una vista no aplica a todos los productos— la línea deja de multiplicar y dice
   * qué es cada imagen, que sigue siendo verdad.
   */
  /**
   * Cuántos modelos multiplican la tanda.
   *
   * 1 mientras "Utilizar varios modelos" esté apagado —y también con él prendido pero un
   * solo modelo elegido—. Automático (sin `modelSelection`) cuenta como uno: es un
   * modelo, el que decide la IA (ADR-2026-08-28-catalogo-varios-modelos).
   */
  const modelosDeLaTandaCount = variosModelos
    ? Math.max(1, (modelSelection ? 1 : 0) + modelSelectionsSerie.length)
    : 1;
  /** Imágenes reales del review: las solo-producto no se multiplican por modelo. */
  const imagenesReview = React.useMemo(
    () => contarImagenesCatalogo(cardsActivasReview, modelosDeLaTandaCount),
    [cardsActivasReview, modelosDeLaTandaCount],
  );
  const notaDeLaTanda: string | undefined = React.useMemo(() => {
    const n = imagenesReview;
    if (n === 0) return undefined;
    const productos = new Set(cardsActivasReview.map((c) => c.product.id)).size;
    const poses = new Set(cardsActivasReview.map((c) => c.vista.id)).size;
    const cabecera = `${n} ${n === 1 ? 'imagen' : 'imágenes'}`;
    // Con varios modelos la cuenta ya no es producto × pose: las cards solo-producto NO
    // se multiplican, así que el producto de los tres números casi nunca da el total.
    // En vez de mentir con una multiplicación que no cierra, la línea nombra el modelo
    // como el tercer eje y deja que el número hable.
    if (modelosDeLaTandaCount > 1)
      return `${cabecera}: cada pose se genera con los ${modelosDeLaTandaCount} ${palabra.plural} elegidos.`;
    if (productos * poses !== n)
      return `${cabecera}: una por cada combinación de producto y pose.`;
    return `${cabecera}: ${productos} ${
      productos === 1 ? 'producto' : 'productos'
    } × ${poses} ${poses === 1 ? 'pose' : 'poses'}.`;
  }, [cardsActivasReview, imagenesReview, modelosDeLaTandaCount]);

  /**
   * El modelo de la tanda, para la card del panel en el paso 3.
   *
   * Mismo criterio que usaba el review para su card global: sólo aplica si alguna vista
   * de la tanda NO es solo-producto (esas no usan modelo). `null` = la card no va.
   */
  const modeloDeLaTanda = React.useMemo(() => {
    // `catalogoVistasDosColumnas` y no `catalogoPaso2`: éste se declara MÁS ABAJO
    // (depende de `catalogoPasoProductos`) y leerlo acá rompía en runtime por TDZ —
    // "Cannot access 'catalogoPaso2' before initialization"—. Los dos valen lo mismo en
    // el paso 2, y `catalogoVistasDosColumnas` además lo dice sin rodeos.
    if (!catalogoVistasDosColumnas && !catalogoReviewDosColumnas) return null;
    // La card aparece SÓLO cuando ya hay elegida una vista que usa modelo. Con la tanda
    // vacía NO se muestra: elegir modelo antes de saber si va a haber alguien en la foto
    // es configurar algo que todavía no existe, y en una tanda de puro packshot ese
    // control nunca llega a aplicar. Aparece cuando la decisión se vuelve real
    // (ADR-2026-08-27-el-modelo-aparece-cuando-hay-alguien-a-quien-vestir, que supersede
    // en este punto a ADR-2026-08-27-en-el-paso-2-el-modelo-ocupa-el-lugar-de-las-vistas).
    const usaModelo = catalogoVistas.some((v) => v.kind !== 'solo-producto');
    if (!usaModelo) return null;
    const sel = modelSelection;
    const label =
      !sel || sel.mode === 'auto'
        ? 'Automático'
        : sel.mode === 'preset'
          ? sel.name
          : sel.mode === 'custom'
            ? 'Personalizado'
            : 'Subido';
    const imageUrl =
      sel?.mode === 'preset'
        ? sel.imageUrl
        : sel?.mode === 'upload'
          ? sel.previewUrl
          : null;
    return { label, imageUrl };
  }, [
    catalogoVistasDosColumnas,
    catalogoReviewDosColumnas,
    catalogoVistas,
    modelSelection,
  ]);

  /**
   * El `configure` de Fotos de producto, a DOS COLUMNAS —
   * ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo.
   *
   * Nació como el `select-vistas` del Catálogo —la grilla de vistas ocupando la columna
   * de enfrente—, pero desde
   * ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral las vistas se
   * eligen en el panel lateral, como el Fondo: la columna vuelve a ser la MUESTRA del
   * modo y este paso es el panel compacto con sus cards (Productos, Vistas, Fondo),
   * Formato, instrucción y el Generar.
   */
  const fotosProductoDosColumnas =
    preset.kind === 'fotos-producto' && step === 'configure';

  /**
   * Al pasar al paso 2 de Fotos de producto, el panel de VISTAS se abre SOLO.
   *
   * "Siguiente" existe para elegir las vistas: es la única decisión que el paso agrega,
   * y desde que la grilla se mudó al panel lateral
   * (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral) llegar acá con
   * todo cerrado dejaba una pantalla que no pedía nada — había que descubrir la card y
   * tocarla para que apareciera lo que el paso vino a hacer.
   *
   * Sólo en la TRANSICIÓN (el efecto depende del flag, no del panel): si el usuario lo
   * cierra, se queda cerrado, y al generar —que lo cierra— tampoco vuelve a abrirse.
   */
  React.useEffect(() => {
    if (!fotosProductoDosColumnas) return;
    setPanelCatalogo('vistas');
  }, [fotosProductoDosColumnas]);

  /**
   * Fotos de producto, PASO 1: la pantalla que sólo elige productos.
   *
   * Es el complemento de `fotosProductoDosColumnas` y existe por lo mismo que
   * `catalogoPasoProductos`: en este paso el pie del panel va SIN los ajustes rápidos
   * (ADR-2026-09-04-los-ajustes-de-fotos-de-producto-bajan-al-paso-2). Acá no se
   * configura nada —la pantalla pide una sola cosa, qué productos entran— y las celdas
   * de Calidad, Fondo y Formato volvían a meter la configuración entera abajo, que es
   * justo lo que este paso saca de encima. Es la misma decisión que ya tomó el Catálogo,
   * el modo hermano, en ADR-2026-08-26-catalogo-paso-1-grilla-siempre-visible.
   */
  const fotosProductoPasoProductos =
    preset.kind === 'fotos-producto' && !fotosProductoDosColumnas;

  /** Cualquiera de los pasos que se dibujan como panel + columna que cambia. */
  const pasoDosColumnas =
    configureDosColumnas ||
    catalogoVistasDosColumnas ||
    catalogoReviewDosColumnas ||
    fotosProductoDosColumnas;

  /**
   * Los dos pasos del Catálogo donde el panel se angosta y le cede el ancho a la columna
   * de al lado — ver `ANCHO_PANEL_CONFIG_CATALOGO`.
   *
   * No incluye el paso 1: ahí la columna de al lado es la MUESTRA del modo (una imagen),
   * que no gana nada con más ancho, y el panel es lo único que se opera.
   */
  const catalogoPanelAngosto =
    catalogoVistasDosColumnas || catalogoReviewDosColumnas;
  // Fotos de producto SALIÓ de acá: tomaba el ancho angosto del Catálogo (400px) y
  // abría con un panel visiblemente más chico que el de Modelo, siendo el mismo panel
  // del mismo rediseño (ADR-2026-09-06-un-solo-ancho-de-panel-para-los-modos). Su
  // grilla de vistas no pierde nada: lo que el panel se lleva de más lo compensa la
  // fila, que ahora llega al borde derecho
  // (ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde). Los pasos 2 y 3
  // del CATÁLOGO se quedan angostos: ahí la columna de al lado es la grilla de vistas o
  // el review, que es lo que se está operando
  // (ADR-2026-09-04-el-panel-se-angosta-en-los-pasos-2-y-3-del-catalogo).

  /**
   * Pasos donde lo que se toca en el panel de productos vale YA, sin confirmar.
   *
   * En el paso 1 el "Siguiente" confirma la selección; a partir del paso 2 ese botón pasa
   * a ser "Generar", así que si el panel sigue ahí —y sigue: es la mitad de la pantalla—
   * agregar o sacar un producto no tendría dónde aplicarse y la tanda saldría con la
   * selección vieja: se cobraría una cosa y se generaría otra.
   */
  const seleccionEnVivo = autoUnSoloPaso || pasoDosColumnas;

  /**
   * Resumen del pie del CTA en "Automática": cuántas imágenes salen y de qué.
   *
   * La frase la escribe `resumenTandaAutomatica`, que vive con el resto de los helpers
   * de la tanda: el pie de MOBILE dice exactamente lo mismo y lo saca de ahí
   * (ADR-2026-09-02-automatico-mobile-toma-los-ajustes-del-desktop).
   */
  const resumenAutomatica = (imagenes: number, productos: number): string =>
    resumenTandaAutomatica({ imagenes, productos });

  /** Modelos elegidos en la grilla: el principal + la serie. Puede ser 0 — la grilla deja
   *  deseleccionar todo, y ahí no hay nada que generar. */
  const modelosElegidos =
    (modelSelection ? 1 : 0) + modelSelectionsSerie.length;

  /**
   * Qué falta para poder generar en el modo Video, con el id del bloque adonde mandar
   * el foco. Prioridad: gancho → modelo → voz → guion (la misma que el hint del
   * ConfigurePanel). Los ids existen SÓLO en el render desktop del panel (prop
   * `domIds`, para no duplicarlos con el mobile).
   *
   * Vive acá arriba —y no adentro del pill flotante, que fue donde nació— porque con el
   * diseño nuevo el Generar es el CTA del panel de productos y necesita lo mismo
   * (ADR-2026-08-21-video-con-el-diseno-nuevo).
   */
  const faltaEnInfluencer: { msg: string; targetId: string } | null =
    preset.kind !== 'video-influencer'
      ? null
      : !ganchoId
        ? {
            msg: 'Elegí un gancho para generar.',
            targetId: 'influencer-desktop-gancho-card',
          }
        : !modelSelection || modelSelection.mode === 'auto'
          ? {
              // En este modo no existe "Automático": modelo sí o sí.
              msg: `Elegí un ${palabra.singular} para generar.`,
              targetId: 'influencer-desktop-modelo-card',
            }
          : voiceGuion &&
              voiceGuion.guionMode !== 'auto' &&
              voiceGuion.vozId == null
            ? {
                msg: 'Elegí una voz para generar.',
                targetId: 'influencer-desktop-voz-row',
              }
            : voiceGuion && isVoiceGuionIncomplete(voiceGuion)
              ? {
                  msg:
                    voiceGuion.guionMode === 'audio'
                      ? 'Grabá tu guion para generar.'
                      : 'Escribí tu guion para generar.',
                  targetId: 'influencer-desktop-guion-block',
                }
              : null;

  /** Paso 2 de "Imagen con modelo" con una de las dos grillas vacía: no hay imagen
   *  posible, así que el CTA se apaga y en lugar del resumen de la tanda dice qué falta.
   *
   *  Sólo ese modo: Video comparte el layout de dos columnas pero no tiene grilla de
   *  poses, así que su `poseIds` vacío no significa que falte nada — lo que le falta lo
   *  dice `faltaEnInfluencer` (ADR-2026-08-21-video-con-el-diseno-nuevo). */
  const faltaModeloOPose =
    configureDosColumnas &&
    preset.kind === 'design-modelo' &&
    (modelosElegidos === 0 || poseIds.length === 0);

  /**
   * Las imágenes que saldrían de la selección actual: una por cada vista × producto
   * compatible.
   *
   * El Catálogo genera DESDE el paso de vistas, sin review en el medio, así que el costo
   * que se muestra y el payload que se manda se arman acá. Sale del mismo helper que usa
   * el review para que las dos entradas a la generación no puedan divergir.
   */
  const cardsDeLaTanda = React.useMemo(
    () =>
      catalogoVistasDosColumnas
        ? construirCardsDeCatalogo(catalogoVistas, selectedProducts)
        : [],
    [catalogoVistasDosColumnas, catalogoVistas, selectedProducts],
  );

  /**
   * Qué falta para poder generar en el paso de vistas del Catálogo, o null si está listo.
   *
   * El botón se APAGA mientras falte algo, en vez de dejarse apretar y sacudir la grilla:
   * el sacudón sirve cuando lo que falta está a la vista en la misma columna, y acá el
   * panel y las vistas son dos columnas distintas —con el panel comprimido, los productos
   * ni siquiera se ven—. Apagado, más una línea que dice qué falta, se entiende sin
   * probar.
   */
  /**
   * La tanda de la COLUMNA ÚNICA de Catálogo: una imagen por combinación vista ×
   * producto.
   *
   * Aparte de `cardsDeLaTanda` porque aquélla sólo se arma en el paso `select-vistas`,
   * que en este diseño no existe: acá las dos decisiones conviven en la misma pantalla y
   * el número tiene que estar disponible desde el primer render.
   */
  const cardsCatalogoUnaColumna = React.useMemo(
    () =>
      catalogoUnaColumna
        ? construirCardsDeCatalogo(catalogoVistas, selectedProducts)
        : [],
    [catalogoUnaColumna, catalogoVistas, selectedProducts],
  );

  /** Qué falta para poder generar, en el orden en que la columna lo pide. */
  const faltaCatalogoUnaColumna: string | null = !catalogoUnaColumna
    ? null
    : selectedProducts.length === 0
      ? 'Elegí al menos un producto.'
      : catalogoVistas.length === 0
        ? 'Elegí al menos una vista.'
        : cardsCatalogoUnaColumna.length === 0
          ? 'Las vistas elegidas no aplican a estos productos.'
          : null;

  /**
   * Paso 1 del Catálogo: la columna pide SÓLO productos.
   *
   * El modo volvió a tener dos pasos —productos, y después vistas + ajustes— porque la
   * pantalla única no dejaba leer lo único que hay que entender al entrar: que se
   * eligen VARIOS productos y que de cada uno salen VARIAS imágenes
   * (ADR-2026-08-26-catalogo-paso-1-solo-productos). El paso 2 es `select-vistas`, que
   * ya se dibujaba como panel + columna que cambia.
   *
   * En este paso la columna NO es `PanelCatalogoCompacto`: es la GRILLA de productos
   * (`PickProductPanel`), la misma que usan los modos de diseño, con las tarjetas
   * siempre a la vista (ADR-2026-08-26-catalogo-paso-1-grilla-siempre-visible).
   */
  // Los DOS pasos siguientes quedan afuera: el de vistas y el review, que desde
  // ADR-2026-08-27-catalogo-el-review-convive-con-el-panel también es una columna al lado
  // del panel. Sin restar el review, `configure` volvía a dibujar la grilla de productos
  // con su "Siguiente" mientras al lado se revisaba la tanda.
  const catalogoPasoProductos =
    catalogoUnaColumna &&
    !catalogoVistasDosColumnas &&
    !catalogoReviewDosColumnas;

  /** Paso 2 del Catálogo: la columna angosta con las cards de lo ya decidido. */
  const catalogoPaso2 = catalogoUnaColumna && !catalogoPasoProductos;

  const faltaEnCatalogo: string | null = !catalogoVistasDosColumnas
    ? null
    : selectedProducts.length === 0
      ? 'Elegí al menos un producto.'
      : catalogoVistas.length === 0
        ? 'Elegí al menos una vista.'
        : cardsDeLaTanda.length === 0
          ? // Hay productos y hay vistas, pero ninguna combinación es compatible (p. ej.
            // quedó el maniquí invisible elegido y el producto que quedó es un zapato).
            // Sin este caso el botón se apagaría sin decir por qué.
            'Las vistas elegidas no aplican a estos productos.'
          : null;

  /**
   * Qué falta para poder generar en Fotos de producto (paso 2) — el mismo criterio y el
   * mismo lugar que el `faltaEnCatalogo` de arriba: el CTA se apaga y dice por qué.
   *
   * El caso de "hay productos y vistas pero ninguna combinación queda activa" existe acá
   * también: cada vista tiene su checklist de productos, y desactivarlos todos no es
   * posible (el último no se puede sacar), pero una vista puede no aplicarle a NINGÚN
   * producto de la selección actual. Ahí `fotosProductoTotalImagenes` cae a 0 y el botón
   * cobraría una tanda vacía.
   */
  const faltaEnFotosProducto: string | null = !fotosProductoDosColumnas
    ? null
    : selectedProducts.length === 0
      ? 'Elegí al menos un producto.'
      : vistaIds.length === 0
        ? 'Elegí al menos una vista.'
        : fotosProductoTotalImagenes === 0
          ? 'Las vistas elegidas no aplican a estos productos.'
          : null;

  /**
   * Lo que dice la card de VISTAS del panel de Fotos de producto: cuántas van y cuáles.
   *
   * Mismo criterio que la card de Productos —hasta cuatro miniaturas y la última celda
   * pasa a ser el "+N"—: en este modo cada vista extra es una imagen más por producto
   * que se genera y se paga, así que cuántas van es parte de lo que la card tiene que
   * decir. Las vistas sin foto de ejemplo (hoy sólo "Automática") no aportan miniatura
   * pero SÍ cuentan para el número.
   */
  const cardVistasProducto = React.useMemo(() => {
    const elegidas = vistasProducto.filter((v) => vistaIds.includes(v.id));
    const MAX = 4;
    const hayExtra = elegidas.length > MAX;
    const thumbs = elegidas
      .map((v) => v.thumbUrl)
      .filter((u): u is string => !!u);
    return {
      valor:
        elegidas.length === 0
          ? 'Sin elegir'
          : elegidas.length === 1
            ? elegidas[0].nombre
            : `${elegidas.length} elegidas`,
      imagenes: thumbs.slice(0, hayExtra ? MAX - 1 : MAX),
      extra: hayExtra ? `+${elegidas.length - (MAX - 1)}` : undefined,
    };
  }, [vistasProducto, vistaIds]);

  /**
   * El resumen de la tanda de Fotos de producto: cuántas imágenes salen y de dónde.
   *
   * Cuando la multiplicación no cierra —hay vistas que no le aplican a todos los
   * productos, o productos desactivados en el checklist de una vista— NO se escribe
   * "P × V": esa cuenta daría otro número que el que se cobra. En ese caso se nombra el
   * total y se dice de qué está hecho, igual que hace el review del Catálogo.
   */
  const resumenFotosProducto = (): string => {
    const n = fotosProductoTotalImagenes;
    const cabecera = `${n} ${n === 1 ? 'imagen' : 'imágenes'}`;
    const productos = selectedProducts.length;
    const vistas = vistaIds.length;
    if (productos * vistas !== n)
      return `${cabecera}: una por cada combinación de producto y vista.`;
    return `${cabecera} · ${productos} ${
      productos === 1 ? 'producto' : 'productos'
    } × ${vistas} ${vistas === 1 ? 'vista' : 'vistas'}`;
  };

  /**
   * El rótulo de la celda Fondo en Fotos de producto.
   *
   * El modo ofrece dos caminos —liso (cuatro colores) o escena descrita—, así que la
   * celda dice CUÁL quedó, con el color o el texto: `fondoSelectionLabel` es del picker
   * grande y habla de biblioteca, guardados y automático, que acá no existen.
   */
  const etiquetaFondoProducto = (): string => {
    if (!esFondoProducto(fondoSelection)) return 'Fondo liso';
    if (fondoSelection.mode === 'descripcion')
      return fondoSelection.text.trim() || 'Describir escena';
    // El MISMO nombre que la opción elegida en el picker, y no el color
    // (ADR-2026-09-04-el-rotulo-del-fondo-dice-lo-que-dice-el-picker): con "Fondo liso"
    // marcado a la derecha, acá decía "Fondo blanco" y las dos mitades de la pantalla
    // nombraban distinto la misma elección. Qué color es lo dice el recuadro de la card,
    // que está pintado de ese color.
    return 'Fondo liso';
  };

  /**
   * El color del recuadro de la card de Fondo en Fotos de producto.
   *
   * El fondo liso no tiene foto: lo que la card muestra ES el color, igual que el blanco
   * del Catálogo. Sin selección todavía, el default del modo (blanco).
   */
  const colorFondoLiso = (sel: typeof fondoSelection): string => {
    // `colorLiso` vive SÓLO en la rama 'blanco' de la unión —la de descripción no tiene
    // color—, así que el narrowing es por `mode` y no por `esFondoProducto` solo.
    const id =
      esFondoProducto(sel) && sel.mode === 'blanco'
        ? (sel.colorLiso ?? 'blanco')
        : 'blanco';
    return (
      COLORES_FONDO_LISO.find((c) => c.id === id)?.hex ??
      COLORES_FONDO_LISO[0].hex
    );
  };

  const configureFullScreen =
    isFullScreenConfigure &&
    step === 'configure' &&
    !configureDosColumnas &&
    // Fotos de producto salió del `configure` a pantalla completa: su paso 2 son dos
    // columnas (productos + vistas), como el del Catálogo
    // (ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo). Sin esto, las dos
    // ramas de layout quedaban prendidas a la vez.
    !fotosProductoDosColumnas &&
    // El review del Catálogo dejó de tomarse la pantalla: ahora es la columna de al lado
    // del panel (ADR-2026-08-27-catalogo-el-review-convive-con-el-panel).
    !catalogoReviewDosColumnas;
  /**
   * Estos modos se presentan como una SECCIÓN y no como un panel flotante —
   * ADR-2026-08-03-anuncio-se-ve-como-seccion, extendido a Imagen automática y
   * Imagen con modelo por ADR-2026-08-03-seccion-tambien-automatica-y-modelo, a
   * Catálogo por ADR-2026-08-03-seccion-tambien-catalogo y a Video con influencer por
   * ADR-2026-08-03-seccion-tambien-video-influencer.
   *
   * Sigue siendo el mismo panel `fixed` de todos los modos: lo que cambia es lo que
   * se ve. Sin fondo ni borde propios —el lienzo lo pone el shell— y con breadcrumb
   * + `<h1>` como `/plantillas`, en vez de un recuadro sin título.
   *
   * La lista va explícita y no atada a `isDesignFlow` (que describe el FLUJO: mismos
   * steps, misma preview chica): son dos cosas distintas y esto es cómo se ve la
   * pantalla. Catálogo lo confirma — entró por
   * ADR-2026-08-03-seccion-tambien-catalogo y NO es un `isDesignFlow`, así que
   * atarlas lo habría dejado afuera. El día que un modo nuevo entre al flujo tampoco
   * tiene por qué heredar la presentación sin que alguien lo decida.
   *
   * Lo que sigue con el marco flotante en esta pantalla: los presets de plantillas
   * (`template` / `design`). El asistente no está en la lista porque no pasa por acá —
   * tiene pantalla propia (`AsistenteMode`).
   */
  const esSeccion =
    preset.kind === 'anuncio' ||
    preset.kind === 'design-auto' ||
    preset.kind === 'design-modelo' ||
    esCatalogoIA(preset.kind) ||
    // "Fotos de producto" faltaba en esta lista y era el único modo que
    // seguía abriendo con el marco flotante — la misma clase de olvido que
    // [[ADR-2026-08-13-modo-nuevo-lista-de-kinds-en-homecards]] (un modo nuevo
    // se cae del `if` sin error ni warning). Además dejaba al modo SIN botón
    // Generar en desktop: es `isFullScreenConfigure`, así que el
    // `hideGenerateButton` de más abajo apaga el Generar del ConfigurePanel
    // esperando que lo reponga el pill del header — y ese pill vive dentro de
    // este `esSeccion`. Ver ADR-2026-08-21-fotos-producto-sin-generar-en-desktop.
    preset.kind === 'fotos-producto' ||
    // Infografía entró con ADR-2026-08-05-infografia-se-ve-como-seccion: quedó afuera
    // porque cuando se escribió esta lista el modo no lo veía nadie (flag global en
    // `false`), no porque su pantalla fuera distinta. Con el modo visible para su rubro
    // era la única que abría en un recuadro flotante y sin título.
    preset.kind === 'infografia' ||
    // Catálogo de muebles, por lo mismo y con una razón de más: en Hogar y Decoración
    // ES el Catálogo —reemplaza a `catalogo-ia`, que ahí se oculta, y se llama igual
    // (ADR-2026-08-05-el-catalogo-de-muebles-se-llama-catalogo)—. Dejarlo con el marco
    // flotante hacía que "Catálogo" se viera distinto según el rubro de la marca.
    preset.kind === 'catalogo-muebles' ||
    preset.kind === 'video-influencer';
  /**
   * Título de la pantalla en la presentación de sección — el nombre CORTO del modo,
   * el mismo que la tira de la home, la sidebar, el sheet de Generar en mobile y la
   * hoja del breadcrumb (ADR-2026-08-03-un-solo-nombre-por-modo).
   *
   * No es `preset.title`: en Anuncio los dos dicen "Anuncio" y no se notaba, pero en
   * estos dos el `title` es la frase de la card del carrusel ("Generar imagen
   * automáticamente") y el `<h1>` habría contradicho al breadcrumb que tiene tres
   * líneas más arriba, en la misma pantalla. `preset.title` queda de respaldo para
   * los kinds que no son un modo.
   */
  /**
   * El paso en el que la pantalla scrollea por dentro — el único en el que el
   * contenido puede pasar el alto de la ventana (ver el `overflow-y-auto` de más
   * abajo, en el wrapper que envuelve al encabezado y al contenido).
   *
   * Sale como constante porque de él dependen tres cosas que tienen que estar de
   * acuerdo: quién es el scroller, que el encabezado viva adentro de él
   * (ADR-2026-08-04-el-breadcrumb-no-se-pega) y quién se queda con los 72px de aire
   * de arriba (ADR-2026-08-04-el-encabezado-pegado-se-lleva-el-aire).
   */
  // A dos columnas NO scrollea el wrapper: cada columna maneja su propio scroll, igual
  // que en los pasos de elegir producto.
  const contenidoScrollea = configureFullScreen;

  /**
   * La columna de resultados se sale de su caja y llega al marco —
   * ADR-2026-09-06-la-columna-de-resultados-llega-al-marco.
   *
   * Sólo cuando la columna sostiene las TANDAS y sólo en los modos del rediseño: los
   * márgenes negativos de `COLUMNA_RESULTADOS_AL_BORDE` cancelan paddings CONCRETOS
   * —el `lg:!pr-[52px]` de `FILA_MODO_REDISENO` y el `lg:pt-[52px]` que el panel pone
   * en la presentación de sección—, así que fuera de esa combinación no hay nada que
   * cancelar y la columna se pasaría de largo. Importa porque una tanda vieja sin
   * `modo` guardado se cuela en cualquier modo (`tandasDelModo` la deja pasar), y con
   * la condición atada sólo a `columnaConfiguracion` eso la sacaba de la pantalla.
   */
  const columnaResultadosAlBorde =
    !!columnaConfiguracion && esSeccion && modoConDisenoNuevo;
  /**
   * El título de la pantalla es el ÚLTIMO nivel del breadcrumb —
   * ADR-2026-08-04-el-titulo-es-la-hoja-del-breadcrumb.
   *
   * Era siempre el nombre del modo, así que en `configure` el breadcrumb decía
   * "Inicio / Modelo / Configurar" y el `<h1>` de abajo decía "Modelo": el rastro
   * marcaba un nivel que el título contradecía. Es el mismo criterio que ya cumplían
   * las otras secciones del shell —`/plantillas` titula "Plantillas", que es su hoja—,
   * sólo que acá la hoja cambia con el paso.
   *
   * El fallback cubre a los presets sin breadcrumb (una plantilla, un modo que no está
   * en `MODE_KIND_TO_PATH`): ahí `migas` viene `undefined` y el nombre del modo sigue
   * siendo lo mejor que se puede decir.
   */
  const tituloSeccion =
    migas?.[migas.length - 1]?.label ??
    nombreCortoModo((preset.kind && MODE_KIND_TO_PATH[preset.kind]) || '', preset.title);
  // Configure MOBILE a pantalla completa (sin hero, panel = 100dvh, Generar
  // sticky): los design-flow desde siempre + video-influencer (ADR-533).
  const fullScreenConfigureMobile =
    isDesignFlow || preset.kind === 'video-influencer';
  // Preview del preset como imagen CHICA y centrada (object-contain con
  // padding), no full-bleed. Lo usan los modos de diseño/catálogo y también
  // 'video-influencer', cuyo asset del preview es la miniatura compuesta
  // (producto → influencer): a tamaño hero se veía desproporcionada frente a
  // los otros modos. Ver ADR-507.


  /** Drawer de alta de producto — lo abre la tarjeta placeholder de la grilla. */
  const [subirProductoAbierto, setSubirProductoAbierto] = useState(false);

  /**
   * El nodo donde el panel de productos DIBUJA la tanda: el lugar que ocupa la muestra
   * del modo en el paso 1 del Catálogo (ADR-2026-09-04-la-tanda-se-muda-a-la-muestra).
   *
   * Va como estado y no como `useRef` porque el panel tiene que RE-RENDERIZAR cuando el
   * nodo aparece: con una ref, el portal se quedaría esperando un render que nadie
   * dispara. Y el nodo se monta SIEMPRE (vacío no ocupa nada), no recién con el primer
   * producto: si apareciera con la elección, la tira se dibujaría un frame colgada del
   * panel y saltaría, y el vuelo de la tarjeta aterrizaría donde ya no está.
   */
  const [destinoTanda, setDestinoTanda] = useState<HTMLElement | null>(null);
  /**
   * Lo elegido EN VIVO en el paso 1, sólo para saber si la muestra ya cedió su lugar a
   * la tanda. La selección de verdad la sigue confirmando el "Siguiente" del panel: esto
   * no la reemplaza, apaga la ilustración.
   */
  const [elegidosEnVivo, setElegidosEnVivo] = useState<MockProductV2[]>([]);
  /**
   * La muestra del modo ya cumplió: hay al menos un producto en la tanda, así que su
   * lugar pasa a mostrar lo que se está armando y la ilustración se funde.
   *
   * La ilustración NO se desmonta: se le baja la opacidad y queda ocupando su caja. El
   * tablero se dibuja encima (absolute), así que nada se mueve al aparecer — y el vuelo
   * de la tarjeta elegida, que mide su celda de destino en el mismo frame en que se
   * elige, aterriza donde la celda va a quedar.
   */
  const tandaEnLaMuestra = catalogoPasoProductos && elegidosEnVivo.length > 0;


  /**
   * ¿La preview del paso 2 está comprimida?
   *
   * Arranca comprimida cada vez que se entra a `configure` —ahí el panel es un
   * recordatorio, no la herramienta— y el control del título la abre para cambiar la
   * selección sin volver al paso anterior. Se resetea al cambiar de paso: si quedara
   * abierta, volver a entrar mostraría el panel ancho y la configuración apretada.
   */
  const [previewComprimida, setPreviewComprimida] = useState(true);
  useEffect(() => {
    setPreviewComprimida(true);
  }, [step]);
  /** Paso 2 y sin descomprimir: el panel va angosto y en modo preview. */
  // Catálogo queda AFUERA: en su paso 2 la columna de al lado es el picker de vistas y
  // el panel sigue siendo la configuración del modo —no un recordatorio—, así que a
  // 280px no entra. Ver ADR-2026-08-26-catalogo-paso-1-solo-productos.
  const panelComprimido =
    pasoDosColumnas &&
    previewComprimida &&
    !catalogoUnaColumna &&
    // Fotos de producto, por el mismo motivo que el Catálogo: en su paso 2 el panel NO es
    // un recordatorio de lo elegido sino la configuración del modo (productos + Calidad +
    // Fondo + Formato + Generar), y en 280px no entra.
    !fotosProductoDosColumnas;

  const smallCenteredPreview =
    isDesignFlow ||
    esCatalogoIA(preset.kind) ||
    // Fotos de producto entra con el rediseño
    // (ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo). No es cosmético:
    // esta bandera es la que enciende el layout de DOS COLUMNAS de desktop —la
    // presentación de un lado, el panel del otro—, así que sin ella el modo se quedaba
    // con el layout viejo por más que sus flags de paso dijeran otra cosa. En mobile
    // además pasa a mostrar su hero contenido sobre blanco, como los otros modos, en vez
    // del recorte a sangre sobre gris.
    preset.kind === 'fotos-producto' ||
    preset.kind === 'catalogo-muebles' ||
    preset.kind === 'infografia' ||
    preset.kind === 'video-influencer';
  /**
   * La cabecera (breadcrumb + `<h1>`) baja a la COLUMNA de la imagen —
   * ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna.
   *
   * Sólo en los pasos que dibujan las dos columnas (imagen a la izquierda, panel de
   * productos a la derecha). En `preview` el layout es un carrusel a todo lo ancho y en
   * el `configure` full-screen la imagen no está, así que ahí la cabecera se queda
   * arriba, cruzando la pantalla, que es lo que corresponde cuando abajo no hay dos
   * columnas que respetar.
   */
  const cabeceraEnColumna =
    esSeccion &&
    step !== 'preview' &&
    smallCenteredPreview &&
    !configureFullScreen &&
    // Con las columnas invertidas la cabecera queda en la de la DERECHA, y un breadcrumb
    // que arranca a mitad de pantalla no se lee como el rastro de dónde estás. Vuelve
    // arriba, cruzando las dos columnas, que es donde está en el resto del shell.
    !modoConDisenoNuevo;
  // Hero collage "producto → resultados" para catálogo-muebles / infografía
  // (reemplaza al `<img>` en el preview chico). Undefined = kind sin collage.
  // El rubro puede remapear qué collage se muestra (ver `HERO_COLLAGE_POR_RUBRO`).
  const kindDelHero =
    (rubroMarca && preset.kind
      ? HERO_COLLAGE_POR_RUBRO[rubroMarca]?.[preset.kind]
      : undefined) ?? preset.kind;
  const heroCollage = kindDelHero ? HERO_COLLAGE[kindDelHero] : undefined;
  /**
   * MOBILE, paso 1: la pantalla se arma como la del modo Modelo —
   * ADR-2026-09-08-el-paso-1-de-catalogo-en-mobile-copia-al-de-modelo, extendido a Fotos
   * de producto por ADR-2026-09-08-fotos-de-producto-y-editar-copian-el-paso-1-de-modelo.
   *
   * Título y bajada ARRIBA de la muestra (lo que ya hace desktop,
   * ADR-2026-08-21-modelo-v2-la-muestra-se-queda; en mobile el mismo mensaje vivía dentro
   * del panel, con ícono y en otro tono — ADR-2026-09-02-catalogo-mobile-toma-el-encabezado-de-desktop),
   * la portada en el medio y UNA card al pie.
   *
   * Los tres modos que la usan son los que ya comparten anatomía en desktop
   * (`modoConDisenoNuevo` menos Modelo, que tiene su propio shell): su paso 1 es sólo
   * elegir productos.
   */
  const pasoUnoComoModelo =
    (esCatalogoIA(preset.kind) ||
      preset.kind === 'fotos-producto' ||
      preset.kind === 'design-auto') &&
    step === 'choose-product';

  /**
   * Título + bajada que ENCABEZAN la muestra en DESKTOP, en vez del pie de foto —
   * ADR-2026-08-21-modelo-v2-la-muestra-se-queda, extendido a Automática y Fotos de
   * producto por ADR-2026-09-08-automatica-y-fotos-de-producto-encabezan-la-muestra.
   *
   * Con encabezado, el `<p>` de abajo NO se dibuja: sería el mismo mensaje dos veces.
   * Es un valor y no tres condiciones sueltas porque el bloque es idéntico en los tres
   * modos y lo único que cambia es el copy; los que no están acá conservan su pie de
   * foto (`null`).
   *
   * El Catálogo se pregunta por `catalogoPasoProductos` y no por el `kind`: su paso 1 es
   * el único de sus tres pasos que dibuja la muestra.
   */
  const encabezadoMuestra: { titulo: string; bajada: string } | null =
    catalogoPasoProductos
      ? {
          titulo: 'Elegí tus productos y sus vistas',
          bajada: 'Atlas arma el catálogo de cada uno en segundos',
        }
      : !disenoElegirProducto
        ? null
        : preset.kind === 'design-auto'
          ? {
              titulo: 'Atlas elige la escena por vos',
              bajada:
                'Subí tu producto y la IA arma la imagen final en segundos, sin configurar nada.',
            }
          : preset.kind === 'fotos-producto'
            ? {
                titulo: `Fotos de tu producto, sin ${palabra.singular}`,
                bajada:
                  'Elegí uno o varios y desde qué vistas — sobre un fondo liso o en una escena.',
              }
            : null;
  // Modos que generan a partir de UN solo producto: el video con influencer
  // (ADR-563) y lo que el host marque con `unSoloProducto` —hoy, las plantillas de
  // Belleza (ADR-2026-09-14-la-plantilla-de-belleza-se-genera-con-un-producto)—. El
  // picker va en `singleSelect`. Fotos de producto estuvo acá hasta
  // ADR-2026-08-30-fotos-producto-multi-producto: ahora acepta varios productos y
  // genera producto × vista.
  const singleProductMode = preset.kind === 'video-influencer' || unSoloProducto;

  // Costo en créditos/tokens del preset — se muestra en el pill al
  // costado del texto en todos los botones "Generar" (el del
  // ConfigurePanel y el flotante de design-modelo desktop).
  //   • unidad 'segundo' → ratePer × durationSeconds (video).
  //   • cualquier otra unidad → ratePer × 1.
  //   • design-auto → multiplicamos también por autoImageCount
  //     (1-6 imágenes que pide el usuario).
  //   • design-modelo con multi-modelo → la generación produce 1
  //     imagen con todos los modelos juntos, no multiplicamos.
  //   • design-modelo con SERIE de modelos (grilla multi-select) → sí:
  //     cada modelo es una generación aparte (× poses).
  //   • multi-preset (templates) → multiplicamos por la cantidad
  //     seleccionada.
  // ¿Está activo el modo video? Solo aplica al kind 'template' — los
  // otros kinds tienen su propio flow y no respetan este toggle.
  const isVideoModeActive =
    (previewMediaTab === 'video' && preset.kind === 'template') ||
    // El modo "Video con influencer" siempre está en modo video (reusa el
    // layout de plantilla-video).
    preset.kind === 'video-influencer';
  // Contador "cantidad de imágenes" del modo Imagen personalizada. Solo
  // aplica con una sola pose (o pose Automática) y sin varios modelos —
  // ver designModeloCount.ts. Se usa para el pill de créditos, el stepper
  // del header desktop y (vía el mismo helper) el payload de InterfazV2.
  const designModeloCount = resolveDesignModeloCount({
    rawCount: autoImageCount,
    poseCount: poseIds.length,
    multiModelEnabled,
    modelCount: 1 + modelSelectionsSerie.length,
    max: autoImageCountMax,
  });
  // Los modos con selector de resolución: Automático, Modelo y Catálogo. En los tres el
  // control se llama "Calidad" en la UI del rediseño y "Resolución" en la fila
  // desplegable de desktop, pero es el mismo ajuste y el mismo estado
  // (ADR-2026-08-25-resolucion-de-generacion-2k-4k).
  const tieneSelectorResolucion =
    preset.kind === 'design-auto' ||
    preset.kind === 'design-modelo' ||
    esCatalogoIA(preset.kind) ||
    // Fotos de producto entra con la Calidad de vuelta en su columna, ahora en el pie
    // (ADR-2026-09-06-la-calidad-de-fotos-de-producto-es-real). Entrar acá es lo que hace
    // que el pill del botón —y el guard de saldo— cobren el multiplicador de la
    // resolución elegida; sin esto el selector sería un control que no cambia el precio
    // de lo que sí se genera en 4K.
    preset.kind === 'fotos-producto' ||
    // Las plantillas de imagen, con la Calidad en su pie como la de Modelo
    // (ADR-2026-09-28-las-plantillas-eligen-la-calidad). El stepper de la producción
    // multiplica este `creditCost` por la cantidad, y el carrusel también sale en la
    // resolución elegida, así que el pill cuenta cada imagen de la tanda al mismo precio.
    plantillaEligeCalidad(preset, isVideoModeActive);

  const creditCost = (() => {
    // ── Video con influencer (ADR-671) ──────────────────────────────────
    // Regla ÚNICA del modo: 1 crédito por segundo de video FINAL. Con gancho
    // el video final suma los 4s del clip de apertura; sin gancho arranca
    // directo con el principal y esos 4 créditos no se cobran.
    //   • Guion escrito/grabado (ADR-547): la duración la marca el guion
    //     (duración del audio o estimación de lectura, tope 14s) y el gancho
    //     suma sus 4s aparte (`INFLUENCER_GUION_BASE_COST`).
    //   • Automático: la barra de Duración YA es el total (con gancho el
    //     backend recorta el principal a barra-4), así que el costo es la
    //     barra pelada. Antes de ADR-671 el automático cobraba además 1
    //     crédito fijo por el frame — se sacó para que la regla sea una sola.
    // Si el guion todavía no está (texto vacío / sin audio) cae al valor de
    // la barra; el Generar está bloqueado igual, así que es solo cosmético.
    if (preset.kind === 'video-influencer') {
      const conGancho = !isSinGancho(ganchoId);
      const guionDur = voiceGuion ? getGuionDurationSec(voiceGuion) : null;
      if (guionDur != null)
        return (conGancho ? INFLUENCER_GUION_BASE_COST : 0) + guionDur;
      return videoDuration;
    }
    const ratePer = preset.creditosPorUnidad;
    if (ratePer == null) return null;
    const unitMul =
      preset.unidadConsumo === 'segundo'
        ? preset.durationSeconds || 1
        : 1;
    const presetCount = multiPresetIds.length > 0 ? multiPresetIds.length : 1;
    // 'design-auto' y 'anuncio' generan `autoImageCount` imágenes (el
    // control de Cantidad), así que el costo escala con esa cantidad.
    const autoCountMul =
      preset.kind === 'design-auto' || preset.kind === 'anuncio'
        ? autoImageCount
        : 1;
    // design-modelo (single-modelo) genera 1 imagen por pose seleccionada
    // — el backend itera el array de `pose` y devuelve 1 imagen por cada
    // entry. En multi-modelo no aplica: ese flow tiene pose single-select.
    // Con UNA sola pose el multiplicador es el contador de cantidad
    // (`designModeloCount`), que en ese caso pide N variaciones del mismo
    // prompt (ADR-2026-07-30-imagen-personalizada-cantidad).
    const poseCountMul =
      preset.kind === 'design-modelo' && !multiModelEnabled
        ? Math.max(1, poseIds.length, designModeloCount.count)
        : 1;
    // En multi-modelo (design-modelo con varios modelos, o design /
    // E-commerce con pose 2+ personas) el backend genera 1 imagen por
    // cada modelo extra. El multiplicador es `1 + extras` = total de
    // personas / slots en la composición.
    const modelCountMul =
      (preset.kind === 'design-modelo' || preset.kind === 'design') &&
      multiModelEnabled
        ? 1 + modelSelectionsExtra.length
        : 1;
    // Serie de modelos (grilla multi-select de design-modelo): cada modelo es
    // una generación aparte — un request por modelo, con las poses elegidas
    // dentro de cada uno. Multiplica sobre `poseCountMul`
    // (ADR-2026-08-20-modelos-multi-select-serie).
    const modelSerieMul =
      preset.kind === 'design-modelo' && !multiModelEnabled
        ? 1 + modelSelectionsSerie.length
        : 1;
    // 'fotos-producto' genera UNA imagen por cada par (producto, vista)
    // aplicable y activo — el total ya viene sumado del padre
    // (`fotosProductoTotalImagenes`, un request por par; ver
    // ADR-2026-08-13-fotos-producto-fan-out-por-vista y
    // ADR-2026-08-30-fotos-producto-multi-producto-2). Sin vistas el Generar
    // está bloqueado, así que el piso de 1 es solo cosmético.
    const vistaCountMul =
      preset.kind === 'fotos-producto'
        ? Math.max(1, fotosProductoTotalImagenes)
        : 1;
    // Resolución: multiplica el costo de CADA imagen entregada (1K=1 · 2K=2 · 4K=3).
    // Sólo cuenta en los modos que tienen el selector — en el resto el valor del
    // state nunca se muestra y no puede encarecer nada por accidente.
    // Ver ADR-2026-08-25-resolucion-de-generacion-2k-4k.
    const resolucionMul = tieneSelectorResolucion
      ? creditosPorResolucion(resolucion)
      : 1;
    const imageCost =
      ratePer *
      unitMul *
      presetCount *
      autoCountMul *
      poseCountMul *
      modelCountMul *
      modelSerieMul *
      vistaCountMul *
      resolucionMul;
    // Cuando el modo es video, sumamos el costo de animar cada imagen
    // generada (1 video por imagen). El total que ve el user en el botón
    // de Generar = imágenes + videos.
    const totalImages =
      presetCount *
      autoCountMul *
      poseCountMul *
      modelCountMul *
      modelSerieMul *
      vistaCountMul;
    const videoCost = isVideoModeActive
      ? videoCreditCostPerImage * totalImages
      : 0;
    // La Calidad/resolución ya multiplicó dentro de `imageCost` (`resolucionMul`), así
    // que acá no se suma nada por ella.
    return imageCost + videoCost;
  })();

  // Costo de "Generar producción" = generación normal + carrusel. El
  // carrusel (generar_nuevos_angulos) produce 2 imágenes, así que sumamos
  // 2 × el costo por imagen del preset.
  const CARRUSEL_IMAGE_COUNT = 2;
  const productionCreditCost =
    creditCost != null && preset.creditosPorUnidad != null
      ? creditCost + CARRUSEL_IMAGE_COUNT * preset.creditosPorUnidad
      : creditCost;

  // Último hint del pill flotante del influencer — se conserva para que el
  // texto no desaparezca de golpe durante el fade-out de la card (ADR-552).
  const lastInfluencerHintRef = useRef<string>('');

  // Index para navegar el preview en DESKTOP. Solo se inicializa con el
  // preset que abrió el sheet — en mobile cada card se renderiza
  // independiente, no se usa este state.
  const [previewIndex, setPreviewIndex] = useState<number>(() => {
    const i = previewList.findIndex((p) => p.id === preset.id);
    return i >= 0 ? i : 0;
  });

  // `panelOpen` controla el slide-up del panel inferior (steps !=
  // preview) animando max-height de 0 → valor final. Eso causa que la
  // imagen (flex-1 arriba) también anime su altura por el cambio de
  // flex space, dando el efecto "panel sube mientras la imagen se
  // acomoda al espacio restante" pedido por el user. Reset a false
  // cuando volvemos a preview.
  const [panelOpen, setPanelOpen] = useState<boolean>(step !== 'preview');

  // Ref al scroll container del preview mobile — necesario para
  // sincronizar el scroll inicial con el `previewIndex` resuelto
  // desde el preset tappeado.
  const mobilePreviewScrollRef = useRef<HTMLDivElement | null>(null);

  /* Indicador "Deslizá para explorar" de la vista ampliada en mobile —
     ADR-2026-08-20-la-vista-ampliada-avisa-que-se-desliza.

     La vista es un scroll vertical con snap y una plantilla por pantalla, sin ningún
     borde visible: la card ocupa el viewport entero, así que nada delata que hay más
     abajo. El hint lo dice y se va al primer deslizamiento.

     Se arma UNA vez por montaje del sheet y no se vuelve a mostrar: si el usuario ya
     deslizó, ya lo sabe, y re-armarlo al volver de "elegir producto" sería repetirle
     una instrucción que no necesita. */
  const [mostrarHintDeslizar, setMostrarHintDeslizar] = useState(true);
  /* La posición que el scroll tiene por decisión NUESTRA, no del usuario.

     Es necesaria porque el preview NO arranca siempre en 0: se abre en la plantilla que
     el usuario tocó, y el effect de más abajo hace `el.scrollTop = previewIndex *
     clientHeight`. Ese salto dispara `onScroll` igual que un dedo, así que sin esta
     referencia el hint se apagaría solo —antes de que nadie lo lea— cada vez que se abre
     una plantilla que no es la primera. Comparando contra ella, lo que apaga el hint es
     el movimiento REAL. */
  const scrollProgramaticoRef = useRef(0);

  // Step "renderizado": sigue a `step` pero retrasa la transición
  // hacia 'preview' por 300ms para que el panel inferior alcance a
  // animar su salida (max-height → 0) ANTES de que el render
  // condicional cambie de rama. Sin esto, al volver de
  // choose-product el panel desaparecía instantáneo.
  const [renderedStep, setRenderedStep] = useState(step);

  // Effect 1: sync `renderedStep` con `step`.
  //   OUT (→ preview): primero cerrar el panel (panelOpen=false), 300ms
  //   después cambiar renderedStep para que el render cambie de rama.
  //   IN (preview → otro): cambiar renderedStep inmediato → la rama
  //   else se monta. La animación de entrada la dispara Effect 2.
  useEffect(() => {
    if (step === 'preview') {
      setPanelOpen(false);
      const id = window.setTimeout(() => setRenderedStep('preview'), 300);
      return () => window.clearTimeout(id);
    }
    setRenderedStep(step);
  }, [step]);

  // Effect 2: animación de entrada del panel cuando se viene de
  // 'preview'. useLayoutEffect garantiza que corre síncrono justo
  // después del commit que monta el panel — así setPanelOpen(false)
  // se aplica antes del paint, y luego el rAF dispara setPanelOpen(true)
  // en el siguiente frame para que CSS transition detecte el cambio
  // 0 → 55dvh. Sin el useLayoutEffect, React podía batchear los dos
  // setStates en un único commit y la animación se "saltaba".
  const prevRenderedStepRef = useRef(renderedStep);
  useLayoutEffect(() => {
    const prev = prevRenderedStepRef.current;
    prevRenderedStepRef.current = renderedStep;
    if (renderedStep === 'preview') return;
    // Solo animamos cuando venimos de preview (mount fresco del panel).
    // Transiciones entre non-preview steps (choose-product → configure)
    // no resetean panelOpen — el panel sigue abierto y solo cambia
    // max-height de 55dvh a 78dvh por la transición CSS existente.
    if (prev !== 'preview') return;
    setPanelOpen(false);
    let rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => setPanelOpen(true));
    });
    return () => cancelAnimationFrame(rafId);
  }, [renderedStep]);

  // Detección de dirección del cambio de step para elegir la animación
  // del contenido al cambiar entre steps non-preview (choose-product ↔
  // configure ↔ upload-product, etc.). Adelante = el contenido nuevo
  // entra de abajo (`animate-mode-swap`); atrás = de arriba
  // (`animate-mode-swap-back`), dando la sensación de "desandar" el
  // movimiento del panel. La transición preview↔step no usa esto — ahí
  // el panel mismo desliza con max-height.
  const stepOrder = (s: typeof step): number => {
    switch (s) {
      case 'preview':
        return 0;
      case 'choose-product':
        return 1;
      case 'pick-product':
      case 'upload-product':
      case 'select-vistas':
        return 2;
      case 'configure':
        return 3;
      default:
        return 0;
    }
  };
  /**
   * ¿El último cambio de paso fue hacia ATRÁS? Decide de qué lado entra el contenido.
   *
   * Se congela EN EL RENDER del cambio, en un ref, en vez de derivarse en cada render
   * comparando contra un ref que actualiza un `useEffect`. Con aquella versión el valor
   * era correcto en el render del cambio, pero el efecto corría después y dejaba
   * `prev === step`: cualquier re-render posterior —y hay varios: el refresh de
   * productos, el resize del grid— recalculaba `isBack` en `false` y le cambiaba la
   * clase al nodo YA montado, lo que en CSS no es un no-op sino una animación NUEVA. En
   * la práctica, volver de "Configurar" mostraba la animación de avanzar.
   */
  const prevStepForAnimRef = useRef(step);
  const isBackStepRef = useRef(false);
  if (step !== prevStepForAnimRef.current) {
    isBackStepRef.current =
      stepOrder(step) < stepOrder(prevStepForAnimRef.current);
    prevStepForAnimRef.current = step;
  }
  const isBackStepTransition = isBackStepRef.current;
  const swapAnimClass = isBackStepTransition
    ? 'animate-mode-swap-back'
    : 'animate-mode-swap';
  /**
   * Transición de la columna que cambia al pasar de "Elegí el producto" a "Configurar".
   *
   * Se dispara por REMONTAJE: el contenedor lleva un `key` distinto en cada paso, así que
   * React tira el nodo viejo y monta el nuevo, y la animación de entrada corre sola. No
   * hay animación de SALIDA —el nodo viejo se va en el mismo frame—: mantenerlo vivo para
   * desvanecerlo obligaría a duplicar el árbol de la configuración (con sus pickers y sus
   * requests) durante la transición, que es mucho costo para 300ms.
   */
  const pasoAnimClass = isBackStepTransition
    ? 'animate-paso-atras'
    : 'animate-paso-adelante';

  // Sincronizar el scroll con `previewIndex` cada vez que entramos al
  // step preview (mount inicial + volver desde choose-product).
  // Usamos `renderedStep` (no `step`) porque la transición OUT tiene
  // delay: el scroll container del preview branch recién se monta
  // cuando `renderedStep` pasa a 'preview', después de que el panel
  // terminó de animar.
  useEffect(() => {
    if (renderedStep !== 'preview') return;
    const el = mobilePreviewScrollRef.current;
    if (!el || el.clientHeight <= 0) return;
    el.scrollTop = previewIndex * el.clientHeight;
    // Este salto lo decidimos nosotros: queda anotado para que el `onScroll` que
    // dispara no cuente como "el usuario deslizó" y apague el hint.
    scrollProgramaticoRef.current = el.scrollTop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedStep]);

  // Si el user vino del flow multi-select, resolvemos los preset objects
  // a partir de los ids (lookup en previewList) — se usan para mostrar
  // un carrusel en vez de una sola imagen en los steps post-preview.
  const multiPresets = React.useMemo<PresetV2[]>(() => {
    if (multiPresetIds.length < 2) return [];
    return multiPresetIds
      .map((id) => previewList.find((p) => p.id === id))
      .filter((p): p is PresetV2 => !!p);
  }, [multiPresetIds, previewList]);
  const isMultiPreset = multiPresets.length > 1;
  const desktopPreviewPreset = previewList[previewIndex] || preset;

  const canPrev = previewIndex > 0;
  const canNext = previewIndex < previewList.length - 1;

  // Multi-select: aplica a kind 'template' siempre y a kind 'design'
  // cuando la pose tiene UNA sola persona. Las poses multi-persona no
  // admiten multi-select porque el backend solo soporta una pose
  // multi-persona por job (mezclar dos poses con N personas no tiene
  // semántica clara). En multiMode el botón "Utilizar" se reemplaza
  // por "Utilizar seleccionadas (N)" y los cards muestran un check
  // overlay. Tap en card toggle la selección en vez de utilizar.
  //
  // `canMultiSelect` ahora depende del PRESET ACTUAL (currentPreset al
  // navegar en preview), no del preset inicial — para que al swipear
  // entre poses de e-commerce, una pose 2-persona oculte el botón
  // mientras una 1-persona lo muestre.
  // TEMP — multi-selección de PLANTILLAS desactivada y oculta a pedido. El
  // botón "Seleccionar varias" (ícono Copy) del preview de plantillas no se
  // renderiza y su lógica de multi-add queda deshabilitada. El flujo de
  // e-commerce (`design`) NO se toca. Poné en `true` para reactivar.
  const TEMPLATE_MULTISELECT_ENABLED = false;
  const isPresetMultiSelectable = (p: PresetV2 | null | undefined): boolean => {
    if (!p) return false;
    if (p.kind === 'template') return TEMPLATE_MULTISELECT_ENABLED;
    if (p.kind === 'design') return (p.personCount ?? 1) === 1;
    // 'design-producto' (vistas solo-producto de "Fondo blanco") queda
    // fuera a propósito: su generación es 1 vista = 1 request y el flow
    // multi de e-commerce arma `poseIds`, que estas cards no usan.
    return false;
  };
  const [multiMode, setMultiMode] = useState<boolean>(false);
  const [multiSelectedIds, setMultiSelectedIds] = useState<string[]>([]);
  const isMultiSelected = (id: string) => multiSelectedIds.includes(id);
  const toggleMultiId = (id: string) => {
    setMultiSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  // Tap en "Seleccionar varias" arranca el modo y agrega el preset
  // actualmente visible (el que el user está mirando). Sino el user ve
  // checks vacíos y no entiende qué pasa.
  const enterMultiMode = (currentId: string) => {
    setMultiMode(true);
    setMultiSelectedIds([currentId]);
  };
  // Salir de multi mode resetea la selección. Lo llamamos cuando el user
  // toca cancelar o cuando deselect lleva a N=0.
  const exitMultiMode = () => {
    setMultiMode(false);
    setMultiSelectedIds([]);
  };
  const multiSelectedPresets = multiSelectedIds
    .map((id) => previewList.find((p) => p.id === id))
    .filter((p): p is PresetV2 => !!p);
  const handleConfirmMulti = () => {
    if (multiSelectedPresets.length === 0) return;
    onUtilizarMultiple(multiSelectedPresets);
  };
  // Si el user salió del step 'preview' o cambió de colección (previewList
  // distinto), reseteamos el modo para no arrastrar selección stale.
  useEffect(() => {
    if (step !== 'preview') {
      setMultiMode(false);
      setMultiSelectedIds([]);
    }
  }, [step]);

  // Wheel/trackpad navigation en el carousel desktop. Mantenemos un lock
  // corto para que un fling continuo no skippee 5 plantillas de una.
  // Usamos addEventListener con passive: false para poder preventDefault y
  // así el browser no haga back/forward gesture en mac.
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const wheelLockRef = useRef<boolean>(false);
  useEffect(() => {
    if (step !== 'preview') return;
    const el = carouselRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Si el trackpad da deltaX (swipe horizontal) lo preferimos. Sino
      // caemos a deltaY (mouse wheel) — así también con scroll vertical
      // se puede navegar de plantilla, que es el feedback que pidió el user.
      const horizIntent = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const delta = horizIntent ? e.deltaX : e.deltaY;
      if (Math.abs(delta) < 12) return;
      e.preventDefault();
      if (wheelLockRef.current) return;
      if (delta > 0) {
        setPreviewIndex((i) =>
          i < previewList.length - 1 ? i + 1 : i,
        );
      } else {
        setPreviewIndex((i) => (i > 0 ? i - 1 : i));
      }
      wheelLockRef.current = true;
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 450);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [step, previewList.length]);
  // El step content lo compartimos entre mobile y desktop. Para el step
  // 'configure', PASAMOS un flag `useStickyGenerate` para que en desktop
  // el botón Generar quede pegado al bottom del panel (mt-auto). En
  // mobile el panel es un bottom sheet limitado a 55dvh con scroll
  // interno — no aplica el sticky bottom.
  /**
   * `pasoForzado` dibuja el contenido de OTRO paso sin mover el estado. Lo usa el
   * `configure` a dos columnas, que necesita el panel de productos —el contenido de
   * `choose-product`— a la derecha mientras el paso real ya es `configure`.
   */
  /**
   * Cabecera de la pantalla (breadcrumb + título) para meterla DENTRO del panel de
   * productos, en su borde de arriba.
   *
   * Afuera se come una franja del alto por encima de las dos columnas; adentro, la fila
   * arranca pegada al techo y ese alto se lo quedan las columnas, que es lo que se
   * quiere en una pantalla donde la grilla y la configuración pelean por espacio
   * vertical. El `<h1>` va en 26px y no en los 32 de una sección a todo lo ancho: acá
   * vive en una columna angosta y compite con el título propio del panel.
   */
  /**
   * Las migas hasta el MODO, sin el nivel del paso — ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso.
   *
   * `migasModo` (InterfazV2) arma siempre `[Inicio, <modo>, …paso]`, así que los dos
   * primeros niveles son la identidad de la pantalla y el resto es dónde se está parado
   * dentro de ella. El encabezado del panel se queda con los dos primeros: al avanzar de
   * paso NO cambia, que es lo que hace que la pantalla siga siendo "Fotos de producto"
   * y no se convierta en "Configurar". El paso lo dice la columna de al lado, con su
   * propio título ("Elegí las vistas", "Revisá tu catálogo").
   */
  const migasDelModo = migas?.slice(0, 2);
  const tituloDelModo =
    migasDelModo?.[migasDelModo.length - 1]?.label ?? tituloSeccion;
  /**
   * El interruptor Imagen / Video de una plantilla —
   * ADR-2026-09-08-el-switch-de-imagen-o-video-va-arriba-de-la-plantilla.
   *
   * Decide QUÉ se genera con la plantilla: en `video` el sheet entra en
   * `isVideoModeActive` y el flujo pasa a ser el de plantilla-video. Vivía sólo en la
   * cabecera del paso `preview`; con Plantillas entrando directo al paso de producto,
   * ese paso ya no existe y el interruptor se quedaba sin pantalla. Ahora es una
   * variable y la dibujan los dos lugares: el header del preview (donde estaba) y la
   * columna de la plantilla (donde hace falta).
   */
  const toggleImagenVideo = (compacto = false) => (
    <div className="inline-grid grid-cols-2 p-0.5 rounded-full bg-white shadow-sm ring-1 ring-black/[0.06] relative">
      <div
        aria-hidden
        className="absolute top-0.5 bottom-0.5 left-0.5 right-1/2 rounded-full bg-brand-primary transition-transform duration-300 ease-out"
        style={{
          transform:
            previewMediaTab === 'imagen' ? 'translateX(0)' : 'translateX(100%)',
        }}
      />
      <button
        type="button"
        onClick={() => onPreviewMediaTabChange('imagen')}
        // Compacto dentro de la card de la plantilla: con el tamaño de siempre, el
        // interruptor se comía el ancho y dejaba la etiqueta en "Pla…" y el nombre en
        // "Silló…" (ADR-2026-09-08-el-switch-de-imagen-o-video-va-en-la-card-de-la-plantilla).
        className={`relative z-10 inline-flex items-center justify-center rounded-full font-semibold transition-colors duration-300 ${
          compacto ? 'px-3 h-7 text-[12.5px]' : 'px-5 h-8 text-[14px]'
        } ${previewMediaTab === 'imagen' ? 'text-white' : 'text-brand-black/65'}`}
      >
        Imagen
      </button>
      <button
        type="button"
        onClick={() => onPreviewMediaTabChange('video')}
        className={`relative z-10 inline-flex items-center justify-center rounded-full font-semibold transition-colors duration-300 ${
          compacto ? 'px-3 h-7 text-[12.5px]' : 'px-5 h-8 text-[14px]'
        } ${previewMediaTab === 'video' ? 'text-white' : 'text-brand-black/65'}`}
      >
        Video
      </button>
    </div>
  );

  const cabeceraEnPanel = modoConDisenoNuevo || plantillaEnElPanel ? (
    // `pb-5` sólo en el paso de producto de una plantilla: ahí abajo va la grilla de
    // `PickProductPanel` PEGADA, y las tarjetas arrancaban a 4px de la bajada
    // (ADR-2026-09-10-el-paso-de-producto-de-plantillas-le-faltaba-el-aire-de-la-fila-de-recuento).
    //
    // El aire que falta es el que en los otros modos pone la fila de recuento de la
    // grilla ("N productos elegidos", `pt-5 pb-1`): esa fila la gatea `pistasDeUso`,
    // que las plantillas no prenden, así que acá no existe y nada separaba el
    // encabezado del contenido. Por eso el valor es `pt-5` y no uno elegido a ojo.
    //
    // En los demás casos NO va: `modoConDisenoNuevo` sí tiene esa fila —con `pb`
    // propio quedaría el doble— y el paso de configuración de la plantilla trae su
    // propio wrapper con `pt-2`.
    <div className={`px-6 pt-5 ${plantillaEligeProducto ? 'pb-5' : ''}`}>
      <EncabezadoPantalla
        // En plantillas van los TRES niveles y no los dos del modo: el último es la
        // plantilla elegida, y es lo que hace que "Plantillas" —el nivel del medio— sea
        // un link de vuelta a la grilla. En los modos el tercer nivel es el PASO, que
        // sí se recorta (ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso).
        migas={plantillaEnElPanel ? migas : migasDelModo}
        className="!px-0 !pt-0 !pb-2"
        respaldo={
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
        }
      />
      {/* `key` por título: al cambiar de paso el <h1> se remonta y hace su fade. Sin
          desplazamiento — el movimiento lo lleva la columna de al lado, y esta caja es
          justamente la que NO se mueve. */}
      <h1
        key={tituloDelModo}
        className="animate-paso-titulo text-[21px] font-semibold leading-tight text-brand-black/90"
      >
        {tituloDelModo}
      </h1>
      {/* La bajada del paso, sólo mientras se eligen los productos: es donde hay que
          decir que la elección es MÚLTIPLE. La grilla no lo dice —los círculos de
          selección se leen igual con uno que con varios— y el `<h1>` dice el nombre de
          la pantalla, no lo que hay que hacer en ella
          (ADR-2026-09-08-la-eleccion-multiple-se-dice-abajo-del-titulo).
          En el paso 3 no va: ahí ya no se eligen productos. */}
      {plantillaEligeProducto && (
        <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
          {singleProductMode
            ? // Con selección única la frase de arriba mentiría: tocar otro producto
              // REEMPLAZA al elegido, no lo suma.
              'Elegí el producto que va en la plantilla.'
            : 'Elegí uno o varios productos: con más de uno, Atlas arma el outfit completo.'}
        </p>
      )}
    </div>
  ) : undefined;

  const renderStep = (
    variant: 'mobile' | 'desktop',
    pasoForzado?: PanelStep,
  ) => {
    // En mobile, los steps 'pick-product' y 'upload-product' viven en una
    // pantalla full-screen aparte (portal sobre la top bar). El preset
    // sheet inline debajo se queda con el contenido de choose-product como
    // fallback (es lo que el usuario verá cuando cierre esa pantalla).
    const pasoBase = pasoForzado ?? step;
    const effectiveStep =
      variant === 'mobile' &&
      (pasoBase === 'pick-product' || pasoBase === 'upload-product')
        ? 'choose-product'
        : pasoBase;

    // Subida en bloque de catálogo en curso → el spinner ocupa SOLO la card
    // (este panel, donde viven el grid + "Subir nuevo producto"), no toda la
    // pantalla. Mismo patrón visual que ModelProcessingView (ModelPicker).
    if (bulkUploadingProducts) {
      return (
        <div className="flex flex-col items-center justify-center text-center gap-5 h-full min-h-[300px] px-6 py-10">
          <div className="relative w-16 h-16">
            <div
              className="absolute inset-0 rounded-full border-4 border-brand-primary/15 border-t-brand-primary animate-spin"
              style={{ animationDuration: '1s' }}
            />
          </div>
          <div>
            <p className="text-[16px] font-semibold text-brand-black/90">
              Subiendo productos…
            </p>
            <p className="text-[13px] text-[#3C3C43]/55 mt-1.5 max-w-sm">
              Estamos creando un producto por cada foto. Puede tardar unos
              segundos.
            </p>
          </div>
        </div>
      );
    }

    return effectiveStep === 'choose-product' ? (
      // DESKTOP (modos de diseño Y plantillas): fusionamos el "Elegí el
      // producto" (header + Subir) con la lista de productos en un solo panel
      // scrolleable. Mobile mantiene el ChooseProductPanel (flujo de dos pasos
      // / pantalla full-screen separada).
      variant === 'desktop' ? (
        <PickProductPanel
          // Re-montar cuando cambia la selección confirmada (ej. tras una
          // subida en bloque de catálogo, que setea `selectedProducts` SIN
          // navegar de paso): así el grid fusionado re-inicializa su selección
          // interna desde `initialSelectedIds` y muestra lo recién subido
          // tildado. Durante el uso normal (toggles internos) `selectedProducts`
          // no cambia → el key es estable → no remonta.
          //
          // …salvo en los pasos donde la selección viaja EN VIVO (Automática y los pasos a
          // dos columnas): ahí cada tap cambia `selectedProducts`, así que un key derivado
          // de la selección remontaría el panel en cada toque —perdiendo el scroll de la
          // grilla—. En esos pasos la fuente de verdad es la selección interna del panel y
          // el host la espeja, así que no hay nada que re-inicializar.
          //
          // El `preset.id` va SIEMPRE, en las dos ramas: cambiar de modo tiene que
          // remontar el panel (ADR-2026-09-08-cambiar-de-modo-limpia-los-productos). Su
          // selección interna es la fuente de verdad en los pasos en vivo, y con un key
          // constante sobrevivía al cambio y el efecto que la reporta se la devolvía al
          // host un frame después de que el host la hubiera limpiado.
          key={
            seleccionEnVivo
              ? `choose-pick-vivo-${preset.id}`
              : `choose-pick-${preset.id}-${selectedProducts.map((p) => p.id).join('-')}`
          }
          variant="desktop"
          onConfirm={onConfirmProducts}
          onBack={onBackToChoose}
          initialSelectedIds={selectedProducts.map((p) => p.id)}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          rubro={rubro}
          onStartUpload={onStartUpload}
          onStartNewProduct={onStartNewProduct}
          presetKind={preset.kind}
          onSkipProduct={onSkipProduct}
          // Sin el bloque de "Elegí el producto para generar" + "Subir nuevo producto"
          // arriba de la grilla: la imagen de muestra de al lado ya presenta el modo, y
          // el bloque repetía el mensaje empujando los productos fuera de la pantalla.
          // En plantillas el bloque se va por lo mismo que en los modos: la cabecera
          // de arriba ya dice dónde estás, y el botón azul a todo el ancho empujaba los
          // productos fuera de la pantalla. El alta pasa a ser la PRIMERA card de la
          // grilla, como en Catálogo.
          ocultarBloqueSubida={modoConDisenoNuevo || plantillaEligeProducto}
          // El alta arranca desde la primera tarjeta de la grilla y corre en un drawer,
          // en vez de navegar al step `upload-product` y llevarse la pantalla.
          onSubirProducto={
            modoConDisenoNuevo || plantillaEligeProducto
              ? () => setSubirProductoAbierto(true)
              : undefined
          }
          // Tarjetas sin marco, con el nombre en blanco sobre un degradado encima de la
          // foto en vez de en un pie aparte. En plantillas no es cosmético: con el panel
          // a la izquierda la columna mide ~550px, y ahí tres pies de tarjeta cortaban
          // todos los nombres en "Nuevo produ…".
          nombreSobreLaFoto={modoConDisenoNuevo || plantillaEligeProducto}
          // Tres columnas fijas: tarjetas más chicas, tres productos por fila. En el
          // preview del paso 2 el panel es angosto y baja a dos.
          columnasFijas={
            panelComprimido
              ? 1
              : modoConDisenoNuevo || plantillaEligeProducto
                ? 3
                : undefined
          }
          // Paso 2: el panel se comprime a una preview de lo elegido, con un control para
          // volver a abrirlo sin salir del paso.
          compacto={panelComprimido}
          onAlternarCompacto={
            pasoDosColumnas ? () => setPreviewComprimida((v) => !v) : undefined
          }
          // Ajustes rápidos al pie: lo que casi siempre se toca, sin tener que pasar por
          // la pantalla de configuración. Fondo y Formato abren los pickers que ya
          // existen; Calidad se resuelve en la fila.
          // El CTA se ve desde el principio, deshabilitado hasta que haya un producto
          // elegido: así se lee cuál es el paso siguiente antes de tocar nada.
          footerSiempreVisible={modoConDisenoNuevo}
          // La línea de "elegí uno o varios" y el degradado del pie del scroll.
          pistasDeUso={modoConDisenoNuevo}
          // Catálogo: la invitación a elegir varios ya vive en la bajada del título de
          // la sección, así que la línea de la grilla se queda con el nombre a secas y
          // no repite el mensaje dos veces en la misma columna.
          tituloGrilla={catalogoPasoProductos ? 'Tus productos' : undefined}
          // Sin la línea que separa el CTA de la grilla: acá el pie es sólo el botón y
          // el degradado del scroll ya marca dónde termina la grilla.
          footerSinBorde={catalogoPasoProductos}
          // Y el hint se lee como texto de la pantalla —negro, con aire contra el
          // botón— y no como el pie de un CTA: acá es lo único que explica el modo.
          confirmHintDestacado={catalogoPasoProductos}
          // La tira de preview: lo que ya entró en la tanda, con los huecos punteados al
          // lado diciendo que puede entrar más.
          tiraDeSeleccion={catalogoPasoProductos}
          // Y al elegirlo, el producto se MUDA: sale de la grilla —volando hasta la
          // tira— y de ahí en más se saca tocándolo abajo.
          ocultarElegidas={catalogoPasoProductos}
          // Y las tarjetas elegidas se numeran: lo que se está armando acá es una TANDA
          // —cada producto entra en la cuenta que después multiplica a las vistas—, y
          // tres checks idénticos no lo dicen.
          ordenDeSeleccion={catalogoPasoProductos}
          // Comprimido no lleva cabecera: el breadcrumb y el título de la pantalla ya
          // están en la columna de al lado, y repetirlos en 280px se comía el alto que
          // necesitan las miniaturas.
          cabecera={panelComprimido ? undefined : cabeceraEnPanel}
          // Automático genera desde acá, así que lo elegido tiene que viajar hacia arriba
          // en el momento del tap y no al confirmar (que en ese modo no existe).
          // Lo elegido viaja hacia arriba SIEMPRE, no sólo en los modos que generan
          // desde acá: en el paso 1 del Catálogo lo usa la columna de al lado para saber
          // que la muestra ya tiene que cederle el lugar a la tanda
          // (ADR-2026-09-04-la-tanda-se-muda-a-la-muestra). Que la selección VALGA sin
          // confirmar sigue siendo cosa de `seleccionEnVivo`.
          onSelectionChange={(productos) => {
            setElegidosEnVivo(productos);
            if (seleccionEnVivo) onProductosEnVivo?.(productos);
          }}
          // Dónde se dibuja la tanda una vez que hay algo elegido: el lugar de la muestra.
          destinoTira={catalogoPasoProductos ? destinoTanda : undefined}
          // Paso 1 "Siguiente" (confirma los productos y abre la configuración); paso 2
          // "Generar" con el costo — el mismo botón, en el mismo lugar, avanzando.
          confirmLabel={
            catalogoVistasDosColumnas
              ? // El paso de vistas NO es el último del Catálogo: lleva al review
                  // ("Revisá tu catálogo"), donde se descartan combinaciones antes de
                  // pagar. Un "Generar" acá prometía que el próximo tap ya cobraba
                // (ADR-2026-08-27-catalogo-vuelve-el-review).
                'Revisar catálogo'
              : configureDosColumnas || autoUnSoloPaso || fotosProductoDosColumnas
                ? 'Generar'
                : modoConDisenoNuevo
                  ? 'Siguiente'
                  : undefined
          }
          onConfirmOverride={
            configureDosColumnas || autoUnSoloPaso || fotosProductoDosColumnas
              ? () => onGenerate(creditCost != null && creditCost > 0 ? creditCost : 1)
              : catalogoVistasDosColumnas
                ? // Avanza al REVIEW (`configure` → `CatalogoCardsFullscreen`), que es el
                  // paso 3 del modo: ahí se ve una card por combinación vista × producto y
                  // se descartan las que no van, antes de pagar
                  // (ADR-2026-08-27-catalogo-vuelve-el-review).
                  //
                  // El guard queda por las dudas: el botón ya está apagado sin vistas
                  // (`faltaEnCatalogo`), así que esto no debería poder llamarse en cero.
                  () => {
                    if (cardsDeLaTanda.length === 0) return;
                    onConfirmCatalogoVistas();
                  }
                : undefined
          }
          // Resumen de la tanda arriba del CTA: cuántas imágenes salen y de qué
          // combinación (modelos × poses). A dos columnas el Generar es ESTE
          // botón, no el del ConfigurePanel.
          confirmHint={
            // Catálogo, paso 1: la aritmética del modo va ARRIBA DEL BOTÓN y no como
            // bajada del título. Es donde se decide —"¿toco Siguiente con estos tres?"—
            // y donde el número de la tanda se vuelve la pregunta; arriba de todo se
            // leía al entrar y ya no estaba a la vista al momento de avanzar.
            // Ver ADR-2026-08-26-catalogo-paso-1-grilla-siempre-visible.
            fotosProductoDosColumnas ? (
              // Fotos de producto: lo que falta, o lo que va a salir. Es el mismo número
              // que cobra la pastilla, escrito de manera que se entienda de dónde sale.
              (faltaEnFotosProducto ?? resumenFotosProducto())
            ) : catalogoPasoProductos ? (
              <>
                Elegí <span className="font-semibold">varios productos</span>: de
                cada uno salen{' '}
                <span className="font-semibold">varias imágenes</span>, una por cada
                vista.
              </>
            ) : faltaEnCatalogo
              ? faltaEnCatalogo
              : catalogoVistasDosColumnas
                ? // Listo para generar: lo que va a salir. Es el mismo número de la
                  // pastilla de créditos, escrito de manera que se entienda de dónde sale
                  // (una imagen por cada combinación vista × producto).
                  `${cardsDeLaTanda.length} ${
                    cardsDeLaTanda.length === 1 ? 'imagen' : 'imágenes'
                  } · ${selectedProducts.length} ${
                    selectedProducts.length === 1 ? 'producto' : 'productos'
                  } × ${catalogoVistas.length} ${
                    catalogoVistas.length === 1 ? 'vista' : 'vistas'
                  }`
                : faltaModeloOPose
              ? // El resumen de la tanda no sirve acá: contaría "1 modelo × 1 pose"
                // —trata el 0 como "automático"— justo cuando no hay nada elegido.
                modelosElegidos === 0
                ? `Elegí al menos un ${palabra.singular}.`
                : 'Elegí al menos una pose.'
              : configureDosColumnas && preset.kind === 'design-modelo' && !multiModelEnabled
              ? resumenTandaDesignModelo({
                  modelCount: modelosElegidos,
                  poseCount: poseIds.length,
                  count: designModeloCount.count,
                })
              : // Automático no tiene modelo ni pose que resumir —la escena la decide la
                // IA—, pero sí tiene una combinación que no es obvia: con varios
                // productos elegidos NO sale una imagen por producto, sale UNA con todos
                // juntos. Decirlo acá evita la sorpresa de pagar una imagen esperando
                // tres.
                autoUnSoloPaso
                ? resumenAutomatica(autoImageCount, selectedProducts.length)
                : undefined
          }
          // Paso 2 de "Imagen con modelo": sin modelo o sin pose no hay imagen posible, así
          // que el "Generar" se apaga en vez de disparar una tanda que el backend
          // resolvería por su cuenta y el usuario pagaría igual.
          confirmDisabled={
            faltaModeloOPose ||
            faltaEnCatalogo != null ||
            faltaEnFotosProducto != null
          }
          confirmBadge={
            // El paso de vistas ya NO lleva pastilla de créditos: dejó de ser el paso que
            // cobra (ADR-2026-08-27-catalogo-vuelve-el-review). El costo se muestra en el
            // "Generar" del review, que es donde se paga; acá decía que este tap ya
            // descontaba. Lo que va a salir sigue dicho arriba del botón, en el
            // `confirmHint` ("4 imágenes · 2 productos × 2 vistas").
            (configureDosColumnas || autoUnSoloPaso || fotosProductoDosColumnas) &&
              !faltaModeloOPose &&
              creditCost != null &&
              creditCost > 0 ? (
              // Pastilla BLANCA con el texto en azul, no la translúcida sobre el botón: el
              // costo es el dato que hay que poder leer de una antes de apretar, y a
              // 12.5px sobre azul el blanco al 25% no daba contraste.
              //
              // El ícono de token no hace falta recolorearlo: el asset YA es un círculo
              // azul con la estrella blanca calada, así que sobre blanco lee igual de
              // bien que sobre el botón.
              <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {creditCost}
              </span>
            ) : undefined
          }
          pie={
            // Catálogo, paso 1: SIN ajustes rápidos al pie. Acá no se configura nada —la
            // pantalla pide una sola cosa, qué productos entran en la tanda— y las
            // cuatro celdas volvían a meter la configuración entera abajo, que es
            // justamente lo que este paso saca de encima
            // (ADR-2026-08-26-catalogo-paso-1-grilla-siempre-visible). Calidad, Fondo y
            // Formato siguen viviendo en el paso 2.
            //
            // Fotos de producto va por el mismo camino desde
            // ADR-2026-09-04-los-ajustes-de-fotos-de-producto-bajan-al-paso-2: entró al
            // rediseño con los ajustes al pie del paso 1 y ahí compiten con lo único
            // que ese paso pide. En su paso 2 —la grilla de vistas— el pie aparece con
            // todo: Calidad, Fondo, Formato y la instrucción.
            catalogoPasoProductos || fotosProductoPasoProductos ? undefined : modoConDisenoNuevo ? (
              <AjustesRapidosProducto
                calidad={calidad}
                onCalidadChange={setCalidad}
                onCambiarModeloYPose={onCambiarModeloYPose}
                // Fondo tampoco va en Automático
                // (ADR-2026-08-28-automatico-sin-fondo): el modo se vende como "la IA
                // decide la escena", y una celda que promete elegirla contradice eso
                // —el prompt automático la arma igual—. Quien quiera dirigirla lo dice
                // en la instrucción, que ahora está desplegada justo arriba.
                fondoLabel={
                  preset.kind === 'design-auto'
                    ? undefined
                    : preset.kind === 'fotos-producto'
                      ? // Este modo tiene su propio fondo (liso de 4 colores o escena
                        // descrita), así que el rótulo lo dice con SU vocabulario: el
                        // label genérico habla de biblioteca y automático, que acá no
                        // existen (ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo).
                        etiquetaFondoProducto()
                      : fondoSelectionLabel(fondoSelection)
                }
                onOpenFondo={
                  preset.kind === 'design-auto'
                    ? undefined
                    : preset.kind === 'fotos-producto'
                      ? // Al PANEL LATERAL, no al sheet de fondos: ése es el picker
                        // completo (biblioteca, guardados, subida) y este modo ofrece dos
                        // caminos. Toggle por el mismo motivo que el Formato de abajo.
                        () =>
                          setPanelCatalogo((p) =>
                            p === 'fondo' ? null : 'fondo',
                          )
                      : () =>
                          onOpenFondoSheet(
                            usaBibliotecaDeFondos(preset.kind) ? 'all' : 'list',
                          )
                }
                fondoAbrePanelLateral={preset.kind === 'fotos-producto'}
                formatoLabel={getFormatLabel(formatoVal)}
                // En Automático (desktop) el formato se elige en el panel lateral, igual
                // que en Modelo v2 (ADR-2026-08-28-automatico-formato-en-el-panel). En
                // mobile no hay panel donde abrirlo: sigue el bottom sheet de siempre.
                onOpenFormato={
                  (autoConPanelLateral || preset.kind === 'fotos-producto') &&
                  variant === 'desktop'
                    ? // TOGGLE, no "abrir": la celda es el mismo control para las dos
                      // direcciones. Con `data-abre-panel-lateral` el tap ya no cuenta
                      // como "click afuera", así que sin esto volver a tocarla no hacía
                      // NADA y la única salida era la X o el Escape. Es lo que hace la
                      // celda equivalente de Modelo v2.
                      () =>
                        setPanelCatalogo((p) =>
                          p === 'formato' ? null : 'formato',
                        )
                    : onOpenFormatPicker
                }
                // Marca la celda como disparador del panel: sin esto el `mousedown` de
                // "click afuera" lo cierra y el `click` lo vuelve a abrir, con el
                // parpadeo que eso implica.
                formatoAbrePanelLateral={
                  (autoConPanelLateral || preset.kind === 'fotos-producto') &&
                  variant === 'desktop'
                }
                compacto={panelComprimido}
                // Instrucción libre — SÓLO en automático, que no tiene paso 2 donde
                // ponerla (ADR-2026-08-21-automatico-con-el-diseno-nuevo). En los modos
                // con configuración aparte sigue viviendo allá, con su textarea grande.
                instruccion={
                  // Fotos de producto la suma por el mismo motivo que Automático: su
                  // paso 2 pasó a ser la grilla de vistas, así que ya no hay un panel de
                  // configuración donde ponerla.
                  preset.kind === 'design-auto' ||
                  preset.kind === 'fotos-producto'
                    ? otraConfig
                    : undefined
                }
                onInstruccionChange={
                  preset.kind === 'design-auto' ||
                  preset.kind === 'fotos-producto'
                    ? onOtraChange
                    : undefined
                }
                // El contador va como una celda más de los ajustes, no en el pie: es un
                // control del mismo rango que Calidad o Formato y se lee mejor con la
                // misma etiqueta arriba. Sólo donde EXISTE de verdad: `design-modelo`
                // genera N variaciones del mismo prompt y ya tiene su estado
                // (`autoImageCount`) y su tope. En Catálogo no aparece porque ahí la
                // cantidad no se elige — sale de (vistas × productos), y un contador acá
                // prometería un control que la pantalla siguiente ignora.
                cantidad={
                  // En Automático el contador ya NO es una celda: se mudó al pie, pegado
                  // al Generar (ADR-2026-08-28-automatico-contador-junto-al-generar).
                  preset.kind !== 'design-auto' &&
                  // Fotos de producto tampoco: su cantidad NO se elige, sale de
                  // (vistas × productos) — un contador acá prometería un control que la
                  // tanda ignora, que es el mismo motivo por el que no está en Catálogo.
                  preset.kind !== 'fotos-producto' &&
                  modoConDisenoNuevo &&
                  designModeloCount.enabled ? (
                    <ContadorImagenes
                      valor={autoImageCount}
                      max={autoImageCountMax}
                      onChange={onAutoImageCountChange}
                      disabled={selectedProducts.length === 0}
                    />
                  ) : undefined
                }
              />
            ) : undefined
          }
          // El contador de imágenes, a la IZQUIERDA del Generar y no entre los ajustes
          // (ADR-2026-08-28-automatico-contador-junto-al-generar): es el único control
          // que cambia lo que cuesta la tanda, así que se decide con el precio a la
          // vista. Es el mismo lugar que ya ocupa en el modo Modelo.
          antesDelConfirm={
            autoUnSoloPaso ? (
              <ContadorImagenes
                valor={autoImageCount}
                max={autoImageCountMax}
                onChange={onAutoImageCountChange}
                disabled={selectedProducts.length === 0}
              />
            ) : undefined
          }
          hideBack
          // El video con influencer se genera con UN solo producto (ADR-563).
          singleSelect={singleProductMode}
          title={
            preset.kind === 'video-influencer' ? 'Elegí tu producto' : undefined
          }
        />
      ) : pasoUnoComoModelo ? (
        // CATÁLOGO, FOTOS DE PRODUCTO Y AUTOMÁTICA EN MOBILE: una sola card al pie, la
        // misma del paso 1 del modo Modelo
        // (ADR-2026-09-08-el-paso-1-de-catalogo-en-mobile-copia-al-de-modelo). Reemplaza
        // al par de CTA "Subir nuevo producto" / "Elegir de mis productos": eran dos
        // botones para una sola decisión —de dónde sale el producto—, y ninguno mostraba
        // lo que ya había elegido. La card lo muestra, abre la misma pantalla de
        // productos y sigue aceptando archivos soltados encima.
        //
        // `mt-auto`: la card baja al PIE de la pantalla, debajo de la portada, en vez de
        // quedar colgada arriba. Empujada con margen y no con `justify-end` en el
        // scroller, por lo mismo que en Modelo: si el contenido crece más que la
        // pantalla, `justify-end` deja la primera parte inalcanzable y el margen colapsa
        // solo (ADR-2026-09-04-modelo-mobile-la-card-de-productos-baja-al-pie).
        <div className="mt-auto">
          <CardProductos
            products={selectedProducts}
            onAbrirProductos={onShowProducts}
            onSubirArchivos={onStartUpload}
            // En Automática la acción dice "Añadir producto" con cualquier cantidad, en
            // vez de "Editar outfit" (ADR-2026-09-23-automatico-la-card-dice-anadir-producto):
            // acá no se arma un outfit —la escena la decide la IA—, se le suman
            // productos. Catálogo y Fotos de producto conservan el copy de la card.
            copyElegido={esAutomatica ? 'Añadir producto' : undefined}
            iconoElegido={esAutomatica ? 'agregar' : undefined}
          />
        </div>
      ) : (
        <ChooseProductPanel
          onShowProducts={onShowProducts}
          onStartNewProduct={onStartNewProduct}
          presetKind={preset.kind}
          onSkipProduct={onSkipProduct}
        />
      )
    ) : effectiveStep === 'pick-product' ? (
      <PickProductPanel
        variant={variant}
        onConfirm={onConfirmProducts}
        onBack={onBackToChoose}
        initialSelectedIds={selectedProducts.map((p) => p.id)}
        tiposProductoSugeridos={preset.tiposProductoSugeridos}
        rubro={rubro}
        singleSelect={singleProductMode}
      />
    ) : effectiveStep === 'upload-product' ? (
      <UploadProductPanel
        variant={variant}
        files={pendingProductFiles}
        name={pendingProductName}
        onNameChange={onPendingNameChange}
        onAddMore={onAddMoreFiles}
        onRemoveFile={onRemovePendingFile}
        onCancel={onCancelUpload}
        onConfirm={onConfirmUpload}
        uploading={uploadingProduct}
        error={uploadError}
        rubro={rubro}
        onBulkUpload={onBulkUpload}
      />
    ) : (
      <ConfigurePanel
        variant={variant}
        // Calidad: hasta ahora sólo la dibujaba el pie de DESKTOP
        // (`AjustesRapidosProducto`), así que en mobile el modo Automático no tenía
        // dónde elegirla — se generaba siempre en 1080p sin decirlo
        // (ADR-2026-09-02-automatico-mobile-toma-los-ajustes-del-desktop).
        calidad={calidad}
        onCalidadChange={setCalidad}
        // Las plantillas de imagen la dibujan en el pie, al lado de Formato
        // (ADR-2026-09-28-las-plantillas-eligen-la-calidad). Misma regla que entra a
        // `tieneSelectorResolucion`, así que la celda aparece exactamente donde el pill
        // cobra el multiplicador.
        calidadEnElPie={plantillaEligeCalidad(preset, isVideoModeActive)}
        products={selectedProducts}
        imagenBase={imagenBase}
        onAddProducts={onAddProducts}
        kind={
          // PresetDetailSheet nunca renderiza ConfigurePanel para
          // `asistente` (el padre conmuta a AsistenteMarketingSheet). El
          // modo `video-influencer` REUSA el layout de plantilla-video: se
          // pasa como 'template' y la sección Gancho se inyecta vía
          // `showGancho`. El cast es defensivo.
          // `shorts-studio` tampoco pasa por acá (tiene su propia ruta /shorts-studio);
          // el cast lo mapea a 'template' solo para satisfacer el tipo.
          (preset.kind === 'video-influencer' || preset.kind === 'shorts-studio'
            ? 'template'
            : preset.kind || 'template') as Exclude<
            NonNullable<PresetV2['kind']>,
            | 'asistente'
            | 'prompt-libre'
            | 'creador-video'
            | 'video-influencer'
            | 'shorts-studio'
          >
        }
        showGancho={preset.kind === 'video-influencer'}
        ganchoId={ganchoId}
        sinProducto={sinProducto}
        dialogoLoading={dialogoLoading}
        dialogoError={dialogoError}
        onGanchoIdChange={onGanchoIdChange}
        voiceGuion={voiceGuion}
        onVoiceGuionChange={onVoiceGuionChange}
        onVideoDurationChange={onVideoDurationChange}
        acceptsModelo={
          !preset.inputsAceptados || preset.inputsAceptados.includes('modelo')
        }
        modelSelection={modelSelection}
        modelSelectionsSerie={modelSelectionsSerie}
        onToggleModelSerie={onToggleModelSerie}
        fondoVal={fondoVal}
        formatoVal={formatoVal}
        resolucion={resolucion}
        otraConfig={otraConfig}
        stickyGenerate={variant === 'desktop' || fullScreenConfigureMobile}
        // En desktop sólo design-modelo usa el botón Generar flotante
        // arriba a la derecha — ahí escondemos el sticky del panel.
        // design-auto vuelve al layout estándar (Generar sticky al pie
        // del card de Opciones, como en las plantillas).
        // A dos columnas NO hay Generar en esta columna: ni el flotante de arriba a la
        // derecha (caería sobre el panel de productos) ni el sticky del pie. El único
        // Generar es el CTA del panel de la izquierda, que en `configure` deja de decir
        // "Siguiente" — un solo botón de acción por pantalla.
        hideGenerateButton={variant === 'desktop' && isFullScreenConfigure}
        configureDosColumnas={configureDosColumnas}
        creditCost={creditCost}
        lockSlotCount={lockSlotCount}
        onClearProducts={onClearProducts}
        onRemoveProduct={onRemoveProduct}
        onModelChange={onModelChange}
        modelSelectionsExtra={modelSelectionsExtra}
        onModelChangeExtras={onModelChangeExtras}
        multiModelEnabled={multiModelEnabled}
        onMultiModelEnabledChange={onMultiModelEnabledChange}
        onOpenModelPicker={onOpenModelPicker}
        onOpenModelSheet={onOpenModelSheet}
        onOpenModelSheetForMultiSlot={onOpenModelSheetForMultiSlot}
        multiProductsPerSlot={multiProductsPerSlot}
        onTapSlotProducts={onTapSlotProducts}
        onOpenFondoSheet={onOpenFondoSheet}
        onFondoChange={onFondoChange}
        onFormatoChange={onFormatoChange}
        onResolucionChange={onResolucionChange}
        // En el paso 3 de una plantilla, Formato también se elige en el panel lateral:
        // es la misma pantalla y sería raro que una decisión abra a la derecha y la de al
        // lado tape todo con un sheet centrado.
        onOpenFormatPicker={
          plantillaConfigura
            ? () => setPanelCatalogo('formato')
            : onOpenFormatPicker
        }
        onOtraChange={onOtraChange}
        // Plantillas en el panel del modo: Productos y Modelo se eligen en el panel
        // LATERAL, así que en la columna van como cards
        // (ADR-2026-09-08-la-configuracion-de-plantillas-se-elige-con-cards-y-panel-lateral).
        onAbrirProductosLateral={
          plantillaConfigura ? () => setPanelCatalogo('productos') : undefined
        }
        onAbrirModeloLateral={
          plantillaConfigura ? () => setPanelCatalogo('modelo') : undefined
        }
        // La plantilla elegida, como card arriba de Producto: sin la grilla delante
        // (se entra directo a configurar), es desde acá que se cambia
        // (ADR-2026-09-08-la-plantilla-se-elige-desde-el-panel).
        plantillaActual={
          plantillaConfigura
            ? { titulo: preset.title, imagen: preset.imageUrl }
            : undefined
        }
        onAbrirPlantillaLateral={
          plantillaConfigura ? () => setPanelCatalogo('plantilla') : undefined
        }
        // Imagen/Video, DENTRO del panel y no suelto sobre la plantilla
        // (ADR-2026-09-08-el-switch-de-imagen-o-video-va-al-panel).
        switchMedia={plantillaConfigura ? toggleImagenVideo(true) : undefined}
        onGenerate={onGenerate}
        onGenerateProduction={onGenerateProduction}
        productionCreditCost={productionCreditCost}
        autoImageCount={autoImageCount}
        onAutoImageCountChange={onAutoImageCountChange}
        autoImageCountMax={autoImageCountMax}
        userModels={userModels}
        userAuthId={userAuthId}
        userCompanyId={userCompanyId}
        onModelCreated={onModelCreated}
        allPoses={allPoses}
        customPoses={customPoses}
        poseIds={poseIds}
        onPoseIdsChange={onPoseIdsChange}
        vistasProducto={vistasProducto}
        vistaIds={vistaIds}
        vistaProductosExcluidos={vistaProductosExcluidos}
        onToggleVistaProducto={onToggleVistaProducto}
        fotosProductoTotalImagenes={fotosProductoTotalImagenes}
        onVistaIdsChange={onVistaIdsChange}
        multiPoseEnabled={multiPoseEnabled}
        onOpenPosePicker={onOpenPosePicker}
        onSubirPose={onSubirPose}
        bibliotecaFondos={bibliotecaFondos}
        fondosGuardados={fondosGuardados}
        fondoSelection={fondoSelection}
        onFondoSelectionChange={onFondoSelectionChange}
        onOpenFondoPicker={onOpenFondoPicker}
        fondoView={fondoView}
        onFondoViewChange={onFondoViewChange}
        onFondoSaved={onFondoSaved}
        videoModeEnabled={isVideoModeActive}
        videoDuration={videoDuration}
        videoMultiScene={videoMultiScene}
        videoCreditCostPerImage={videoCreditCostPerImage}
        onOpenVideoConfig={onOpenVideoConfig}
        anuncioTitulo={anuncioTitulo}
        onAnuncioTituloChange={onAnuncioTituloChange}
        anuncioDescripcion={anuncioDescripcion}
        onAnuncioDescripcionChange={onAnuncioDescripcionChange}
        anuncioCta={anuncioCta}
        onAnuncioCtaChange={onAnuncioCtaChange}
        anuncioColors={anuncioColors}
        onAnuncioColorsChange={onAnuncioColorsChange}
        anuncioBrandColors={anuncioBrandColors}
      />
    );
  };

  // El mobile preset sheet se renderiza INLINE (sin portal) — choose-
  // product / configure se quedan debajo de la top bar de la página
  // como cualquier bottom sheet inline. SOLO el pick-product es full-
  // screen sobre la top bar (ese se rendiriza por separado más abajo
  // vía createPortal).
  return (
    <>
      {/* Vista picker fullscreen para el step `select-vistas` del modo
          catalogo-ia. Se monta sobre todo lo demás cuando aplica.
          Renderizamos 2 instancias responsivas — la variant define el
          layout (mobile = full inset, desktop = left: sidebarWidth). */}
      <div className="md:hidden">
        <VistaPickerFullscreen
          open={step === 'select-vistas'}
          variant="mobile"
          sidebarWidth={sidebarWidth}
          allPoses={allPoses}
          customPoses={customPoses}
          onUploadCustomPose={onUploadCustomPose}
          uploadingCustomPose={uploadingCustomPose}
          onDeleteCustomPose={onDeleteCustomPose}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onChange={onCatalogoVistasChange}
          onBack={onBackToChoose}
          onNext={onConfirmCatalogoVistas}
          variantVistas={
            preset.kind === 'catalogo-muebles' ? 'muebles' : 'catalogo'
          }
        />
      </div>
      <div className="hidden md:block">
        <VistaPickerFullscreen
          // En `catalogo-ia` este paso dejó de ser un overlay: se dibuja embebido en la
          // columna, al lado del panel de productos. El overlay sigue vivo para muebles
          // (mismo step, otro catálogo de vistas) y para mobile, que no tiene dos
          // columnas donde ponerlo.
          open={step === 'select-vistas' && !catalogoVistasDosColumnas}
          variant="desktop"
          sidebarWidth={sidebarWidth}
          allPoses={allPoses}
          customPoses={customPoses}
          onUploadCustomPose={onUploadCustomPose}
          uploadingCustomPose={uploadingCustomPose}
          onDeleteCustomPose={onDeleteCustomPose}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onChange={onCatalogoVistasChange}
          onBack={onBackToChoose}
          onNext={onConfirmCatalogoVistas}
          variantVistas={
            preset.kind === 'catalogo-muebles' ? 'muebles' : 'catalogo'
          }
        />
      </div>

      {/* Cards review fullscreen — paso final del flow catalogo-ia,
          reemplaza al ConfigurePanel cuando el preset es catalogo-ia
          y el step es 'configure'. Una instancia por breakpoint, igual
          que el VistaPicker. */}
      <div className="md:hidden">
        <CatalogoCardsFullscreen
          open={
            step === 'configure' &&
            (preset.kind === 'catalogo-ia' ||
              preset.kind === 'catalogo-muebles' ||
              preset.kind === 'infografia')
          }
          variant="mobile"
          sidebarWidth={sidebarWidth}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          allPoses={allPoses}
          customPoses={customPoses}
          onBack={onBackFromCatalogoConfigure}
          onGenerate={onGenerateCatalog}
          userCompanyId={userCompanyId}
          brandId={brandId}
          rubro={rubro}
          userPlan={userPlan}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          onProductsRefreshed={onProductsRefreshed}
          fondoSelection={fondoSelection}
          modelSelection={modelSelection}
          modoMuebles={
            preset.kind === 'catalogo-muebles' || preset.kind === 'infografia'
          }
          soloInfografia={preset.kind === 'infografia'}
          // El Catálogo IA abre la BIBLIOTECA (opciones por categoría:
          // Automático · Añadir fondo · Fondo blanco · Mis fondos · Ciudad…),
          // igual que el picker de Imagen personalizada. Sin esto, en desktop
          // caía en la grilla plana con todos los fondos sueltos ('list'); en
          // mobile ya aterrizaba acá porque el sheet convierte 'list' → 'all'.
          onOpenFondo={() =>
            onOpenFondoSheet(
              usaBibliotecaDeFondos(preset.kind) ? 'all' : 'list',
            )
          }
          onOpenModel={() => onOpenModelSheet('list')}
          // Calidad: en mobile ESTA pantalla es la única del paso 3 —el panel del sheet
          // queda debajo del overlay—, así que el ajuste vive acá o no vive en ningún
          // lado (ADR-2026-09-02-calidad-y-formato-en-el-review-de-mobile). Formato no
          // va: el Catálogo no lo usa (ADR-2026-09-02-el-catalogo-no-elige-formato).
          calidad={calidad}
          onCalidadChange={setCalidad}
        />
      </div>
      <div className="hidden md:block">
        <CatalogoCardsFullscreen
          // En `catalogo-ia` el review dejó de ser un overlay en desktop: se dibuja
          // embebido en la columna, al lado del panel. El overlay sigue vivo para
          // muebles e infografía (mismo step, otra pantalla) y para mobile, que no tiene
          // dos columnas donde ponerlo.
          open={
            step === 'configure' &&
            !catalogoReviewDosColumnas &&
            (preset.kind === 'catalogo-ia' ||
              preset.kind === 'catalogo-muebles' ||
              preset.kind === 'infografia')
          }
          variant="desktop"
          sidebarWidth={sidebarWidth}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          allPoses={allPoses}
          customPoses={customPoses}
          onBack={onBackFromCatalogoConfigure}
          onGenerate={onGenerateCatalog}
          userCompanyId={userCompanyId}
          brandId={brandId}
          rubro={rubro}
          userPlan={userPlan}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          onProductsRefreshed={onProductsRefreshed}
          fondoSelection={fondoSelection}
          modelSelection={modelSelection}
          modoMuebles={
            preset.kind === 'catalogo-muebles' || preset.kind === 'infografia'
          }
          soloInfografia={preset.kind === 'infografia'}
          // El Catálogo IA abre la BIBLIOTECA (opciones por categoría:
          // Automático · Añadir fondo · Fondo blanco · Mis fondos · Ciudad…),
          // igual que el picker de Imagen personalizada. Sin esto, en desktop
          // caía en la grilla plana con todos los fondos sueltos ('list'); en
          // mobile ya aterrizaba acá porque el sheet convierte 'list' → 'all'.
          onOpenFondo={() =>
            onOpenFondoSheet(
              usaBibliotecaDeFondos(preset.kind) ? 'all' : 'list',
            )
          }
          onOpenModel={() => onOpenModelSheet('list')}
        />
      </div>

      {/* Drawer de alta de producto — se monta sobre la pantalla del modo y vuelve al
          mismo lugar al cerrarse. Sólo existe en el layout apilado del Catálogo, que es
          donde la grilla ofrece la tarjeta que lo abre. */}
      <SubirProductoDrawer
        open={subirProductoAbierto}
        onClose={() => setSubirProductoAbierto(false)}
        userCompanyId={userCompanyId}
        brandId={brandId}
        rubro={rubro}
        userPlan={userPlan}
        onProductsRefreshed={onProductsRefreshed}
      />
      <div
        className={`md:hidden fixed inset-0 z-[100] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-[101] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out rounded-none ${
          animated ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          // El sheet siempre llega hasta arriba (100dvh) para que el
          // header transparente flote encima sin franja del home
          // detrás. Los distintos steps acomodan su contenido (imagen
          // + panel) usando flex / max-height adentro. NO aplicamos
          // padding-bottom acá — en PWA standalone con viewport-fit=
          // cover queremos que la imagen full-bleed llegue hasta el
          // borde físico. El CTA flotante "Generar con mi producto"
          // ya maneja su propio `env(safe-area-inset-bottom)` para no
          // quedar debajo del home indicator.
          height: '100dvh',
        }}
      >

        {renderedStep === 'preview' ? (
          // ── Preview: scroll vertical con cards una por viewport ──────
          // Cada card es `min-h-full` (toma toda la altura del sheet) y
          // tiene `snap-start`. El onScroll mantiene `previewIndex`
          // sincronizado con la card visible → el footer fijo (afuera
          // del scroll) usa siempre el preset correcto al tapear
          // "Generar con mi producto".
          //
          // En multi-mode, mostramos un strip sticky abajo con las
          // plantillas seleccionadas (fuera del scroll, sibling del
          // mismo contenedor flex-col).
          <div className="flex-1 min-h-0 flex flex-col">
          <div
            ref={mobilePreviewScrollRef}
            className="flex-1 overflow-y-auto snap-y snap-mandatory"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.clientHeight <= 0) return;
              // El hint se apaga con el PRIMER movimiento real, sin esperar al snap:
              // el pedido es que desaparezca al deslizar, y esperar a que la card
              // siguiente encaje lo dejaría puesto durante todo el arrastre.
              //
              // El umbral de 12px es contra el rebote elástico de iOS y el redondeo
              // sub-pixel del scroll, que disparan `onScroll` sin que nadie haya
              // deslizado. Se mide contra `scrollProgramaticoRef` y no contra 0 — ver
              // su declaración.
              if (
                mostrarHintDeslizar &&
                Math.abs(el.scrollTop - scrollProgramaticoRef.current) > 12
              ) {
                setMostrarHintDeslizar(false);
              }
              const idx = Math.round(el.scrollTop / el.clientHeight);
              if (idx !== previewIndex && idx >= 0 && idx < previewList.length) {
                setPreviewIndex(idx);
              }
            }}
          >
            {previewList.map((p) => (
              <div
                key={p.id}
                // Cada card ocupa todo el viewport del sheet. La imagen
                // hace full-bleed (object-cover); el footer/check fijos
                // viven afuera del scroll para no moverse al scrollear.
                className="snap-start min-h-full relative overflow-hidden"
              >
                {/* Imagen full-bleed. El button entero detecta tap solo en
                    multi-mode (sino el scroll es la única interacción). */}
                <button
                  type="button"
                  // Solo togglea cards multi-selectable — una pose
                  // multi-persona o una vista solo-producto no se pueden
                  // sumar al set (el payload multi no las soporta).
                  onClick={() =>
                    multiMode &&
                    isPresetMultiSelectable(p) &&
                    toggleMultiId(p.id)
                  }
                  disabled={!multiMode}
                  aria-label={
                    multiMode
                      ? isMultiSelected(p.id)
                        ? `Quitar ${p.title}`
                        : `Seleccionar ${p.title}`
                      : undefined
                  }
                  className="absolute inset-0 disabled:cursor-default"
                >
                  {p.outputType === 'video' ? (
                    <video
                      src={p.imageUrlFull || p.imageUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster={p.imageUrl}
                      className="absolute inset-0 w-full h-full object-cover bg-neutral-100"
                    />
                  ) : (
                    <img
                      src={p.imageUrlFull || p.imageUrl}
                      alt={p.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover bg-neutral-100"
                    />
                  )}
                </button>
              </div>
            ))}
          </div>
          {/* ───── Overlays fijos (no se mueven con el scroll) ───── */}
          {/* Gradient bottom para legibilidad del footer. */}
          <div
            className="absolute bottom-0 inset-x-0 h-44 bg-gradient-to-t from-black/65 via-black/25 to-transparent pointer-events-none z-10"
          />
          {/* Footer fijo. Comportamiento del botón:
              • single mode → "Generar con mi producto" (acción directa).
              • multi-mode + card visible NO seleccionada → "Seleccionar"
                (tap añade la card al set).
              • multi-mode + card visible seleccionada → "Generar (N)"
                (tap dispara handleConfirmMulti) + un pill "Seleccionada"
                arriba del botón como indicador visual.
              Se desliza ~88px arriba cuando el multi-strip aparece. */}
          {(() => {
            const currentPreset = previewList[previewIndex];
            const currentSelected =
              !!currentPreset && isMultiSelected(currentPreset.id);
            return (
              <div
                className="absolute inset-x-5 z-20 transition-[bottom] duration-300 ease-out"
                style={{
                  bottom:
                    multiMode && multiSelectedPresets.length > 0
                      ? 'calc(env(safe-area-inset-bottom, 0px) + 112px)'
                      : 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                }}
              >
                {/* "Deslizá para explorar" — ADR-2026-08-20-la-vista-ampliada-avisa-que-se-desliza.
                    Sólo con algo a lo que deslizar: con una sola plantilla el cartel
                    prometería un gesto que no hace nada.

                    Va ADENTRO del contenedor del CTA, y no como un `absolute` propio con
                    su propio `bottom`: ese contenedor ya calcula su separación del piso
                    —incluido el safe area y los +88px de cuando aparece el strip
                    multi-select— y la anima. Con un `bottom` calculado aparte habría que
                    repetir esa cuenta y mantenerla en dos lugares; acá el hint queda
                    apoyado sobre el botón y se mueve con él, gratis.

                    `pointer-events-none` porque está por encima de la foto, que en
                    multi-mode es tappeable. */}
                {previewList.length > 1 && (
                  <div
                    // El montaje lo decide SÓLO la cantidad de plantillas; el estado
                    // maneja la opacidad. Si el `mostrarHintDeslizar` también gateara el
                    // montaje, el elemento se desmontaría en el mismo frame en que se
                    // apaga y la transición no llegaría a correr nunca: sería un corte
                    // seco con una clase de fade al lado que no hace nada.
                    aria-hidden={!mostrarHintDeslizar}
                    // `mb-7` (28px) y no `mb-3`: el hint queda separado del CTA en vez de
                    // apoyado sobre él. Pegado se leía como una etiqueta del botón —algo
                    // sobre lo que se está por tocar— cuando habla de la FOTO y del gesto
                    // de deslizarla. El aire es lo que lo despega de esa lectura.
                    //
                    // Sigue siendo un margen y no un `bottom` propio: el contenedor del
                    // CTA es el que sabe dónde está el piso (safe area, +88px con el strip
                    // multi-select) y lo anima; el hint se sube respecto de él.
                    className={`pointer-events-none mb-7 flex flex-col items-center gap-0.5 transition-opacity duration-300 ${
                      mostrarHintDeslizar ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <span className="text-[13px] font-semibold leading-none text-white [text-shadow:0_1px_10px_rgba(0,0,0,0.55)]">
                      Deslizá para explorar
                    </span>
                    {/* Sólo los chevrones se mueven; el texto queda quieto. Un cartel
                        entero rebotando al lado del CTA compite con él — lo que tiene que
                        sugerir el gesto es la flecha.

                        Dos chevrones superpuestos con `-mt-2`: es el doble-chevron del
                        diseño, hecho con el ícono que ya está en el bundle en vez de
                        sumar un SVG. */}
                    <span
                      aria-hidden="true"
                      // La animación se apaga junto con el hint: el elemento queda montado
                      // para poder desvanecerse, y un keyframe corriendo para siempre
                      // sobre algo invisible es trabajo de composición regalado.
                      className={`flex flex-col items-center text-white [filter:drop-shadow(0_1px_8px_rgba(0,0,0,0.55))] ${
                        mostrarHintDeslizar ? 'animate-deslizar-hint' : ''
                      }`}
                    >
                      <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
                      <ChevronDown className="-mt-2 h-4 w-4" strokeWidth={2.5} />
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2.5">
                  {isPresetMultiSelectable(currentPreset) && !multiMode && currentPreset && (
                    <button
                      type="button"
                      onClick={() => enterMultiMode(currentPreset.id)}
                      aria-label="Seleccionar varias"
                      className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/95 backdrop-blur-sm border border-white/40 text-brand-black/85 shadow-sm transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (!currentPreset) return;
                      if (!multiMode) {
                        onUtilizarPreset(currentPreset);
                        return;
                      }
                      // Multi-mode: la card visible decide la acción.
                      // Sin seleccionar → añadirla. Ya seleccionada →
                      // confirmar generación con todo el set.
                      // Si la card actual NO es multi-selectable (e.g.
                      // pose multi-persona) y aún no está en el set,
                      // no la sumamos: si ya hay seleccionadas confirma,
                      // sino utiliza ese preset como single.
                      const canAddCurrent =
                        isPresetMultiSelectable(currentPreset);
                      if (!canAddCurrent && !currentSelected) {
                        if (multiSelectedPresets.length > 0) {
                          handleConfirmMulti();
                        } else {
                          onUtilizarPreset(currentPreset);
                        }
                        return;
                      }
                      if (currentSelected) {
                        handleConfirmMulti();
                      } else {
                        toggleMultiId(currentPreset.id);
                      }
                    }}
                    disabled={multiMode && !currentPreset}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-md transition-colors"
                  >
                    {/* Sparkles para "Generar"; Plus para "Seleccionar"
                        — el ícono refleja la acción (añadir vs generar). */}
                    {multiMode &&
                    !currentSelected &&
                    isPresetMultiSelectable(currentPreset) ? (
                      <Plus className="w-5 h-5" strokeWidth={2.5} />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                    <span>
                      {!multiMode
                        ? 'Generar con mi producto'
                        : currentSelected
                          ? 'Generar'
                          : isPresetMultiSelectable(currentPreset)
                            ? 'Seleccionar'
                            : multiSelectedPresets.length > 0
                              ? 'Generar'
                              : 'Generar con mi producto'}
                    </span>
                    {/* Badge circular blanco con el conteo — solo cuando
                        el label efectivo es "Generar" en multi-mode con
                        N items. */}
                    {multiMode &&
                      multiSelectedIds.length > 0 &&
                      (currentSelected ||
                        !isPresetMultiSelectable(currentPreset)) && (
                        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-white text-brand-primary text-[13px] font-bold leading-none tabular-nums">
                          {multiSelectedIds.length}
                        </span>
                      )}
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Strip flotante con las seleccionadas — overlay absolute para
              no empujar la UI ni crear scroll extra. Aparece solo cuando
              hay al menos una seleccionada, con un fade+slide-up sutil
              (animate-mode-swap, 200ms) al primer monte. */}
          {multiMode && multiSelectedPresets.length > 0 && (
            <div
              className="absolute left-3 right-3 z-30 pointer-events-none animate-mode-swap"
              style={{
                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="pointer-events-auto">
                <MultiSelectedStrip
                  selected={multiSelectedPresets}
                  onRemove={toggleMultiId}
                  onCancel={exitMultiMode}
                />
              </div>
            </div>
          )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Imagen del preset arriba — en 'design-modelo' se oculta
                completamente al entrar a 'configure' (el modo tiene
                muchas opciones y la imagen no aporta). En design-modelo
                non-configure usamos fondo blanco + object-contain con
                padding (la portada del modo se ve más prolija así, no
                full-bleed como en templates de moda — el `contain` es
                además lo que deja entrar entera la portada 3:2 de
                ADR-2026-08-04-modelo-portada-unica). */}
            <div
              className={`transition-all duration-300 ease-out overflow-hidden ${
                fullScreenConfigureMobile && step === 'configure'
                  ? 'h-0 opacity-0'
                  : smallCenteredPreview
                    ? // Catálogo con el encabezado arriba: menos padding lateral, para que
                      // la ilustración —que es lo que explica el modo— gane esos 16px de
                      // cada lado.
                      `bg-white flex-1 min-h-0 flex flex-col items-center justify-center ${
                        pasoUnoComoModelo ? 'px-4 py-6' : 'p-6'
                      } opacity-100`
                    : 'relative bg-neutral-100 flex-1 min-h-0 opacity-100'
              }`}
            >
              {smallCenteredPreview ? (
                heroCollage ? (
                  <ProductHeroCollage
                    original={heroCollage.original}
                    results={heroCollage.results}
                    layout={heroCollage.layout}
                    className="max-w-[320px]"
                  />
                ) : (
                  <>
                    {/* El texto ENCABEZA la muestra, igual que en desktop
                        (ADR-2026-09-02-catalogo-mobile-toma-el-encabezado-de-desktop).
                        Es el título de la pantalla —qué hace el modo— y reemplaza al
                        bloque que decía lo mismo abajo, dentro del panel.
                        El copy sale de `encabezadoMuestra`, el mismo que usa desktop: uno
                        por modo, escrito en un solo lugar. */}
                    {pasoUnoComoModelo && encabezadoMuestra && (
                      <div className="shrink-0 text-center max-w-[340px] mb-8">
                        <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                          {encabezadoMuestra.titulo}
                        </h2>
                        <p className="text-[14px] text-[#3C3C43]/60 leading-snug mt-1">
                          {encabezadoMuestra.bajada}
                        </p>
                      </div>
                    )}
                    <img
                      src={preset.imageUrlFull || preset.imageUrl}
                      alt={preset.title}
                      // `max-h-full` mantiene el contain de design-modelo/auto
                      // (landscape). Para catalogo-ia (portrait) el max-w
                      // limita el ancho — y como hay `flex items-center`,
                      // queda visualmente similar al resto.
                      //
                      // En Catálogo la ilustración es LO que explica el modo (dos
                      // productos → sus vistas → el catálogo), y a 300px las vistas de
                      // abajo quedaban del tamaño de una estampilla: sube a 360 y se
                      // queda con el alto que le libera el encabezado que se fue del
                      // panel.
                      className={`${
                        pasoUnoComoModelo ? 'max-w-[400px]' : 'max-w-[300px]'
                      } max-h-full w-full h-auto object-contain`}
                    />
                  </>
                )
              ) : isMultiPreset ? (
                <MultiPresetCarousel presets={multiPresets} fit="cover" />
              ) : preset.outputType === 'video' ? (
                <video
                  src={preset.imageUrlFull || preset.imageUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster={preset.imageUrl}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <img
                  src={preset.imageUrlFull || preset.imageUrl}
                  alt={preset.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
            </div>

            {/* La altura del panel adentro del preset sheet cambia según
                el step:
                • choose-product               → 55dvh (panel chico)
                • upload-product · configure   → 78dvh (más espacio para
                                                   previews + form / opts)
                • design-modelo                → ocupa todo el sheet
                pick-product NO afecta este panel — ese caso se maneja con
                un sheet aparte (portal) sobre la top bar. */}
            <div
              // El panel arranca con max-height: 0 (panelOpen=false en
              // el primer render post-mount) y en el siguiente frame
              // se anima a su valor final. Esto cambia el flex-space
              // disponible para la imagen (flex-1 arriba) → la imagen
              // anima su altura sin script extra. design-modelo +
              // configure usa flex-1 sin max-height (panel ocupa
              // todo).
              className={`relative z-10 bg-white flex flex-col overflow-hidden transition-all duration-300 ease-out ${
                fullScreenConfigureMobile && step === 'configure'
                  ? 'flex-1 min-h-0'
                  : 'shrink-0 -mt-6 rounded-t-3xl'
              }`}
              style={{
                maxHeight:
                  fullScreenConfigureMobile && step === 'configure'
                    ? undefined
                    : !panelOpen
                      ? '0px'
                      : step === 'configure' || step === 'upload-product'
                        ? '78dvh'
                        : '55dvh',
                transition: 'max-height 300ms ease-out',
              }}
            >
              <div
                key={step}
                // En design-modelo + configure el panel ocupa todo el
                // sheet (sin imagen del preset arriba), así que el
                // contenido arranca pegado al borde superior y los
                // botones flotantes (back / menu, del header
                // transparente) tapan el primer chip. Agregamos
                // padding superior + safe-area-top en ese caso para
                // que el primer elemento quede debajo de los botones.
                //
                // pb-6 default — pero en design-flow + configure lo
                // sacamos porque el ConfigurePanel renderiza un Generar
                // sticky con su propio padding interno; cualquier pb
                // del wrapper deja un gap visible entre el botón y el
                // borde inferior.
                className={`${swapAnimClass} flex-1 overflow-y-auto px-5 ${
                  fullScreenConfigureMobile && step === 'configure'
                    ? ''
                    : step === 'choose-product' && !pasoUnoComoModelo
                      ? '' // el pb lo pone el style: tiene que sumar la barra
                      : // Columna flex para que el `mt-auto` del grupo de abajo —la card
                        // de productos y, en Automática, la instrucción— tenga contra
                        // qué empujar y baje al pie del scroller, igual que en Modelo
                        // (ADR-2026-09-04-modelo-mobile-la-card-de-productos-baja-al-pie).
                        `pb-6${pasoUnoComoModelo ? ' flex flex-col' : ''}`
                }`}
                style={{
                  paddingTop:
                    fullScreenConfigureMobile && step === 'configure'
                      ? 'calc(env(safe-area-inset-top, 0px) + 5rem)'
                      : '1.75rem', // pt-7 default
                  // En el primer paso del modo la barra inferior sigue a la vista
                  // (ADR-2026-08-04-el-modo-se-lleva-la-barra) y se dibuja ENCIMA de
                  // este panel: el shell la monta afuera del contenido, con un z que
                  // le gana. Sin este hueco, "Elegí de mis productos" —el segundo
                  // CTA— queda medio tapado. Son los 24px de `pb-6` más lo que ocupa
                  // la barra.
                  //
                  // Salvo en los modos con el paso 1 de Modelo: ahí la barra ya no está
                  // (ADR-2026-09-08-el-paso-1-de-catalogo-en-mobile-copia-al-de-modelo),
                  // y reservarle el hueco dejaba la card colgada 64px arriba del borde,
                  // sobre un vacío que no tapa nada.
                  paddingBottom:
                    step === 'choose-product' && !pasoUnoComoModelo
                      ? `calc(1.5rem + ${ALTO_NAV_INFERIOR})`
                      : undefined,
                }}
              >
                {renderStep('mobile')}
              </div>
              {/* AUTOMÁTICA EN MOBILE, paso 1: el "Siguiente" que lleva a la pantalla de
                  las opciones (ADR-2026-09-23-automatico-en-mobile-tiene-su-generar-2).
                  Es la misma `BarraInferiorMobile` del paso 1 de Modelo, con las mismas
                  reglas: sólo el botón (`soloCta`, los ajustes aparecen en el paso 2),
                  sin costo (acá no se genera) y sin dibujarse hasta el primer producto
                  (ADR-2026-09-04-en-mobile-el-siguiente-aparece-con-el-primer-producto).
                  Va EN EL FLUJO, hermana del scroller y adentro del panel, para que el
                  `safe-area` lo maneje esta caja y no el contenido.

                  Sólo `autoUnSoloPaso`: Catálogo y Fotos de producto avanzan al confirmar
                  en el selector, y un "Siguiente" acá les abriría una pantalla que no es
                  la suya. */}
              {autoUnSoloPaso && selectedProducts.length > 0 && (
                <BarraInferiorMobile
                  calidad={calidad}
                  onCalidadChange={setCalidad}
                  formatoLabel={getFormatLabel(formatoVal)}
                  onOpenFormato={onOpenFormatPicker}
                  cantidad={autoImageCount}
                  cantidadMax={autoImageCountMax}
                  onCantidadChange={onAutoImageCountChange}
                  imagenesDeLaTanda={autoImageCount}
                  creditCost={creditCost}
                  ctaLabel="Siguiente"
                  ocultarCosto
                  soloCta
                  onGenerate={() => setAutoPaso2Mobile(true)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Mobile-only: pick-product como PANTALLA completa ── */}
      {/* Cuando step === 'pick-product' en mobile, renderizamos una pantalla
          full-screen (no un bottom sheet) a través de portal a document.body.
          Va POR DEBAJO del chrome del Sidebar (z-50) — z-[35] queda encima
          del contenido de la página (shell z-30) pero deja visibles y
          clickeables los botones de navegación flotantes (volver + menú). Por
          eso el contenido arranca debajo de la top bar (padding-top =
          safe-area + 3.5rem, la altura del header mobile) y ocultamos el link
          "Volver" interno del panel (`hideBack`): el back lo maneja la top bar
          vía `mobileBackOverride`. Usamos la variante `desktop` para tener el
          mismo layout que en escritorio: header + grilla + botón grande de
          Confirmar al pie. La animación `page-slide-in-right` le da el feel de
          "navegar a otra pantalla". */}
      {step === 'pick-product' &&
        ReactDOM.createPortal(
          <div
            className="md:hidden fixed inset-0 z-[35] bg-white flex flex-col page-slide-in-right"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
              // En Automática el safe-area de abajo lo pone `BarraInferiorMobile`, que
              // es lo último de la pantalla: contado también acá se sumaba dos veces
              // (ADR-2026-09-10-el-safe-area-del-pie-se-cuenta-una-sola-vez).
              paddingBottom: esAutomatica
                ? undefined
                : 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {/* El panel arma su layout sobre un `h-full`: sin un contenedor acotado se
                lleva TODO el alto del portal y deja a la barra del pie fuera de la
                pantalla. El mismo `[&>div]:` que usa Modelo para lo mismo. */}
            <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
            <PickProductPanel
              variant="desktop"
              hideBack
              // En mobile el alto es poco: el header "Elegí los productos" va
              // dentro del scroll para que se vaya con las cards y quede solo
              // el buscador pegado arriba.
              scrollHeader
              onConfirm={onConfirmProducts}
              onBack={onBackToChoose}
              initialSelectedIds={selectedProducts.map((p) => p.id)}
              tiposProductoSugeridos={preset.tiposProductoSugeridos}
              rubro={rubro}
              singleSelect={singleProductMode}
              title={
                preset.kind === 'video-influencer'
                  ? 'Elegí tu producto'
                  : undefined
              }
              // Los modos del rediseño usan la MISMA grilla que el modo Modelo —
              // ADR-2026-09-08-la-grilla-de-productos-de-mobile-es-la-misma-en-todos-los-modos.
              // De a dos por fila, tarjeta sin marco con el nombre en blanco sobre la
              // foto —el pie de tarjeta cortaba todos los nombres en "Nuevo produc…"— y
              // "Subir nuevo producto" como primera celda de la grilla, que es de donde
              // sale un producto nuevo sin irse de la pantalla.
              {...(modoConDisenoNuevo
                ? {
                    columnasFijas: COLUMNAS_PRODUCTOS_MOBILE,
                    nombreSobreLaFoto: true,
                    ocultarBloqueSubida: true,
                    onSubirProducto: () => setSubirProductoAbierto(true),
                    // Qué pasa con VARIOS elegidos, dicho por modo. El default
                    // ("Podés elegir uno o varios") avisa que se puede, pero no qué sale
                    // de ahí, y no es lo mismo en los tres: elegir tres productos puede
                    // significar tres catálogos, tres juegos de vistas o UNA foto con los
                    // tres. En desktop esa aclaración ya existe en la fila del recuento
                    // (`tituloSinSeleccion`), que en mobile no se dibuja.
                    description: esCatalogoIA(preset.kind)
                      ? 'Podés elegir varios: Atlas arma el catálogo de cada uno.'
                      : preset.kind === 'fotos-producto'
                        ? 'Podés elegir varios: Atlas genera las vistas de cada uno.'
                        : 'Podés elegir varios: salen juntos en la misma imagen.',
                  }
                : {})}
              // Automática: el "Confirmar" propio del panel se va y lo reemplaza la
              // barra de abajo, como en Modelo
              // (ADR-2026-08-24-modelo-v2-mobile-barra-en-las-dos-pantallas). La
              // selección viaja en vivo —`onProductosEnVivo` deja los productos arriba
              // SIN mover el paso—, que es lo que ese `hideFooter` pide a cambio. El
              // confirm del shell (`onConfirmProducts`) no sirve acá: devuelve a
              // `choose-product` y desmonta el selector en el mismo tap, y la segunda
              // pantalla tiene que entrar ENCIMA de él (ver `salirDelPickerAutoRef`).
              {...(esAutomatica
                ? {
                    hideFooter: true,
                    onSelectionChange: onProductosEnVivo ?? onConfirmProducts,
                  }
                : {})}
            />
            </div>
            {esAutomatica && (
              <BarraInferiorMobile
                calidad={calidad}
                onCalidadChange={setCalidad}
                formatoLabel={getFormatLabel(formatoVal)}
                onOpenFormato={onOpenFormatPicker}
                cantidad={autoImageCount}
                cantidadMax={autoImageCountMax}
                onCantidadChange={onAutoImageCountChange}
                imagenesDeLaTanda={autoImageCount}
                creditCost={creditCost}
                // "Confirmar", no "Siguiente": esta pantalla es el SELECTOR y el botón
                // cierra esa elección. Y no genera: abre la pantalla de las opciones,
                // que es lo que falta decidir después del producto.
                ctaLabel="Confirmar"
                ocultarCosto
                soloCta
                onGenerate={() => {
                  // El orden importa: primero se abre la segunda pantalla y recién
                  // cuando terminó de entrar se cierra este selector.
                  salirDelPickerAutoRef.current = onBackToChoose;
                  setAutoPaso2Mobile(true);
                  // Red por si la animación no dispara `animationend`
                  // (`prefers-reduced-motion`, pestaña en segundo plano).
                  window.setTimeout(cerrarPickerAutoPendiente, 400);
                }}
                falta={
                  selectedProducts.length === 0
                    ? 'Elegí al menos un producto'
                    : undefined
                }
              />
            )}
          </div>,
          document.body,
        )}

      {/* ── Mobile-only: la SEGUNDA pantalla de Automática — las opciones ── */}
      {/* Instrucción, Calidad, Formato, cantidad y el Generar, después de elegir el
          producto (ADR-2026-09-23-automatico-en-mobile-tiene-su-generar-2). Es la misma
          anatomía que "Modelo y pose" en Modelo: portal a `document.body` —montada
          dentro del sheet (`z-[101]`) quedaba atrapada en su stacking context—, z-36
          por encima del selector de productos (35) y por debajo del chrome (50), sin
          "Volver" propio (el back de arriba vuelve al paso 1, ver el effect de
          `mobileBackOverride`), y `BarraInferiorMobile` al pie con el safe-area a su
          cargo. */}
      {esAutomatica &&
        autoPaso2Mobile &&
        ReactDOM.createPortal(
          <div
            // Al terminar de ENTRAR se cierra el selector que quedó abajo.
            // `currentTarget`: sólo interesa la animación de esta pantalla.
            onAnimationEnd={(e) => {
              if (e.target === e.currentTarget) cerrarPickerAutoPendiente();
            }}
            className="md:hidden fixed inset-0 z-[36] bg-white flex flex-col page-slide-in-right"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
            }}
          >
            <div className="shrink-0 px-5 pb-4 flex items-center gap-2">
              <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                Indicaciones y ajustes
              </h2>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-3">
              {/* Lo elegido en el paso anterior, arriba de todo: sin esto la pantalla no
                  dice para qué producto se está configurando. Tocarla CIERRA esta
                  pantalla y abre el selector (que se monta por debajo, z-35): al
                  confirmar, vuelve acá. */}
              <CardProductos
                products={selectedProducts}
                onAbrirProductos={() => {
                  setAutoPaso2Mobile(false);
                  onShowProducts();
                }}
                onSubirArchivos={onStartUpload}
                // El mismo copy que en el paso 1: es la misma acción en la misma card.
                copyElegido="Añadir producto"
                iconoElegido="agregar"
              />
              {/* La instrucción: el único control del modo que se escribe, con el mismo
                  textarea y el mismo "Recientes" que Modelo y que la columna de desktop
                  (ADR-2026-08-28-automatico-instruccion-desplegada). Fondo no hay: la
                  escena la decide la IA (ADR-2026-08-28-automatico-sin-fondo). */}
              <div className="relative">
                <textarea
                  value={otraConfig}
                  onChange={(e) => onOtraChange(e.target.value)}
                  placeholder="Agregá indicaciones extras para afinar el resultado"
                  rows={3}
                  className="w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black/90 placeholder:text-brand-black/35 resize-none transition-colors"
                />
                <PromptRecientesButton
                  tipo="instruccion"
                  onPick={onOtraChange}
                  titulo="Instrucciones usadas antes"
                  className="absolute bottom-2.5 right-2.5"
                />
              </div>
            </div>
            <BarraInferiorMobile
              calidad={calidad}
              onCalidadChange={setCalidad}
              formatoLabel={getFormatLabel(formatoVal)}
              // El bottom sheet de siempre: en mobile no hay panel lateral donde
              // abrirlo (ADR-2026-08-28-automatico-formato-en-el-panel).
              onOpenFormato={onOpenFormatPicker}
              cantidad={autoImageCount}
              cantidadMax={autoImageCountMax}
              onCantidadChange={onAutoImageCountChange}
              imagenesDeLaTanda={autoImageCount}
              creditCost={creditCost}
              // Esta pantalla NO se cierra acá: se cierra cuando la generación arrancó
              // de verdad (ver el effect de `generacionEnCurso`). Cerrándola en el tap,
              // si el chequeo de plan y créditos fallaba, el usuario terminaba en el
              // paso 1 con un modal en vez de donde estaba.
              onGenerate={() =>
                onGenerate(creditCost != null && creditCost > 0 ? creditCost : 1)
              }
              // La misma frase que el pie de desktop, del mismo helper: con varios
              // productos NO sale una imagen por producto, sale UNA con todos juntos.
              resumen={resumenAutomatica(autoImageCount, selectedProducts.length)}
              falta={
                selectedProducts.length === 0
                  ? 'Elegí al menos un producto'
                  : undefined
              }
            />
          </div>,
          document.body,
        )}

      {/* ── Mobile-only: upload-product como PANTALLA completa ── */}
      {/* Mismo patrón que pick-product: pantalla full-screen por debajo del
          chrome (z-[35] < z-50), contenido debajo de la top bar y sin link
          "Volver" interno (el back lo da la top bar vía mobileBackOverride,
          que en upload-product vuelve a choose-product). Variante `desktop`
          para el layout header + scroll + footer con Confirmar. */}
      {step === 'upload-product' &&
        ReactDOM.createPortal(
          <div
            className="md:hidden fixed inset-0 z-[35] bg-[#F9F9F9] flex flex-col page-slide-in-right"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <UploadProductPanel
              variant="desktop"
              hideBack
              files={pendingProductFiles}
              name={pendingProductName}
              onNameChange={onPendingNameChange}
              onAddMore={onAddMoreFiles}
              onRemoveFile={onRemovePendingFile}
              onCancel={onCancelUpload}
              onConfirm={onConfirmUpload}
              uploading={uploadingProduct}
              error={uploadError}
              rubro={rubro}
              onBulkUpload={onBulkUpload}
            />
          </div>,
          document.body,
        )}

      {/* ── Desktop: layout tipo "página" con dos cards ─────────── */}
      {/* En vez del overlay edge-to-edge, simulamos una página normal:
          fondo gris del shell (igual a `EditarImagen`), padding alrededor,
          y dos cards blancas (imagen + opciones) flotando una al lado de
          la otra. El botón "Volver" vive en un header arriba de las
          cards, no dentro de ellas, para que ambos se sientan como
          contenedores parejos. */}
      <div
        // El marco flotante sale de `PantallaChrome`, compartido con el asistente y
        // los pickers full-screen (ADR-2026-08-03-chrome-de-pantalla-compartido):
        // retocarlo acá y no en los otros tres era exactamente cómo se separaban.
        // Los modos-sección van sin superficie ni borde: se apoyan en el lienzo del
        // shell, como `/plantillas` (ADR-2026-08-03-anuncio-se-ve-como-seccion). El
        // resto de los modos conserva el marco flotante.
        //
        // El aire de arriba de una sección del shell son 72px —32px del wrapper de
        // contenido + 40px de la fila del título—, que en `/plantillas` salen de dos
        // reglas de `.shell-seccion` que a este panel no lo alcanzan (las dos excluyen
        // a los hijos `fixed`). Se reparten en 52px de padding del PANEL y los 20px que
        // el encabezado ya trae… salvo en el paso que scrollea
        // (ADR-2026-08-04-el-encabezado-pegado-se-lleva-el-aire): ahí los 72px van
        // ENTEROS al encabezado.
        //
        // El motivo hoy es que el aire de arriba tiene que scrollear con el contenido:
        // el padding del panel queda FUERA del scroller, así que dejaría una franja
        // muerta arriba —el contenido cortado 52px abajo del borde— apenas se scrollea.
        // Adentro del encabezado, esos 72px se van con el resto. En los pasos que NO
        // scrollean el reparto viejo queda igual: ahí el panel no scrollea y el padding
        // no molesta a nadie.
        //
        // `lg:!right-0` hace que el panel mida EXACTAMENTE lo que `<main>`, que es de
        // lo que depende `COLUMNA_SECCION`: la columna es un 90% centrado, así que si
        // el panel midiera 8px menos (el marco flotante) su 5% daría distinto y la
        // columna del modo quedaría corrida contra la de `/inicio`. Se puede sacar
        // porque en la presentación de sección el marco no se ve.
        //
        // Todo con `lg:`: entre 768 y 1024px esta pantalla se monta en
        // `DashboardLayout`, que tiene su propia geometría.
        className={`hidden md:flex fixed inset-y-0 right-0 ${
          cederZIndex ? 'z-30' : 'z-[100]'
        } rounded-tl-[26px] rounded-bl-[26px] ${
          // `plantillaEligeProducto` entra con `esSeccion`: sin marco flotante ni
          // superficie propia. El paso se ve como una pantalla del shell —el panel a la
          // izquierda y la plantilla a la derecha, sobre el lienzo— y no como una
          // tarjeta que flota encima
          // (ADR-2026-09-07-el-paso-de-producto-de-plantillas-se-ve-como-una-pantalla).
          esSeccion || plantillaEnElPanel
            ? `${contenidoScrollea ? '' : 'lg:pt-[52px]'} lg:!right-0`
            : `bg-surface-content ${BORDE_PANTALLA}`
        } ${MARCO_PANTALLA} flex-col transition-all duration-300 ease-out ${
          animated ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.99]'
        } ${
          // El panel NUNCA es el scroller — ADR-2026-08-04-el-encabezado-vive-fuera-del-scroller.
          // En el paso `configure` lo era y el scroll se mudó al wrapper de abajo,
          // donde sigue: el panel es `fixed` CON `transform` (la animación de entrada),
          // y eso hace fragil su propio `overflow`.
          //
          // `overscroll-contain` va de la mano de `overflow-hidden`: éste SÍ es un
          // contenedor de scroll (scrolleable por código, sin barra), así que sin eso
          // la rueda encadena al `<main>` de atrás y la barra de créditos —que asoma
          // arriba— se iría scrolleando sola.
          contenidoScrollea
            ? 'overflow-hidden overscroll-contain'
            : // En los modos-sección el panel NO scrollea: mide lo que la ventana y el
              // scroll pasa a vivir adentro del contenido, que ya lo tiene
              // (ADR-2026-08-03-anuncio-panel-no-scrollea). `overscroll-contain` va de
              // la mano: `overflow-hidden` SÍ es un contenedor de scroll (scrolleable
              // por código, sin barra), así que sin esto la rueda encadena al `<main>`
              // de atrás y la barra de créditos —que ahora asoma arriba— se iría
              // scrolleando sola.
              esSeccion || plantillaEnElPanel
              ? 'overflow-hidden overscroll-contain'
              : ''
        }`}
        style={{
          left: sidebarWidth,
          // El panel arranca DEBAJO de la barra de créditos del shell en vez de
          // taparla (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra). La
          // variable la publica `EstiloMinimal` midiendo la barra; vale 0 cuando no
          // está, y no existe abajo de 1024px (shell viejo), donde el `0px` de
          // respaldo es exactamente el `inset-y-0` que la clase ya pone.
          ...(esSeccion || plantillaEnElPanel
            ? { top: 'var(--shell-barra-creditos, 0px)' }
            : null),
          // Estos modos no llevan superficie propia, pero tampoco pueden ser
          // transparentes: el panel se monta ENCIMA del contenido del shell y sin
          // fondo el breadcrumb y el título quedaban dibujados sobre lo que pasa por
          // debajo (ADR-2026-08-03-regla-del-titulo-no-toca-paneles-fixed). El color
          // es el del lienzo, así que se sigue leyendo como sección y no como
          // tarjeta. La variable la define `.shell-seccion`; el fallback es el lienzo
          // del shell viejo, donde esta pantalla se monta entre 768 y 1024px.
          ...(esSeccion || plantillaEnElPanel
            ? { background: 'var(--plantillas-sticky-bg, #FBFBFD)' }
            : null),
        }}
      >
        {/* Lo que SCROLLEA — ADR-2026-08-04-el-encabezado-vive-fuera-del-scroller,
            revisado por ADR-2026-08-04-el-breadcrumb-no-se-pega.
            En el paso `configure` el scroll no es del panel sino de este wrapper (el
            panel es `fixed` CON `transform` por la animación de entrada, y ésa es
            justo la combinación que rompe el scroll interno en varios motores). El
            encabezado va ADENTRO: es contenido que scrollea como cualquier otro, no
            una franja pegada arriba.

            En los pasos que no scrollean acá es `display: contents`, o sea que el
            wrapper desaparece del layout y sus hijos siguen siendo hijos directos del
            panel, exactamente como antes. */}
        <div
          className={
            contenidoScrollea
              ? 'flex flex-1 min-h-0 flex-col overflow-y-auto scrollbar-hide'
              : 'contents'
          }
        >

        {/* Encabezado compartido (ADR-2026-08-03-chrome-de-pantalla-compartido):
            pone el breadcrumb —o el "Volver" de respaldo, para las pantallas que no
            son un modo—. Las acciones de la derecha (toggle Imagen/Video, pill de
            Generar) siguen siendo de esta pantalla y van como children.

            Con `cabeceraEnColumna` este de acá no se dibuja: el breadcrumb baja a la
            columna de la imagen (ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna),
            y lo pinta `cabeceraSeccion` más abajo. Los children —el toggle
            Imagen/Video— son del paso `preview`, que nunca cae en ese caso. */}
        {!cabeceraEnColumna && !modoConDisenoNuevo && !plantillaEnElPanel && (
        <EncabezadoPantalla
          migas={migas}
          // En la presentación de sección el encabezado se alinea con el CONTENIDO de
          // abajo (`px-8` de la fila) y no con el `px-6` del resto de los modos: el
          // título de una sección arranca donde arranca lo que titula. Antes era
          // `!px-0` —el borde del panel— porque el panel no estaba quedando `fixed` y
          // su borde ERA la columna del shell
          // (ADR-2026-08-03-regla-del-titulo-no-toca-paneles-fixed); ya de vuelta en
          // su lugar, eso deja el título pegado al canto, a 8px de la sidebar.
          // Va con `!` porque pelea contra el `px-6` propio del componente, y quién
          // gana sin eso depende del orden en la hoja de estilos.
          // `lg:!pb-4` son los 16px que `/plantillas` deja entre el breadcrumb y el
          // `<h1>` (el `mb-4` de la variante `seccion` de `Migas`); el `pb-3` propio
          // del encabezado dejaba 12.
          // `lg:!pt-[72px]` en el paso que scrollea: los 52px que dejó de poner el panel
          // más los 20px propios del encabezado
          // (ADR-2026-08-04-el-encabezado-pegado-se-lleva-el-aire). El aire total arriba
          // del breadcrumb no cambia, y como ahora el encabezado vive DENTRO del
          // scroller, esa franja se va con el scroll en vez de quedar como un borde
          // muerto arriba del contenido.
          className={
            // Dentro de la columna izquierda (ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna)
            // el ancho y el margen los pone la columna: acá sólo queda el aire propio.
            cabeceraEnColumna
              ? '!px-0 !pt-0 lg:!pb-4'
              : esSeccion
                ? `!px-8 lg:!pb-4 ${contenidoScrollea ? 'lg:!pt-[72px]' : ''} ${COLUMNA_SECCION}`
                : ''
          }
          respaldo={
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </button>
          }
        >
          {/* Toggle Imagen/Video centrado en el header — solo en el step
              'preview', cuando hay carousel de plantillas para elegir. En
              otros steps (choose-product, configure, etc.) ya se decidió
              qué se va a generar, así que el toggle no aplica. */}
          {step === 'preview' && (
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
            >
              {toggleImagenVideo()}
            </div>
          )}
        </EncabezadoPantalla>
        )}

        {/* Título de la pantalla — sólo en la presentación de sección
            (ADR-2026-08-03-anuncio-se-ve-como-seccion). Mismo tamaño y peso que el
            `<h1>` de `/plantillas`, y el mismo `px-8` que el breadcrumb de arriba y
            que la fila de abajo: los tres arrancan en la misma línea.
            El breadcrumb NO se duplica: lo sigue poniendo `EncabezadoPantalla`, que
            arriba de todo queda ya sólo con eso. */}
        {esSeccion && (
          // Acciones del paso `configure`, a la derecha y a la altura del título —
          // ADR-2026-08-04-las-acciones-si-se-pegan-el-breadcrumb-no.
          //
          // Se PEGAN arriba (`sticky`) para que "Generar" siga a mano con la pantalla
          // scrolleada; el breadcrumb, en cambio, se va con el contenido
          // (ADR-2026-08-04-el-breadcrumb-no-se-pega). El sticky funciona acá porque el
          // scroller es este wrapper —un div común— y no el panel `fixed` con
          // `transform`, que es donde no aguantaba
          // (ADR-2026-08-04-el-encabezado-vive-fuera-del-scroller).
          //
          // No necesita fondo: los dos controles son opacos (pastilla blanca + botón
          // azul) y las tarjetas pasan por debajo sin cruzarse con ningún texto suelto.
          //
          // La fila mide CERO y su contenido desborda hacia abajo (`h-0`): así se
          // superpone a la fila del título sin empujarla ni un pixel. Poner el `<h1>`
          // adentro de esta fila sería lo natural, pero la haría el contenedor directo
          // de un `<h1>` dentro de `.shell-seccion`, y esa regla —la que le da 40px de
          // aire a las filas de título— le caería encima con `!important`, corriendo el
          // título hacia abajo en TODOS los modos-sección. El `<h1>` sigue colgando del
          // panel, que la regla excluye por ser `fixed`.
          <div
            // `top-4`: 16px de aire contra el borde de arriba del panel una vez pegados.
            // Sólo en el paso que scrollea — en los otros no hay scroll que seguir y
            // `sticky` no cambiaría nada, pero tampoco tiene por qué estar.
            className={`${
              contenidoScrollea ? 'sticky top-4 z-20' : 'relative z-10'
            } flex h-0 items-start justify-end px-8 ${COLUMNA_SECCION}`}
          >
            {/* Generar flotante (pill arriba a la derecha) — para los modos
                full-screen (design-modelo / anuncio / video-influencer).
                design-auto renderiza el Generar pegado al bottom del card de
                Opciones vía ConfigurePanel. En video-influencer, cuando falta
                algo (gancho / voz / guion) el pill se atenúa, muestra el hint
                debajo (como el sticky de mobile) y al clickearlo scrollea
                hasta el componente faltante y lo sacude (ADR-552). */}
            {configureFullScreen && (() => {
              // Qué falta en el modo influencer — misma prioridad que el hint
              // del ConfigurePanel (gancho → modelo → voz → guion) + id DOM del
              // target para el scroll+shake. Los ids existen SOLO en el render
              // desktop (prop `domIds`, evita duplicados con el panel mobile).
              const influencerBlock = faltaEnInfluencer;
              // "Fotos de producto" sin ninguna vista elegida: mismo tratamiento
              // que el influencer sin gancho — el pill se atenúa y al tocarlo
              // scrollea a la grilla y la sacude. El id existe solo en el render
              // DESKTOP del panel (el mobile usa su propio sufijo).
              const bloqueoGenerar =
                preset.kind === 'fotos-producto'
                  ? vistaIds.length === 0
                    ? {
                        msg: 'Elegí al menos una vista para generar.',
                        targetId: 'vistas-producto-grid-desktop',
                      }
                    : null
                  : influencerBlock;
              const handleGenerateClick = () => {
                if (bloqueoGenerar) {
                  // Scroll suave + sacudida fuerte al terminar (ADR-553).
                  scrollToAndShake(bloqueoGenerar.targetId);
                  return;
                }
                onGenerate(creditCost != null && creditCost > 0 ? creditCost : 1);
              };
              const generateButton = (
                <button
                  type="button"
                  onClick={handleGenerateClick}
                  aria-disabled={!!bloqueoGenerar}
                  className={`inline-flex items-center gap-2 py-2.5 pl-5 pr-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors pointer-events-auto ${
                    bloqueoGenerar ? 'opacity-60' : ''
                  }`}
                >
                  {preset.kind === 'video-influencer' ? (
                    <Play className="w-4 h-4" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  <span>
                    {preset.kind === 'video-influencer'
                      ? 'Generar video'
                      : 'Generar'}
                  </span>
                  {creditCost != null && creditCost > 0 && (
                    <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
                      <img
                        src="/images/token_icon.png"
                        alt=""
                        aria-hidden="true"
                        className="w-3.5 h-3.5 object-contain"
                      />
                      {creditCost}
                    </span>
                  )}
                </button>
              );
              // Bloqueado: card blanca EXACTAMENTE del ancho del botón (sin
              // marco extra alrededor del pill) que se extiende por detrás
              // hacia abajo con el hint. Siempre montada y colapsada con
              // transición de opacity/max-height para aparecer/desaparecer
              // suave. Durante el fade-out se muestra el último mensaje
              // (ref) para que el texto no desaparezca de golpe.
              if (bloqueoGenerar) {
                lastInfluencerHintRef.current = bloqueoGenerar.msg;
              }
              const generateBlock = (
                <div className="relative inline-flex flex-col items-stretch pointer-events-none">
                  {/* Capa card — mismo radio que el pill (22px), pegada a sus
                      bordes; solo se ve la parte que asoma abajo. */}
                  <div
                    aria-hidden
                    className={`absolute inset-0 rounded-[22px] bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                      bloqueoGenerar ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <div className="relative z-10">{generateButton}</div>
                  {/* Hint — `w-0 min-w-full` lo clava al ancho del botón (no
                      ensancha la card); colapsa con max-height. */}
                  <div
                    className={`relative z-10 w-0 min-w-full overflow-hidden transition-all duration-300 ease-out ${
                      bloqueoGenerar ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="pt-2 pb-1.5 px-3 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                      {bloqueoGenerar?.msg ?? lastInfluencerHintRef.current}
                    </p>
                  </div>
                </div>
              );
              // Sin contador (otros modos full-screen, o design-modelo con
              // varias poses / varios modelos): el Generar va solo, como antes.
              if (preset.kind !== 'design-modelo' || !designModeloCount.enabled) {
                return generateBlock;
              }
              // Contador "cantidad de imágenes" a la IZQUIERDA del Generar —
              // píldora blanca con el mismo lenguaje que el botón "Volver"
              // (bg-white/85 + backdrop-blur + ring). `items-start` para que
              // quede alineado al pill aunque el hint se despliegue abajo.
              const n = designModeloCount.count;
              return (
                <div className="inline-flex items-start gap-2">
                  <div className="inline-flex items-center gap-1 p-1.5 rounded-full bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] pointer-events-auto">
                    <button
                      type="button"
                      onClick={() => onAutoImageCountChange(Math.max(1, n - 1))}
                      disabled={n <= 1}
                      aria-label="Generar una imagen menos"
                      className="w-7 h-7 inline-flex items-center justify-center rounded-full text-brand-black/70 hover:bg-black/[0.05] hover:text-brand-primary disabled:opacity-35 disabled:pointer-events-none transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="min-w-[76px] text-center text-[13.5px] font-semibold text-brand-black/85 tabular-nums">
                      {n} {n === 1 ? 'imagen' : 'imágenes'}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onAutoImageCountChange(Math.min(autoImageCountMax, n + 1))
                      }
                      disabled={n >= autoImageCountMax}
                      aria-label="Generar una imagen más"
                      className="w-7 h-7 inline-flex items-center justify-center rounded-full text-brand-black/70 hover:bg-black/[0.05] hover:text-brand-primary disabled:opacity-35 disabled:pointer-events-none transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {generateBlock}
                </div>
              );
            })()}
          </div>
        )}

        {esSeccion && !cabeceraEnColumna && !modoConDisenoNuevo && !plantillaEnElPanel && (
          <h1
            className={`shrink-0 px-8 pb-4 text-[32px] font-semibold leading-tight text-brand-black/90 ${COLUMNA_SECCION}`}
          >
            {tituloSeccion}
          </h1>
        )}

        {step === 'preview' ? (
          // ── Desktop preview: carousel transicional ────────────────────
          // Cards absolute positioned con transform basado en (i - index).
          // Flechas a los costados de la imagen central (absolute dentro
          // del carousel area, vertical centerd). Soporte de wheel/swipe
          // horizontal para navegar — listener attached via useEffect.
          //
          // El container es `relative` para que el MultiSelectedStrip
          // pueda flotar absoluto en el bottom sin empujar la UI. Usamos
          // pb-28 SIEMPRE (no condicional) — con justify-center, ese
          // padding sube la columna a una posición donde queda lugar
          // para el strip flotante, y al aparecer/desaparecer el strip
          // la UI no se mueve.
          <div className="flex-1 px-6 pb-20 flex flex-col gap-5 min-h-0 justify-center items-center relative">
            {/* Toggle Imagen/Video vive ahora en el `<header>` arriba de
                este container — para mantener la altura del carousel +
                title intactas y reusar el espacio que ya queda libre
                arriba al lado de "Volver". */}
            <div
              ref={carouselRef}
              className="relative w-full flex-1 min-h-0 max-h-[72dvh] flex items-center justify-center overflow-hidden"
            >
              {previewList.map((p, i) => {
                const rel = i - previewIndex;
                if (Math.abs(rel) > 2) return null;
                const isCenter = rel === 0;
                // ±1 mantiene los valores anteriores. ±2 va al lado de
                // ±1 con MISMO tamaño (scale 0.42) y separación similar
                // a la que ±1 tiene con la central. Pueden quedar
                // parcialmente clippeadas por el overflow-hidden del
                // container — eso da el efecto "peek" sin overflow hacia
                // la sidebar.
                const absRel = Math.abs(rel);
                const translateX =
                  Math.sign(rel) * (absRel === 2 ? 712 : absRel === 1 ? 470 : 0);
                const scale = absRel === 0 ? 1 : 0.42;
                const opacity = absRel === 2 ? 0.4 : absRel === 1 ? 0.5 : 1;
                // Click en cualquier card LATERAL = saltar a esa (la
                // pone como centro). Click en la CENTRAL = utilizar en
                // single mode, o toggle selección en multi-mode.
                const onClick = !isCenter
                  ? () => setPreviewIndex(i)
                  : multiMode
                    ? // Igual que en mobile: solo las cards multi-selectable
                      // togglean; el resto no hace nada en multi-mode.
                      () => isPresetMultiSelectable(p) && toggleMultiId(p.id)
                    : () => onUtilizarPreset(p);
                const ariaLabel = !isCenter
                  ? `Ir a ${p.title}`
                  : multiMode
                    ? `${isMultiSelected(p.id) ? 'Quitar' : 'Seleccionar'} ${p.title}`
                    : `Utilizar ${p.title}`;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={onClick}
                    aria-label={ariaLabel}
                    className="absolute h-full max-h-[72dvh] aspect-[3/4] rounded-2xl overflow-hidden bg-neutral-100 ring-1 ring-black/[0.04] shadow-md transition-all duration-500 ease-out"
                    style={{
                      transform: `translateX(${translateX}px) scale(${scale})`,
                      opacity,
                      zIndex: 10 - absRel,
                      // Todas las visibles son clickeables — incluso las
                      // de rel=2 que asoman parcialmente, así el user
                      // puede tocarlas para saltar a esa plantilla.
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                    }}
                  >
                    {p.outputType === 'video' ? (
                      <video
                        src={isCenter ? p.imageUrlFull || p.imageUrl : p.imageUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        poster={p.imageUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        // Centro usa full (se ve grande); laterales usan
                        // opt porque están escaladas a ~0.42 y el opt
                        // alcanza para esa resolución.
                        src={isCenter ? p.imageUrlFull || p.imageUrl : p.imageUrl}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {multiMode && isPresetMultiSelectable(p) && (
                      <span
                        className={`absolute top-3 right-3 w-8 h-8 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white transition-colors ${
                          isMultiSelected(p.id)
                            ? 'bg-brand-primary text-white'
                            : 'bg-white/85 backdrop-blur-sm text-transparent'
                        }`}
                      >
                        <Check className="w-4 h-4" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}

              {/* Flechas — absolute al costado de la imagen central. La
                  central tiene 580px de ancho DOM (aspect 3/4 scaled 1),
                  centrada en el container. Posicionamos las flechas a
                  ~340px del centro para que queden a los lados de la
                  imagen, encima de las laterales (z-20). */}
              {/* Vertical center sin transform: inset-y-0 + my-auto.
                  Antes usábamos `top-1/2 -translate-y-1/2` y el transform
                  parece que en algunos browsers se "resetea" en :active
                  o interfiere con el hit testing (el user reportaba que
                  el botón "se movía para abajo" al presionarlo). Sin
                  transform, la posición es 100% estática.

                  Wrapper más grande con padding para hit area cómoda;
                  visualmente el círculo es 44×44 pero el target táctil
                  es ~56×56. */}
              <button
                type="button"
                onClick={() => canPrev && setPreviewIndex((i) => i - 1)}
                disabled={!canPrev}
                aria-label="Anterior"
                style={{ left: 'calc(50% - 346px)' }}
                className="absolute inset-y-0 my-auto h-11 z-20 inline-flex items-center justify-center p-1.5 box-content rounded-full hover:bg-neutral-50/0 disabled:cursor-not-allowed transition-colors"
              >
                <span
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-brand-black/70 shadow-md ring-1 ring-black/[0.04] transition-colors ${
                    canPrev ? 'hover:bg-neutral-50' : 'opacity-30'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </span>
              </button>
              <button
                type="button"
                onClick={() => canNext && setPreviewIndex((i) => i + 1)}
                disabled={!canNext}
                aria-label="Siguiente"
                style={{ right: 'calc(50% - 346px)' }}
                className="absolute inset-y-0 my-auto h-11 z-20 inline-flex items-center justify-center p-1.5 box-content rounded-full disabled:cursor-not-allowed transition-colors"
              >
                <span
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-brand-black/70 shadow-md ring-1 ring-black/[0.04] transition-colors ${
                    canNext ? 'hover:bg-neutral-50' : 'opacity-30'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </span>
              </button>
            </div>

            {/* Title row debajo, sin flechas ahora (viven arriba al lado
                de la imagen). El title se anima con animate-title-slide
                en cada cambio de previewIndex. */}
            <div className="w-[580px] max-w-full flex items-center justify-between gap-2.5">
              <p
                key={desktopPreviewPreset.id}
                className="text-[19px] font-semibold text-brand-black/90 truncate min-w-0 flex-1 animate-title-slide"
              >
                {desktopPreviewPreset.title}
              </p>
              {/* "Seleccionar varias" pill (ícono Copy) — solo cuando NO
                  estamos en multi mode y el preset actual lo admite. Una
                  vez en multi mode, no hay botón separado para entrar/
                  salir — el botón principal de abajo absorbe la lógica
                  (mismo patrón que en mobile). */}
              {!multiMode &&
                isPresetMultiSelectable(desktopPreviewPreset) && (
                  <button
                    type="button"
                    onClick={() => enterMultiMode(desktopPreviewPreset.id)}
                    aria-label="Seleccionar varias"
                    className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              {/* Botón de acción único — mismo patrón que mobile:
                  • !multiMode → "Generar con mi producto" (utiliza single)
                  • multiMode + currentSelected → "Generar (N)" (confirma)
                  • multiMode + !currentSelected + multi-selectable →
                    "Seleccionar" (añade al set)
                  • multiMode + !multi-selectable → confirma N si hay,
                    sino cae a "Generar con mi producto" single. */}
              <button
                type="button"
                onClick={() => {
                  if (!multiMode) {
                    onUtilizarPreset(desktopPreviewPreset);
                    return;
                  }
                  const canAdd = isPresetMultiSelectable(desktopPreviewPreset);
                  const isSel = isMultiSelected(desktopPreviewPreset.id);
                  if (!canAdd && !isSel) {
                    if (multiSelectedPresets.length > 0) handleConfirmMulti();
                    else onUtilizarPreset(desktopPreviewPreset);
                    return;
                  }
                  if (isSel) handleConfirmMulti();
                  else toggleMultiId(desktopPreviewPreset.id);
                }}
                disabled={multiMode && multiSelectedIds.length === 0 &&
                  !isPresetMultiSelectable(desktopPreviewPreset)}
                className={`shrink-0 inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-md transition-colors ${
                  multiMode &&
                  isMultiSelected(desktopPreviewPreset.id) &&
                  multiSelectedIds.length > 0
                    ? 'pl-5 pr-3'
                    : 'px-6'
                }`}
              >
                {multiMode &&
                !isMultiSelected(desktopPreviewPreset.id) &&
                isPresetMultiSelectable(desktopPreviewPreset) ? (
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
                <span>
                  {!multiMode
                    ? 'Generar con mi producto'
                    : isMultiSelected(desktopPreviewPreset.id)
                      ? 'Generar'
                      : isPresetMultiSelectable(desktopPreviewPreset)
                        ? 'Seleccionar'
                        : multiSelectedPresets.length > 0
                          ? 'Generar'
                          : 'Generar con mi producto'}
                </span>
                {/* Badge circular blanco con el conteo — solo cuando el
                    label efectivo es "Generar" en multi-mode con N items. */}
                {multiMode &&
                  multiSelectedIds.length > 0 &&
                  (isMultiSelected(desktopPreviewPreset.id) ||
                    !isPresetMultiSelectable(desktopPreviewPreset)) && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-white text-brand-primary text-[13px] font-bold leading-none tabular-nums">
                      {multiSelectedIds.length}
                    </span>
                  )}
              </button>
            </div>
            {multiMode && multiSelectedPresets.length > 0 && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-30 w-[640px] max-w-[calc(100%-3rem)] pointer-events-none">
                <div className="pointer-events-auto">
                  <MultiSelectedStrip
                    selected={multiSelectedPresets}
                    onRemove={toggleMultiId}
                    onCancel={exitMultiMode}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          // ── Desktop flujo normal: dos cards (imagen + opciones) ───────
          // En el modo 'design-modelo' a partir del step 'configure'
          // ocultamos la imagen y dejamos solo el aside, centrado y más
          // amplio — son muchas opciones (Modelo + Pose + Fondo + Formato)
          // y la imagen de la izquierda no aporta. La transición es vía
          // `transition-all` sobre width + opacity de la imagen.
          <div
            ref={areaDosColumnasCatalogoRef}
            // `relative`: el panel lateral de Catálogo se posiciona respecto de ESTA
            // área, para respetar el ancho de la columna de configuración.
            className={`relative px-8 pb-6 flex gap-6 ${
              // Con el panel a un ancho PROPIO, `justify-center` dejaba las dos columnas
              // como un bloque centrado que se corría a la derecha en cada monitor más
              // ancho. Arrancan pegadas al borde IZQUIERDO de la fila —que ya está
              // centrada por `COLUMNA_SECCION`—, así que el panel queda un poco más a la
              // izquierda y sobre todo SIEMPRE en el mismo lugar
              // (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660).
              //
              // `justify-end` y no `justify-start`: estos modos van con
              // `flex-row-reverse`, así que el eje está dado vuelta y el "final" es el
              // borde izquierdo de la pantalla.
              modoConDisenoNuevo || plantillaEnElPanel
                ? 'justify-end'
                : 'justify-center'
            } ${
              // El panel de productos va a la IZQUIERDA. Se invierte con
              // `flex-row-reverse` y no reordenando el JSX porque el orden del DOM es el
              // que importa para el foco y los lectores de pantalla: primero la
              // presentación del modo, después el panel con el que se opera.
              //
              // Vale para TODOS los pasos del rediseño, no sólo para el de elegir
              // producto: el panel se queda quieto de un paso al siguiente, que es
              // justamente lo que hace que `configure` se lea como un avance sobre la
              // misma pantalla y no como una pantalla nueva.
              modoConDisenoNuevo || plantillaEnElPanel
                ? 'flex-row-reverse'
                : 'flex-row'
            } ${
              // La MISMA clase con la muestra del modo y con las tandas, a propósito:
              // que cambiara entre un estado y el otro es lo que movía el panel al
              // generar (ADR-2026-09-06-la-columna-de-resultados-ocupa-la-misma-caja).
              // Los modos del rediseño arrancan a 52px de la sidebar —el mismo aire que
              // tienen arriba— y llegan al borde derecho
              // (ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde); los
              // otros modos-sección siguen con el 90% centrado, que es lo que alinea su
              // encabezado cruzado con el resto del shell.
              esSeccion
                ? modoConDisenoNuevo
                  ? FILA_MODO_REDISENO
                  : COLUMNA_SECCION
                : plantillaEnElPanel
                  ? FILA_MODO_REDISENO
                  : ''
            } ${
              configureFullScreen
                ? 'items-start'
                : 'flex-1 min-h-0 items-stretch'
            }`}
          >
            {smallCenteredPreview && !configureFullScreen ? (
              // Layout especial para todos los steps de design-modelo /
              // design-auto / catalogo-ia que muestran la imagen
              // (choose-product, pick-product, upload-product): imagen
              // chica + texto explicativo JUSTO debajo, todo centrado.
              // El card crece a flex-1 para igualar el alto del sidebar.
              // En 'configure' el card se oculta entero (otra rama).
              <div
                // Cuando esta columna es la que sostiene la CONFIGURACIÓN —el paso 2 de
                // Modelo, y Automático, donde la config vive acá y no en el aside—, mide
                // lo mismo que el panel en el resto de los pasos: es el mismo panel
                // (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660). Mostrando
                // la muestra del modo o las tandas sigue tomando el ancho que sobra.
                //
                // En los pasos 2 y 3 del Catálogo NO sostiene la configuración: sostiene
                // el picker de vistas y el review, que son grillas y crecen con lo que
                // haya. Fijarlas en 660 dejaba las DOS columnas del mismo ancho y el resto
                // del monitor en blanco —en 1920 sobraban ~600px—, con las tarjetas de
                // vista a 93px y los nombres desbordados encima de la foto. Ahí toma el
                // ancho que sobra (ADR-2026-09-04-la-columna-de-vistas-toma-el-ancho-que-sobra).
                // Con las TANDAS en esta columna tampoco sostiene la configuración:
                // sostiene una grilla de resultados, que crece con lo que haya. Fijada en
                // 660 dejaba ~280px del monitor en blanco a su derecha y una card menos
                // por fila (ADR-2026-09-04-la-columna-de-resultados-toma-el-ancho-que-sobra,
                // que extiende el mismo criterio de la columna de vistas). El 660 se
                // queda donde nació: en el `configure`, para que el panel no salte de
                // ancho entre pasos.
                // `configureDosColumnas` y no `pasoDosColumnas`: el ancho de panel es
                // para cuando esta columna SOSTIENE LA CONFIGURACIÓN, o sea el paso 2 de
                // "Imagen con modelo". En el paso de vistas, en el review y en Fotos de
                // producto sostiene una GRILLA, que tiene que tomar el ancho que sobra
                // (ADR-2026-09-04-la-columna-de-vistas-toma-el-ancho-que-sobra); antes
                // eso lo garantizaba `catalogoPanelAngosto`, que hacía doble función y
                // dejó de cubrir a Fotos de producto al estandarizarse el ancho del panel
                // (ADR-2026-09-06-un-solo-ancho-de-panel-para-los-modos).
                style={
                  columnaConfiguracion || !configureDosColumnas
                    ? undefined
                    : { flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }
                }
                className={`min-w-0 flex flex-col ${
                  !columnaConfiguracion && configureDosColumnas
                    ? 'grow-0 shrink-0'
                    : 'flex-1'
                } ${
                  // Sólo con las TANDAS: la columna se sale de los paddings y llega al
                  // marco arriba, abajo y a la derecha
                  // (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco). Con la
                  // muestra del modo, la configuración, el picker de vistas o el review
                  // adentro se queda en su caja.
                  columnaResultadosAlBorde ? COLUMNA_RESULTADOS_AL_BORDE : ''
                }`}
              >
                {/* La cabecera vive ACÁ, no cruzando la pantalla
                    (ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna): el
                    breadcrumb, el título y la imagen son una sola presentación del modo y
                    ocupan su mitad, mientras la selección de productos usa la altura
                    entera de la suya. */}
                {cabeceraEnColumna && (
                  <>
                    <EncabezadoPantalla
                      migas={migas}
                      className="!px-0 !pt-0 lg:!pb-4"
                      respaldo={
                        <button
                          type="button"
                          onClick={onClose}
                          className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Volver
                        </button>
                      }
                    />
                    <h1 className="shrink-0 pb-4 text-[32px] font-semibold leading-tight text-brand-black/90">
                      {tituloSeccion}
                    </h1>
                  </>
                )}
                {/* `columnaConfiguracion` gana SIEMPRE que venga, no sólo en el paso 2:
                    en automático no hay paso 2 y las tandas tienen que ocupar igual esta
                    columna, que es donde estaba la muestra del modo
                    (ADR-2026-08-21-automatico-con-el-diseno-nuevo). En los otros modos no
                    cambia nada: ahí sólo llega con el paso 2 abierto.

                    Fotos de producto queda AFUERA del primer término: su paso 2 ya no
                    pone nada en esta columna —las vistas se eligen en el panel lateral,
                    como el Fondo (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral)—,
                    así que la columna sigue siendo la MUESTRA del modo, igual que en el
                    paso 1. Con tandas manda `columnaConfiguracion`, que es lo que las
                    dibuja, y ahí sí ocupa la columna. */}
                {(pasoDosColumnas && !fotosProductoDosColumnas) ||
                columnaConfiguracion ? (
                  // La configuración ocupa el lugar de la muestra. Scrollea por su
                  // cuenta (`overflow-y-auto`): el wrapper de afuera ya no es el
                  // scroller —`contenidoScrollea` es false a dos columnas— y sin esto la
                  // columna crecería más que la pantalla.
                  //
                  // El picker de vistas es la excepción: scrollea ADENTRO (su caja tiene
                  // las pills pegadas arriba), así que acá sólo se le da el alto.
                  <div
                    key="paso-configurar"
                    className={`flex-1 min-h-0 ${
                      catalogoVistasDosColumnas || catalogoReviewDosColumnas
                        ? // Los dos scrollean ADENTRO (el picker por sus pills pegadas
                          // arriba, el review por su propia área de scroll): acá sólo se
                          // les da el alto.
                          'flex flex-col'
                        : 'overflow-y-auto'
                    } ${
                      // El aire que `COLUMNA_RESULTADOS_AL_BORDE` sacó afuera vuelve acá
                      // adentro, donde scrollea con las cards en vez de ser una franja
                      // fija arriba.
                      columnaResultadosAlBorde ? COLUMNA_RESULTADOS_AIRE_INTERNO : ''
                    } ${pasoAnimClass}`}
                  >
                    {catalogoReviewDosColumnas ? (
                      // Paso 3: el review, embebido. Sin su barra de arriba (el Volver lo
                      // da el breadcrumb, el Generar vive en el panel) y sin las cards
                      // globales de Fondo y Modelo, que pasaron al panel.
                      <CatalogoCardsFullscreen
                        open
                        embebido
                        // Arriba del título y DENTRO de la caja de la columna: suelto
                        // sobre el gris de la pantalla se leía como un control de la
                        // pantalla entera y no de esta columna
                        // (ADR-2026-08-27-el-volver-vive-en-la-caja-del-titulo).
                        volverEmbebido={onBackFromCatalogoConfigure}
                        ocultarAjustesGlobales
                        onActiveCardsChange={setCardsActivasReview}
                        variant="desktop"
                        sidebarWidth={sidebarWidth}
                        selectedProducts={selectedProducts}
                        selectedVistas={catalogoVistas}
                        allPoses={allPoses}
                        customPoses={customPoses}
                        onBack={onBackFromCatalogoConfigure}
                        onGenerate={onGenerateCatalog}
                        userCompanyId={userCompanyId}
                        brandId={brandId}
                        rubro={rubro}
                        userPlan={userPlan}
                        tiposProductoSugeridos={preset.tiposProductoSugeridos}
                        onProductsRefreshed={onProductsRefreshed}
                        fondoSelection={fondoSelection}
                        modelSelection={modelSelection}
                        onOpenFondo={() =>
                          onOpenFondoSheet(
                            usaBibliotecaDeFondos(preset.kind) ? 'all' : 'list',
                          )
                        }
                        onOpenModel={() => onOpenModelSheet('list')}
                      />
                    ) : catalogoVistasDosColumnas ? (
                      <VistaPickerFullscreen
                        open
                        embebido
                        volverEmbebido={onBackToChoose}
                        variant="desktop"
                        sidebarWidth={sidebarWidth}
                        allPoses={allPoses}
                        customPoses={customPoses}
                        onUploadCustomPose={onUploadCustomPose}
                        uploadingCustomPose={uploadingCustomPose}
                        onDeleteCustomPose={onDeleteCustomPose}
                        selectedProducts={selectedProducts}
                        selectedVistas={catalogoVistas}
                        onChange={onCatalogoVistasChange}
                        onBack={onBackToChoose}
                        // El CTA vive en el panel de al lado; el picker embebido no
                        // dibuja el suyo, pero la prop es obligatoria.
                        onNext={onConfirmCatalogoVistas}
                      />
                    ) : (
                      columnaConfiguracion ?? renderStep('desktop')
                    )}
                  </div>
                ) : (
                <div
                  key="paso-presentacion"
                  // Sin `pl-28`: esos 112px eran el aire que le cedía la columna a la
                  // tira vacía, que flotaba saliendo del borde derecho del panel. Sin esa
                  // tarjeta la muestra va centrada de entrada, igual que con la tanda ya
                  // armada — antes el título saltaba al centro recién con el primer
                  // producto (ADR-2026-09-07-la-tira-vacia-no-se-dibuja).
                  className={`flex-1 min-h-0 flex flex-col items-center justify-center gap-2 ${
                    modoConDisenoNuevo ? pasoAnimClass : ''
                  }`}
                >
                {/* El texto ENCABEZA la muestra en vez de ir de pie de foto — el
                    tratamiento que estrenó Modelo v2
                    (ADR-2026-08-21-modelo-v2-la-muestra-se-queda): título en negrita
                    arriba y la bajada abajo, para que se lea como el título de la
                    pantalla y no como un epígrafe de la imagen. Quiénes lo llevan lo
                    dice `encabezadoMuestra`; el resto conserva su párrafo debajo (la
                    rama de más abajo). */}
                {encabezadoMuestra && (
                  <div className="shrink-0 text-center max-w-[440px] mb-3">
                    <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                      {encabezadoMuestra.titulo}
                    </h2>
                    <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                      {encabezadoMuestra.bajada}
                    </p>
                  </div>
                )}
                {heroCollage ? (
                  <ProductHeroCollage
                    original={heroCollage.original}
                    results={heroCollage.results}
                    layout={heroCollage.layout}
                  />
                ) : catalogoPasoProductos ? (
                  /* Catálogo, paso 1: la muestra y la TANDA comparten la misma caja —
                     con el primer producto elegido, la tira se muda acá y la ilustración
                     se funde (ADR-2026-09-04-la-tanda-se-muda-a-la-muestra).
                     La caja es `relative` y el tablero va encima con `absolute inset-0`:
                     así aparecer no mueve nada. Si el tablero entrara EN FLUJO, la
                     columna se recentraría en el mismo frame en que se elige el producto
                     —y el vuelo de la tarjeta, que mide su celda de destino en ese
                     frame, aterrizaría a doscientos píxeles de donde la celda termina—.
                     La caja crece (`flex-1`) pero con TOPE de 600px: sin el tope se comía
                     todo el alto sobrante de la columna y la ilustración quedaba centrada
                     ahí adentro, a un palmo del título. Con el tope, el sobrante se lo
                     reparte el `justify-center` de la columna —arriba del título y abajo
                     de la caja—, que es lo que mantiene al título PEGADO a la muestra. */
                  <div className="relative w-full flex-1 min-h-0 max-h-[600px] flex items-center justify-center">
                    <img
                      src={preset.imageUrlFull || preset.imageUrl}
                      alt={preset.title}
                      // Más grande que en el resto de los modos: es lo que explica qué
                      // hace el Catálogo. La columna mide ~1010px y le sobraba alto con
                      // el tope de 480.
                      //
                      // El tope de alto lo pone la CAJA (`max-h-[600px]` + `flex-1`), y
                      // la imagen se acota a ella: en una pantalla baja la caja mide lo
                      // que sobre y la imagen baja con ella, sin desbordarse.
                      //
                      // Con la tanda armada la ilustración se APAGA, no se desmonta: la
                      // caja que ocupa es la que le da su tamaño al tablero de encima.
                      className={`max-w-[680px] max-h-full w-full h-auto object-contain transition-opacity duration-300 ${
                        tandaEnLaMuestra ? 'opacity-0' : 'opacity-100'
                      }`}
                    />
                    {/* El nodo donde el panel de al lado dibuja la tanda. Va SIEMPRE
                        montado —vacío no ocupa nada— porque el panel necesita el nodo
                        antes de la primera elección para portalear en el mismo frame.
                        `pointer-events-none` en la caja y `auto` en el tablero: la caja
                        cubre toda la muestra y sin eso se comería los clicks de un área
                        donde no hay nada que tocar.
                        `items-start`: el tablero se apoya ARRIBA de la caja, debajo del
                        título. Centrado quedaba flotando en el medio de un espacio vacío
                        —es más chico que la ilustración que reemplaza—. */}
                    <div
                      ref={setDestinoTanda}
                      className="absolute inset-0 flex items-start justify-center pointer-events-none"
                    />
                  </div>
                ) : (
                  <img
                    src={preset.imageUrlFull || preset.imageUrl}
                    alt={preset.title}
                    // `max-h-[360px]` evita que un asset portrait (como el
                    // hero de catalogo-ia) se vea desproporcionalmente alto
                    // comparado con los landscape de los otros modos. El hero
                    // de anuncio es un collage que se ve mejor un poco más
                    // chico, así que sólo ese baja de tamaño en desktop.
                    className={`${
                      disenoElegirProducto
                        ? // Más grande que en el resto de los modos (que están topeados
                          // en 480px): es lo que explica qué hace el Catálogo y el pedido
                          // fue darle protagonismo. El ancho de la columna la limita
                          // antes que este tope en pantallas chicas.
                          'max-w-[560px] max-h-[480px]'
                        : preset.kind === 'anuncio'
                          ? 'max-w-[380px] max-h-[330px]'
                          : 'max-w-[480px] max-h-[420px]'
                    } w-full h-auto object-contain`}
                  />
                )}
                {/* Con el texto encabezando la muestra, el pie de foto sería el mismo
                    mensaje dos veces. */}
                <p
                  hidden={!!encabezadoMuestra}
                  className={`text-[#3C3C43]/60 leading-relaxed text-center text-balance ${
                    disenoElegirProducto
                      ? 'text-[15.5px] max-w-[440px] mt-1'
                      : 'text-[12.5px] max-w-[360px]'
                  }`}
                >
                  {preset.kind === 'design-auto'
                    ? 'Atlas elige la escena por vos. Subí tu producto y la IA arma la imagen final en segundos — sin configurar nada.'
                    : // Bajadas CORTAS de los modos con el rediseño — la muestra pasó a
                      // ocupar la columna entera y el párrafo largo competía con ella.
                      // Una por modo: la mecánica que explican no es la misma.
                      preset.kind === 'catalogo-ia'
                      ? 'Elegí vistas y poses — Atlas genera una imagen por cada combinación.'
                      : preset.kind === 'design-modelo'
                        ? `Elegí ${palabra.singular}, pose y fondo — Atlas arma la escena en segundos.`
                        : esCatalogoIA(preset.kind)
                      ? 'Generá photoshoots de catálogo para cada producto en un solo proceso. Elegí qué vistas y poses querés generar — Atlas arma una imagen por cada combinación.'
                      : preset.kind === 'catalogo-muebles'
                        ? 'Subí tu producto y Atlas arma el catálogo completo: packshot, foto en contexto e infografía con medidas — todo en un solo proceso.'
                        : preset.kind === 'infografia'
                          ? 'Subí tu producto y Atlas genera la infografía con sus medidas y características, lista para tu ficha.'
                          : preset.kind === 'anuncio'
                            ? 'Armá un anuncio listo para publicar — texto, colores y formato definidos por vos o por Atlas.'
                            : preset.kind === 'video-influencer'
                              ? preset.description
                              : preset.kind === 'fotos-producto'
                                ? `Fotos de tu producto solo, sin ${palabra.singular}. Elegí uno o varios productos y desde qué vistas querés verlos — sobre un fondo liso o en una escena.`
                                : `Generá una imagen de tu producto sobre un ${palabra.singular} de IA. Elegí el ${palabra.singular}, la pose y el fondo — Atlas arma la escena en segundos.`}
                </p>
                </div>
                )}
              </div>
            ) : plantillaConfigura && columnaConfiguracion ? (
              // Generó: la columna pasa de la plantilla a los RESULTADOS, igual que en
              // los otros modos (ADR-2026-09-08-plantillas-genera-en-la-columna). Son las
              // mismas tandas del registro a nivel app, así que también siguen a la vista
              // al cambiar de modo (ADR-2026-09-08-los-resultados-se-quedan-toda-la-sesion).
              //
              // `overflow-y-auto` y no sólo `min-h-0`: la columna es el SCROLLER de los
              // resultados (ADR-2026-09-14-la-columna-de-resultados-de-plantillas-scrollea).
              // `PanelTandaCreaciones` no scrollea por su cuenta y a dos columnas el
              // wrapper de afuera tampoco (`contenidoScrollea` es false), así que sin
              // esto la grilla crecía más que la pantalla y el `overflow-hidden` del
              // panel cortaba todo lo que pasara del alto: con más de dos filas, las de
              // abajo eran inalcanzables. Es el mismo scroller que ya tiene la rama de
              // los modos ("paso-configurar"), que llega acá con el mismo contenido.
              <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
                {columnaConfiguracion}
              </div>
            ) : (
              <ColumnaDeLaPlantilla
                // Con el diseño nuevo, la plantilla lleva ARRIBA el interruptor
                // Imagen/Video (ADR-2026-09-08-el-switch-de-imagen-o-video-va-arriba-de-la-plantilla),
                // así que la tarjeta pasa a vivir dentro de una columna. Sin él, la
                // tarjeta es el ítem de la fila, como siempre.
                // En TODAS las cards de la grilla de Plantillas, que es donde lo dibujaba
                // el `preview` del que viene: gatearlo por `kind === 'template'` lo hacía
                // desaparecer justo en las poses de "Fondo blanco", que son las primeras
                // de la grilla — y la pantalla se leía como que el interruptor no estaba.
                // Ojo: en esas poses no HACE nada (`isVideoModeActive` está gateado por
                // ese kind), igual que no hacía nada en el preview. Que genere video desde
                // una pose es una decisión de backend, no de esta pantalla.
                encabezado={
                  // Sin interruptor arriba de la imagen, en NINGÚN paso —
                  // ADR-2026-09-10-el-interruptor-de-medio-recien-en-el-segundo-paso,
                  // que supersede ADR-2026-09-08-el-switch-de-imagen-o-video-va-arriba-de-la-plantilla.
                  //
                  // En el paso de producto la pregunta es cuál producto, y elegir imagen
                  // o video ahí adelanta una decisión que recién se usa al configurar. Se
                  // queda donde ya lo puso
                  // ADR-2026-09-08-el-switch-de-imagen-o-video-va-al-panel: dentro de la
                  // card del paso 2, junto al resto de las decisiones del modo.
                  //
                  // Con `null` la columna desaparece y la tarjeta vuelve a ser el ítem de
                  // la fila, que es exactamente como ya se dibujaba el paso 2: los dos
                  // pasos dejan de tener anatomías distintas a la derecha.
                  null
                }
              >
              <div
                className={`relative ${
                  // La caja blanca (fondo, redondeo, sombra y recorte) sólo cuando la
                  // tarjeta MIDE lo que la imagen. Dentro de la columna del interruptor y
                  // sin una proporción declarada eso no pasa: el alto de la tarjeta se
                  // resuelve después que su ancho, así que el ancho se calcula con el
                  // TAMAÑO NATURAL de la foto y queda una franja blanca al costado
                  // (ADR-2026-09-08-la-plantilla-sin-proporcion-declarada-no-lleva-caja).
                  // Ahí la caja se va y el marco se lo pone la propia imagen.
                  plantillaEnElPanel && preset.kind !== 'template' && !isMultiPreset
                    ? 'flex items-center justify-center'
                    : 'bg-white rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/[0.04]'
                } ${
                  configureFullScreen
                    ? 'hidden'
                    : isMultiPreset
                      ? 'flex-1'
                      : preset.kind === 'template'
                        ? // Plantillas: preview en 4:5. Estira al alto
                          // disponible (items-stretch del row) y el ancho
                          // queda dado por la relación 4:5.
                          `aspect-[4/5] ${
                            // Dentro de la columna con el interruptor arriba: la tarjeta
                            // se lleva el alto que sobra y el ancho sale de la
                            // proporción. Fuera, es el ítem de la fila y se estira sola.
                            plantillaEnElPanel
                              ? 'flex-1 min-h-0 max-w-full'
                              : 'shrink-0 max-w-[60%]'
                          }`
                        : plantillaEnElPanel
                          ? 'flex-1 min-h-0 max-w-full w-full'
                          : 'shrink-0 max-w-[60%]'
                }`}
              >
                {isMultiPreset ? (
                  <MultiPresetCarousel presets={multiPresets} fit="cover" />
                ) : preset.outputType === 'video' ? (
                  <video
                    src={preset.imageUrlFull || preset.imageUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={preset.imageUrl}
                    className={
                      preset.kind === 'template'
                        ? 'absolute inset-0 w-full h-full object-cover'
                        : plantillaEnElPanel
                          ? // Sin caja alrededor: el marco (redondeo, sombra, filete) lo
                            // lleva la imagen, que es lo único que sabe cuánto mide.
                            'block max-h-full max-w-full w-auto h-auto object-contain rounded-2xl shadow-sm ring-1 ring-black/[0.04]'
                          : 'block h-full w-auto max-w-full object-cover'
                    }
                  />
                ) : (
                  <img
                    src={preset.imageUrlFull || preset.imageUrl}
                    alt={preset.title}
                    className={
                      preset.kind === 'template'
                        ? 'absolute inset-0 w-full h-full object-cover'
                        : plantillaEnElPanel
                          ? // Sin caja alrededor: el marco (redondeo, sombra, filete) lo
                            // lleva la imagen, que es lo único que sabe cuánto mide.
                            'block max-h-full max-w-full w-auto h-auto object-contain rounded-2xl shadow-sm ring-1 ring-black/[0.04]'
                          : 'block h-full w-auto max-w-full object-cover'
                    }
                  />
                )}
              </div>
              </ColumnaDeLaPlantilla>
            )}
            <aside
              // El ancho, en px y no en clases: es el mismo número para el panel entero
              // (`ANCHO_PANEL_CONFIG`) y lo comparten las ramas del REDISEÑO que lo
              // dibujan.
              //
              // El `configure` a pantalla completa NO entra: ése es el de los modos que
              // quedaron afuera del rediseño —Video con influencer, plantillas,
              // E-commerce, Anuncio—, y ahí el panel es la pantalla entera, con secciones
              // de dos columnas adentro. Con los 660 del panel de Modelo, el carrusel de
              // modelos quedaba cortado por la mitad y las secciones apretadas contra el
              // borde. Vuelve a su `max-w-[1040px]` de siempre — ver
              // ADR-2026-09-04-el-configure-a-pantalla-completa-vuelve-a-1040, que acota
              // ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660 a los tres
              // modos que esa entrada nombra.
              style={
                configureFullScreen
                  ? undefined
                  : panelComprimido
                    ? undefined
                    : catalogoPanelAngosto
                      ? // Pasos 2 y 3 del Catálogo: el panel se angosta y le cede el
                        // ancho a las vistas / al review, que es lo que se está usando.
                        {
                          flexBasis: ANCHO_PANEL_CONFIG_CATALOGO,
                          width: ANCHO_PANEL_CONFIG_CATALOGO,
                        }
                      : catalogoPaso2 || modoConDisenoNuevo || plantillaEnElPanel
                        ? { flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }
                        : undefined
              }
              ref={panelConfigRef}
              className={`shrink-0 flex flex-col ${
                configureFullScreen
                  ? 'w-full max-w-[1040px] bg-transparent'
                  : `bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden ${
                      panelComprimido
                        ? // Paso 2: el panel se comprime a una preview y le cede el ancho
                          // a la configuración. Ancho FIJO en px y no un porcentaje: lo
                          // que tiene que entrar son dos miniaturas y las filas de
                          // ajustes, que miden lo mismo en cualquier pantalla — con un
                          // porcentaje, en un monitor grande la preview se agrandaba sin
                          // mostrar nada más.
                          'basis-[280px] grow-0'
                        : catalogoPaso2 || modoConDisenoNuevo || plantillaEnElPanel
                          ? // El ancho de `ANCHO_PANEL_CONFIG` (el `style` de arriba),
                            // el mismo en todos los pasos y en los tres modos. Antes eran
                            // dos medidas distintas —320px en los pasos 2 y 3 del Catálogo
                            // (ADR-2026-08-27-el-panel-del-paso-2-baja-a-320) y 35% de la
                            // fila en el resto—, así que el panel cambiaba de tamaño al
                            // avanzar de paso
                            // (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660).
                            'grow-0 shrink-0'
                          : 'flex-1'
                    }`
              }`}
            >
              {catalogoPaso2 ? (
                // Catálogo IA: la columna es la CONFIGURACIÓN entera —productos, vistas,
                // ajustes y Generar— y no la grilla de productos, que pasó al panel
                // lateral (ADR-2026-08-24-catalogo-toma-el-diseno-de-modelo).
                <>
                {/* La cabecera VUELVE al panel, con el nombre del MODO y no el del paso
                    (ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso, que
                    supersede ADR-2026-08-27-el-panel-de-configuracion-no-lleva-titulo).
                    La objeción de ese ADR era que el título repetía el paso en la mitad
                    que no cambia; con `migasDelModo` no repite nada: dice dónde estás, y
                    el paso lo sigue diciendo la columna de al lado. El "Volver" se quedó
                    en el tope de esa columna, así que tampoco se duplica. */}
                {cabeceraEnPanel}
                {/* `pt-2` y no `pt-6`: el aire de arriba ya lo puso la cabecera. */}
                <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                  <PanelCatalogoCompacto
                    products={selectedProducts}
                    // La card es un INTERRUPTOR: el mismo tap que abre el panel lo
                    // cierra (ADR-2026-08-24-la-card-abre-y-cierra-su-panel).
                    onAbrirProductos={() =>
                      setPanelCatalogo((p) =>
                        p === 'productos' ? null : 'productos',
                      )
                    }
                    onSubirArchivos={onStartUpload}
                    calidad={calidad}
                    onCalidadChange={setCalidad}
                    fondoSelection={fondoSelection}
                    // Fondo y Modelo abren el MISMO panel lateral que Productos y
                    // Vistas, no un sheet a pantalla completa: el sheet tapaba la columna
                    // que se estaba armando y el fondo se elige mirándola
                    // (ADR-2026-08-27-fondo-y-modelo-en-el-panel-lateral). Interruptor,
                    // igual que las otras cards.
                    onOpenFondo={() =>
                      setPanelCatalogo((p) => (p === 'fondo' ? null : 'fondo'))
                    }
                    // Cantidad, Formato e indicaciones ya no se pasan: salieron del paso
                    // (ADR-2026-08-27-el-paso-de-vistas-se-queda-con-calidad). Los tres
                    // estados siguen vivos en `InterfazV2` y viajan al backend con su
                    // default — la cuenta de la tanda la fija vista × producto, así que
                    // `autoImageCount` no la toca.
                    // La tanda es vista × producto: el mismo armador que ya usaba el
                    // paso de vistas, así que lo que se anuncia acá es exactamente lo
                    // que se genera.
                    // Paso 3: lo que se genera son las cards que sobrevivieron a los
                    // descartes del review, no las combinaciones crudas.
                    imagenesDeLaTanda={
                      catalogoReviewDosColumnas
                        ? imagenesReview
                        : contarImagenesCatalogo(
                            cardsCatalogoUnaColumna,
                            modelosDeLaTandaCount,
                          )
                    }
                    // Modelo vive en el panel desde que el review dejó de dibujar sus
                    // cards globales (ADR-2026-08-27-catalogo-el-review-convive-con-el-panel)
                    // y ahora también está en el paso 2, en el lugar que dejó Vistas
                    // (ADR-2026-08-27-en-el-paso-2-el-modelo-ocupa-el-lugar-de-las-vistas).
                    modelo={modeloDeLaTanda}
                    onOpenModelo={() =>
                      setPanelCatalogo((p) => (p === 'modelo' ? null : 'modelo'))
                    }
                    ctaLabel={
                      catalogoReviewDosColumnas
                        ? undefined
                        : // El paso de vistas ya no es el último: lleva al REVIEW, donde
                          // se descartan combinaciones antes de pagar
                          // (ADR-2026-08-27-catalogo-vuelve-el-review).
                          'Revisar catálogo'
                    }
                    // La pastilla de créditos vuelve recién en el paso 3, que es el que
                    // cobra. En el paso 2 decía que el tap ya descontaba.
                    // 1 crédito por imagen, multiplicado por la Calidad elegida
                    // (ADR-2026-08-25-resolucion-de-generacion-2k-4k).
                    creditCost={
                      catalogoReviewDosColumnas
                        ? imagenesReview * creditosPorResolucion(calidad)
                        : null
                    }
                    onGenerate={() => {
                      setPanelCatalogo(null);
                      if (catalogoReviewDosColumnas) {
                        if (cardsActivasReview.length === 0) return;
                        onGenerateCatalog(cardsActivasReview);
                        return;
                      }
                      if (cardsCatalogoUnaColumna.length === 0) return;
                      onConfirmCatalogoVistas();
                    }}
                    // La bajada del review, al pie del CTA: de dónde sale el número.
                    nota={
                      catalogoReviewDosColumnas ? notaDeLaTanda : undefined
                    }
                    falta={
                      catalogoReviewDosColumnas
                        ? cardsActivasReview.length === 0
                          ? 'No queda ninguna combinación: agregá vistas o productos.'
                          : null
                        : faltaCatalogoUnaColumna
                    }
                  />
                </div>
                </>
              ) : fotosProductoDosColumnas ? (
                // ── Fotos de producto, paso 2 ────────────────────────────────────────
                // La MISMA columna compacta del Catálogo y no la grilla de productos
                // (ADR-2026-09-04-fotos-de-producto-usa-la-columna-compacta-del-catalogo):
                // los dos modos hacen lo mismo en este paso —elegir vistas mirando la
                // grilla de al lado—, y con la grilla de productos acá la pantalla tenía
                // dos grillas compitiendo. Lo ya decidido se lee como cards y se cambia
                // en el panel lateral.
                //
                // Con la cabecera del MODO arriba, igual que en el paso 1: al avanzar, la
                // pantalla sigue diciendo dónde estás
                // (ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso).
                <>
                {cabeceraEnPanel}
                {/* El scroll dejó de vivir acá: lo maneja `PanelCatalogoCompacto` con
                    `pieFijo`, que scrollea sólo su mitad de arriba y deja los ajustes y
                    el Generar fijos al pie
                    (ADR-2026-09-06-el-pie-de-configuracion-es-un-componente). Este
                    wrapper ya sólo pone el alto y los márgenes, igual que el de Modelo. */}
                <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-2">
                  <PanelCatalogoCompacto
                    pieFijo
                    products={selectedProducts}
                    // Interruptor, igual que en el Catálogo: el mismo tap abre y cierra
                    // el panel (ADR-2026-08-24-la-card-abre-y-cierra-su-panel).
                    onAbrirProductos={() =>
                      setPanelCatalogo((p) =>
                        p === 'productos' ? null : 'productos',
                      )
                    }
                    onSubirArchivos={onStartUpload}
                    // Calidad VUELVE a la columna, ahora en el pie fijo y al lado de
                    // Formato en vez de apilada entre las cards — supersede
                    // ADR-2026-09-04-fotos-de-producto-sin-calidad-en-la-columna. En la
                    // fila de dos es un renglón, que es lo que aquel ADR le objetaba.
                    // El control es REAL: el modo entró a `tieneSelectorResolucion` (el
                    // costo multiplica) y su payload manda `image_size`
                    // (ADR-2026-09-06-la-calidad-de-fotos-de-producto-es-real).
                    calidad={calidad}
                    onCalidadChange={setCalidad}
                    // El fondo de este modo no es el de la biblioteca: es liso de cuatro
                    // colores o una escena descrita, así que la card lo dice con SU
                    // vocabulario y sin foto.
                    fondoSelection={null}
                    fondoValor={etiquetaFondoProducto()}
                    fondoVacio={
                      esFondoProducto(fondoSelection) &&
                      fondoSelection.mode === 'descripcion' ? (
                        <TypeIcon className="w-6 h-6 text-brand-black/25" />
                      ) : (
                        // Liso: el recuadro PINTADO del color elegido es la preview
                        // literal, igual que el blanco del Catálogo.
                        <span
                          className="block w-full h-full"
                          style={{
                            backgroundColor: colorFondoLiso(fondoSelection),
                          }}
                        />
                      )
                    }
                    onOpenFondo={() =>
                      setPanelCatalogo((p) => (p === 'fondo' ? null : 'fondo'))
                    }
                    formatoLabel={getFormatLabel(formatoVal)}
                    onOpenFormato={() =>
                      setPanelCatalogo((p) =>
                        p === 'formato' ? null : 'formato',
                      )
                    }
                    // Vistas: card acá y grilla en el panel lateral, igual que el Fondo
                    // (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral).
                    // Interruptor, como el resto de las cards del panel
                    // (ADR-2026-08-24-la-card-abre-y-cierra-su-panel).
                    vistasValor={cardVistasProducto.valor}
                    vistasImagenes={cardVistasProducto.imagenes}
                    vistasExtra={cardVistasProducto.extra}
                    onOpenVistas={() =>
                      setPanelCatalogo((p) => (p === 'vistas' ? null : 'vistas'))
                    }
                    instruccion={otraConfig}
                    onInstruccionChange={onOtraChange}
                    imagenesDeLaTanda={fotosProductoTotalImagenes}
                    creditCost={creditCost ?? null}
                    onGenerate={() => {
                      setPanelCatalogo(null);
                      onGenerate(
                        creditCost != null && creditCost > 0 ? creditCost : 1,
                      );
                    }}
                    falta={faltaEnFotosProducto}
                    // Sin nada pendiente, de dónde sale el número: el mismo texto que
                    // tenía arriba del CTA en el pie del panel.
                    nota={
                      faltaEnFotosProducto ? undefined : resumenFotosProducto()
                    }
                  />
                </div>
                </>
              ) : pasoDosColumnas ? (
                // El panel de productos NO se va al pasar de paso: se queda igual
                // que en el paso anterior —misma grilla, mismos ajustes, mismo pie— y lo
                // único que cambia es la columna de al lado. Su "Siguiente" pasa a
                // re-confirmar: si acá se agrega o saca un producto, se vuelve a aplicar
                // sobre la configuración que ya está abierta.
                <div className="flex-1 min-h-0 flex flex-col">
                  {renderStep('desktop', 'choose-product')}
                </div>
              ) : step === 'pick-product' ||
              step === 'upload-product' ||
              step === 'choose-product' ? (
                // Estos paneles manejan su propio layout (header / scroll
                // / footer fijo). NO les metemos padding ni overflow al
                // wrapper — sería doble scroll y el footer no se vería
                // sticky. Incluye el choose-product FUSIONADO de desktop
                // (PickProductPanel con header de upload), tanto en modos de
                // diseño como en plantillas.
                <div key={step} className={`${swapAnimClass} flex-1 min-h-0 flex flex-col`}>
                  {renderStep('desktop')}
                </div>
              ) : (
                // Resto de los steps: wrapper con padding + overflow-y
                // por si el contenido es más alto. `justify-center` solo
                // en choose-product (contenido corto, queda mejor centrado).
                // En design-modelo configure NO scrolleamos internamente
                // — la ventana entera es la scrolleable.
                <>
                {/* La cabecera de la pantalla (breadcrumb + `<h1>`) va FIJA arriba del
                    panel, fuera del scroll — igual que en el paso anterior, así no se
                    mueve al avanzar. `ConfigurePanel` no la puede poner: no tiene dónde
                    (su raíz ya es el contenido scrolleable). */}
                {plantillaConfigura && cabeceraEnPanel}
                <div
                  key={step}
                  // pb-6 default — pero en design-flow + configure lo
                  // sacamos para que el Generar sticky del ConfigurePanel
                  // llegue al borde del aside sin gap (mismo fix que en
                  // mobile). El modo 'video-influencer' (que reusa este panel
                  // como plantilla-video con Generar sticky) también lo saca:
                  // sino el `pb-6` deja una franja vacía debajo del botón. ADR-505.
                  className={`${swapAnimClass} px-6 ${
                    // Con la cabecera arriba, el aire ya lo puso ella (mismo criterio
                    // que el panel del Catálogo).
                    plantillaConfigura ? 'pt-2' : 'pt-6'
                  } flex flex-col ${
                    // Sin `pb-6` en el paso 3 de una plantilla: el pie del panel trae el
                    // suyo, y el padding del scroller despegaba al sticky 24px del borde.
                    (fullScreenConfigureMobile && step === 'configure') ||
                    plantillaConfigura
                      ? ''
                      : 'pb-6'
                  } ${
                    configureFullScreen ? '' : 'flex-1 min-h-0 overflow-y-auto'
                  } ${step === 'choose-product' ? 'justify-center' : ''}`}
                >
                  {renderStep('desktop')}
                </div>
                </>
              )}
            </aside>
            {/* El panel lateral de Catálogo: productos, fondo y modelo, en el mismo lugar
                y con la misma forma (ADR-2026-08-24-catalogo-toma-el-diseno-de-modelo).
                Vistas ya no está: se fue con su card
                (ADR-2026-08-27-vistas-no-tiene-card-en-el-panel). */}
            {(catalogoUnaColumna ||
              autoConPanelLateral ||
              // Fotos de producto abre Fondo y Formato acá, al lado de la columna, en vez
              // de en un sheet centrado que tapa la pantalla
              // (ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo).
              fotosProductoDosColumnas ||
              // El paso 3 de una plantilla elige Productos y Modelo en este mismo panel.
              plantillaConfigura) && (
              <PanelLateralModo
                abierto={panelCatalogo !== null}
                onCerrar={() => setPanelCatalogo(null)}
                // Cada valor con su título: cayendo en un `else` de "Productos", una
                // rama sin nombre propio abría la grilla equivocada bajo el título
                // correcto (ADR-2026-08-25-el-panel-lateral-no-cae-en-productos-por-descarte).
                titulo={
                  panelCatalogo === 'fondo'
                    ? 'Fondo'
                    : panelCatalogo === 'modelo'
                      ? palabra.titulo
                      : panelCatalogo === 'formato'
                        ? 'Formato'
                        : panelCatalogo === 'vistas'
                          ? 'Vistas'
                          : panelCatalogo === 'plantilla'
                            ? 'Plantillas'
                            : 'Productos'
                }
                // Vistas ocupa TODO lo que queda a la derecha de la columna de
                // configuración: mientras está abierto es lo único que se mira, y a 615
                // los tiles quedaban en ~180px con media pantalla en blanco al lado. El
                // resto de los pickers se queda en 615: sus grillas no ganan nada con
                // más ancho (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral).
                maxAncho={panelCatalogo === 'vistas' ? 'todo' : 615}
                // Medido, no escrito — ver `offsetPanelLateral`. El `calc` queda de
                // respaldo para el primer render, antes de que haya medida.
                offsetIzquierdo={offsetPanelLateral ?? 'calc(35% + 2rem)'}
                contenedorRef={areaDosColumnasCatalogoRef}
              >
                {panelCatalogo === 'vistas' ? (
                  // Las VISTAS del modo, acá y no en la columna de enfrente
                  // (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral):
                  // es la misma grilla de siempre, con su checklist de productos por
                  // vista, en el panel donde ya se eligen Fondo, Formato y Productos.
                  //
                  // El panel NO se cierra al elegir: acá se marcan VARIAS —cada una es
                  // una imagen más por producto— y cerrarse en el primer tap obligaría a
                  // reabrirlo por cada vista. Se cierra con la X, el Escape o el click
                  // afuera, como el de Fondo.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed">
                      {selectedProducts.length > 1
                        ? 'Desde qué ángulo se ven los productos. Generamos una imagen de cada producto por cada vista; tocá las miniaturas de una vista para elegir cuáles entran.'
                        : 'Desde qué ángulo se ve el producto. Elegí todas las que necesites: generamos una imagen por cada una.'}
                    </p>
                    {/* La cuenta, pegada a la grilla: es lo que cambia con cada tap. El
                        TOTAL de la tanda no se repite acá —lo dice el pie del panel de
                        al lado, junto al costo— para no tener el mismo número en dos
                        lugares que se pueden desincronizar. */}
                    <span className="mt-3 mb-4 inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-100 text-brand-black/70 text-[12px] font-medium">
                      {vistaIds.length}{' '}
                      {vistaIds.length === 1
                        ? 'vista seleccionada'
                        : 'vistas seleccionadas'}
                      {selectedProducts.length > 1 &&
                        fotosProductoTotalImagenes > 0 && (
                          <span className="text-[#3C3C43]/55">
                            · {fotosProductoTotalImagenes}{' '}
                            {fotosProductoTotalImagenes === 1
                              ? 'imagen'
                              : 'imágenes'}
                          </span>
                        )}
                    </span>
                    {/* Tres columnas FIJAS y no reparto por ancho: el panel mide siempre
                        lo mismo (`maxAncho`), así que no hay nada que repartir — el
                        `auto-fill` que usaba la columna existía porque ésa medía 400px o
                        1000px según el monitor. */}
                    <VistaProductoPicker
                      vistas={vistasProducto}
                      selectedIds={vistaIds}
                      onChange={onVistaIdsChange ?? (() => undefined)}
                      columns={3}
                      productos={selectedProducts}
                      excluidosPorVista={vistaProductosExcluidos}
                      onToggleProducto={onToggleVistaProducto}
                    />
                  </div>
                ) : panelCatalogo === 'fondo' &&
                  preset.kind === 'fotos-producto' ? (
                  // El fondo de ESTE modo son dos caminos —liso (cuatro colores) o
                  // escena descrita—, no la biblioteca entera: el mismo picker reducido
                  // que ya usaba su panel de configuración, ahora en el lateral.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <FondoProductoPicker
                      selection={
                        esFondoProducto(fondoSelection)
                          ? fondoSelection
                          : { mode: 'blanco' }
                      }
                      onChange={onFondoSelectionChange}
                      productNames={selectedProducts.map((p) => p.name)}
                    />
                  </div>
                ) : panelCatalogo === 'fondo' ? (
                  // La misma grilla de fondos que usa Modelo v2: categorías como pills y
                  // los fondos en la misma caja, sin salir al sheet
                  // (ADR-2026-08-25-fondos-con-pills-como-poses). Describir y subir sí
                  // salen: son formularios propios, no una grilla que elegir.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <FondoGrilla
                      selection={fondoSelection}
                      onChange={onFondoSelectionChange}
                      biblioteca={bibliotecaFondos}
                      guardados={fondosGuardados}
                      // La subida ocurre DENTRO del panel
                      // (ADR-2026-08-25-el-fondo-se-sube-en-el-panel): sin estas dos, la
                      // vista "subir" de la grilla no tiene empresa a la que guardar.
                      companyId={userCompanyId}
                      onFondoSaved={onFondoSaved}
                      // Contexto del "Sugerime ideas": con qué productos y en qué rubro,
                      // para que las ideas no salgan genéricas.
                      selectedProductNames={selectedProducts.map((p) => p.name)}
                      rubro={rubro}
                      // Aplicar una descripción CIERRA el panel: es el final de la
                      // decisión, como el "Listo" del panel de productos.
                      onAplicar={() => setPanelCatalogo(null)}
                    />
                  </div>
                ) : panelCatalogo === 'formato' ? (
                  // La MISMA grilla que Modelo v2 abre en su panel
                  // (ADR-2026-08-24-formato-en-el-panel-lateral). Elegir CIERRA el
                  // panel: es una decisión de un solo tap, sin nada más que ajustar ahí
                  // adentro, así que quedarse abierto pediría un gesto de más para
                  // volver a la columna.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <FormatPickerGrid
                      selectedId={formatoVal}
                      onPick={(id) => {
                        onFormatoChange(id);
                        setPanelCatalogo(null);
                      }}
                      columnas={3}
                    />
                  </div>
                ) : panelCatalogo === 'plantilla' ? (
                  // TODAS las plantillas, para cambiar la elegida sin volver a la grilla.
                  // Elegir CIERRA el panel: es una decisión de un solo tap, como Formato.
                  <>
                    {/* Los chips de categoría, PEGADOS arriba: son los mismos de la
                        pantalla de Plantillas y el mismo single-select —tocar la activa
                        la apaga— (ADR-2026-09-08-el-panel-de-plantillas-filtra-por-categoria).
                        Con 267 plantillas en una grilla sola, encontrar una era
                        scrollear. La fila scrollea en horizontal como allá; `shrink-0`
                        para que no la comprima la grilla de abajo. */}
                    {!!categoriasDePlantillas?.length && (
                      <div className="shrink-0 flex flex-wrap gap-2 px-6 pb-3 pt-1">
                        {categoriasDePlantillas.map((c) => {
                          const activa = filtroPlantilla === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              aria-pressed={activa}
                              onClick={() =>
                                setFiltroPlantilla((f) => (f === c.id ? null : c.id))
                              }
                              className={`rounded-full px-3.5 h-8 text-[13.5px] font-medium transition-colors ring-1 ${
                                activa
                                  ? 'bg-[#EBEFFE] text-brand-primary ring-brand-primary'
                                  : 'bg-white text-brand-black/70 ring-black/[0.08] hover:bg-black/[0.03]'
                              }`}
                            >
                              {c.titulo}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  <div
                    ref={scrollerPlantillasRef}
                    className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2"
                  >
                    <div className="grid grid-cols-3 gap-2.5">
                      {(categoriasDePlantillas?.length
                        ? categoriasDePlantillas
                            .filter((c) => !filtroPlantilla || c.id === filtroPlantilla)
                            .flatMap((c) => c.presets)
                        : previewList
                      ).map((p) => {
                        const activa = p.id === preset.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            // La elegida se centra al abrir el panel: es de donde el
                            // usuario viene y desde donde va a seguir mirando.
                            ref={activa ? tilePlantillaActivaRef : undefined}
                            onClick={() => {
                              onElegirPlantilla?.(p);
                              setPanelCatalogo(null);
                            }}
                            aria-pressed={activa}
                            className={`group relative aspect-[3/4] overflow-hidden rounded-xl text-left transition-shadow ring-1 ${
                              activa
                                ? 'ring-2 ring-brand-primary'
                                : 'ring-black/[0.06] hover:shadow-[0_2px_16px_rgba(0,0,0,0.09)]'
                            }`}
                          >
                            <img
                              src={p.imageUrl}
                              alt={p.title}
                              loading="lazy"
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                            />
                            {/* El check circular de la elegida, el MISMO de la grilla de
                                productos (`PickProductPanel`): el anillo azul solo se
                                lee recién comparando tiles, y acá se abre justamente
                                para ver dónde está uno parado. 20px, arriba a la
                                derecha, como allá. */}
                            {activa && (
                              <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center shadow">
                                <Check className="w-3 h-3 text-white" strokeWidth={3} />
                              </span>
                            )}
                            <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-2.5 pb-2 pt-8">
                              <span className="block truncate text-[12.5px] font-medium text-white">
                                {p.title}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  </>
                ) : panelCatalogo === 'modelo' ? (
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    {/* Sólo se dibuja si el host pasó el handler: en el resto de los
                        modos la grilla no fan-outea por modelo y el control no aplicaría.

                        `modelViewLateral === 'list'`: en create/upload el panel es un
                        formulario que ocupa todo, y ahí no hay grilla que multi-seleccionar. */}
                    {onVariosModelosChange &&
                      modelViewLateral === 'list' && (
                        <SwitchSeleccionMultiple
                          titulo={`Utilizar varios ${palabra.plural}`}
                          activo={variosModelos}
                          onChange={onVariosModelosChange}
                          // La bajada dice la CONSECUENCIA —que la tanda se multiplica—
                          // antes de que se note en la pastilla de créditos. Y cuando el
                          // tope está lleno, dice cómo salir: las cards apagadas ya
                          // muestran que no entra otro, pero no qué hacer al respecto.
                          bajada={
                            !variosModelos
                              ? `Se genera una imagen por ${palabra.singular}. Hasta ${MAX_MODELOS_SERIE}.`
                              : modelosDeLaTandaCount >= MAX_MODELOS_SERIE
                                ? `Llegaste al máximo de ${MAX_MODELOS_SERIE} ${palabra.plural}. Sacá uno para elegir otro.`
                                : `${modelosDeLaTandaCount} ${
                                    modelosDeLaTandaCount === 1
                                      ? `${palabra.singular} elegido`
                                      : `${palabra.plural} elegidos`
                                  } — cada pose se genera con cada uno.`
                          }
                        />
                      )}
                    <ModelPickerInline
                      selection={modelSelection}
                      // Apagado, el tap REEMPLAZA (`onChange`); prendido, ACUMULA
                      // (`onToggleSelection`). Es la misma grilla: lo único que cambia es
                      // qué hace el tap.
                      onChange={onModelChange}
                      selectionsSerie={
                        variosModelos ? modelSelectionsSerie : []
                      }
                      onToggleSelection={
                        variosModelos ? onToggleModelSerie : undefined
                      }
                      view={modelViewLateral}
                      onViewChange={setModelViewLateral}
                      userModels={userModels}
                      userId={userAuthId}
                      companyId={userCompanyId}
                      onModelCreated={onModelCreated}
                      hideLabel
                      grilla
                      columnasGrilla={3}
                    />
                  </div>
                ) : (
                  <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
                    <PickProductPanel
                      variant="desktop"
                      // Selección EN VIVO: no hay paso de confirmación —el Generar está
                      // en la columna—, así que cada tap ya deja el producto puesto.
                      onSelectionChange={onProductosEnVivo ?? onConfirmProducts}
                      onConfirm={() => setPanelCatalogo(null)}
                      confirmLabel="Listo"
                      onBack={() => setPanelCatalogo(null)}
                      initialSelectedIds={selectedProducts.map((p) => p.id)}
                      tiposProductoSugeridos={preset.tiposProductoSugeridos}
                      rubro={rubro}
                      onStartUpload={onStartUpload}
                      onStartNewProduct={onStartNewProduct}
                      presetKind={preset.kind}
                      onSkipProduct={onSkipProduct}
                      columnasFijas={3}
                      ocultarBloqueSubida
                      nombreSobreLaFoto
                      pistasDeUso
                      // También acá: es el otro camino para tocar el producto (la card
                      // "Producto" de la configuración), y sin esto el modo de un solo
                      // producto se podía saltear desde el panel lateral.
                      singleSelect={singleProductMode}
                    />
                  </div>
                )}
              </PanelLateralModo>
            )}
          </div>
        )}
        </div>
      </div>
    </>
  );
};
